/**
 * SystemModuleBuilderPage — F12.M Etap C.4 + C.7 (tabs).
 *
 * Builder layoutu formularza dla modułu systemowego.
 * Route: `/system-modules/:slug/builder?layoutId=<id>` (tenant scope).
 *
 * UI (4 obszary):
 *  1. Header — back, nazwa modułu, LayoutSwitcher (per-rola/kategoria), Save
 *  2. Top-bar zakładek — przeciągalne, rename inline, hide toggle (C.7)
 *  3. Content per typ zakładki:
 *     - `fields` → canvas sekcji + paleta pól po lewej + properties po prawej (C.4)
 *     - inne (`subcollection`/`audit_log`/`tree_picker`/`satellite`)
 *       → placeholder „content systemowy — admin nie konfiguruje wewnątrz"
 *  4. Save: PUT całego `tabs[]` do API
 *
 * Model danych (`layout_config` JSONB):
 *  {
 *    tabs: [
 *      { id, slug, title, type, hidden, sortOrder, sections?, collection? },
 *      ...
 *    ]
 *  }
 */

import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  closestCenter,
  type Collision,
  type CollisionDetection,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useDraggable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  type EmbeddedTableColumn,
  parseEmbeddedTableColumns,
  parseEmbeddedTableHiddenColumnIds,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  ArrowLeft,
  ChevronDown,
  Copy,
  Eye,
  EyeOff,
  GripVertical,
  Layout as LayoutIcon,
  Lock,
  Pencil,
  Plus,
  Save,
  SlidersHorizontal,
  Trash2,
  X,
  Zap,
} from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { TableLayoutEditor } from '@/components/embedded-table/TableLayoutEditor';
import { BuilderEnvironmentProvider } from '@/components/fields/builder-environment';
import {
  LayoutElementRegistry,
  tryRenderLayoutElement,
} from '@/components/fields/LayoutElementRegistry';
import { exportFormLayout } from '@/components/form-transfer/form-transfer-api';
import { FormImportModal } from '@/components/form-transfer/FormImportModal';
import { FormField } from '@/components/FormField';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { AddKartotekaFieldModal } from '@/components/kartoteki/AddKartotekaFieldModal';
import { StylePanelAdvanced, styleToCSS } from '@/components/module-builder/style-panel';
import type { LayoutElementStyle } from '@/components/module-builder/style-panel/styleTypes';
import { AccordionInCanvas } from '@/components/modules/AccordionInCanvas';
import { ColumnsInCanvas } from '@/components/modules/ColumnsInCanvas';
import { ContainerInCanvas } from '@/components/modules/ContainerInCanvas';
import { FieldBlock } from '@/components/modules/FieldBlock';
import { renderIconByName } from '@/components/modules/IconPicker';
import { LayoutElementBlock } from '@/components/modules/LayoutElementBlock';
import { LayoutElementsPalette } from '@/components/modules/LayoutElementsPalette';
import { LayoutFormModal, type LayoutFormValues } from '@/components/modules/LayoutFormModal';
import { type LayoutOption, LayoutSwitcher } from '@/components/modules/LayoutSwitcher';
import { PaletteSearchInput } from '@/components/modules/PaletteSearchInput';
import { StepperInCanvas } from '@/components/modules/StepperInCanvas';
import {
  SubcollectionLayoutEditor,
  type SubcollectionLayoutElement,
  type TargetField,
} from '@/components/modules/SubcollectionLayoutEditor';
import { TabFormModal, type TabFormValues } from '@/components/modules/TabFormModal';
import { Spinner } from '@/components/Spinner';
import {
  AddSystemFieldModal,
  type SystemFieldType,
  systemFieldTypeI18nKey,
} from '@/components/system-modules/AddSystemFieldModal';
import { Tooltip } from '@/components/Tooltip';
import { useToast } from '@/contexts/ToastContext';
import { useHostScope } from '@/hooks/useHostScope';
import { type PaletteDragData, type SectionDragData, usePaletteDnD } from '@/hooks/usePaletteDnD';
import { useAdditionalTablesList } from '@/lib/additional-tables/api';
import { useAdditionalViewsList } from '@/lib/additional-views/api';
import { api } from '@/lib/api';
import { useAdditionalTableColumnsModel } from '@/lib/embedded-table/use-additional-table-columns-model';
import { useAdditionalViewColumnsModel } from '@/lib/embedded-table/use-additional-view-columns-model';
import {
  applyOverlayPatchByKey,
  applyTabOverrides,
  deriveSystemOverrides,
  elementOverrideKey,
  isOverlayMaskPatch,
  type TabOverrides,
} from '@/lib/kartoteki/apply-tab-overrides';
import {
  extractLayoutSingletonTypes,
  layoutElementDisplayName as layoutElementDisplayNameOf,
  layoutSingletonRejectedMessage,
} from '@/lib/layout-singleton-error';
import {
  collectElementTypes,
  findInTree,
  hasNonContainerElement,
  insertIntoContainer,
  isLayoutSingletonTaken,
  type LayoutElement as TreeLayoutElement,
  removeElementsOfTypes,
  removeFromTree,
  reorderAccordionPanels,
  reorderInContainer,
  reorderSteps,
  updateInTree,
} from '@/lib/layout-tree';
import { computedProcessPaletteFields } from '@/lib/processes/computed-fields';
import { normalizeSearch } from '@/lib/search-normalize';

import { filterAdditionalTablesForModule } from './document-module-slugs';
import {
  extractViewLevelElements,
  loadTabsFromConfig,
  saveTabsToConfig,
} from './layout-shape-adapter';

// ─── Types ──────────────────────────────────────────────────────────────────

interface SystemModuleField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
  isSystemLocked: boolean;
  isRequired: boolean;
  sortOrder: number;
  /** Opis pola (z katalogu) — pokazywany w tooltipie palety pól. */
  description?: string | null;
}

interface SystemModuleCategory {
  id: string;
  name: string;
  color: string | null;
  icon: string | null;
}

interface SystemModuleLayout {
  id: string;
  moduleSlug: string;
  layoutType: string;
  name: string;
  isDefault: boolean;
  /** Preferencja tenanta „pokazuj dokowany podgląd załączników po prawej" — modelowana 1:1 na `isDefault`. */
  previewDockEnabled: boolean;
  /**
   * TRUE = layout zasiany systemowo (nietykalny) — builder blokuje edycję struktury:
   * zmianę nazwy zakładki, dodawanie/usuwanie zakładek, edycję zawartości. Tenant edytuje
   * wyłącznie własne kopie/nowe layouty (`false`). Patrz migracja 0121 + seed-system-modules.
   */
  isSystemManaged: boolean;
  layoutConfig: LayoutConfig;
  categoryId: string | null;
  roleIds: string[];
  /** B.2+B.3 — marker layoutu centralnego (NOT NULL ⟹ producent, read-only dla tenanta). */
  sourceViewSlug: string | null;
  /** Układ z menu modułu kreatora ITE (NOT NULL ⟹ tylko do odczytu, nigdy domyślny). */
  menuSourceModuleId?: string | null;
  /**
   * Nakładka per-tenant (ukrycia + kolejność zakładek) dla layoutu systemowego —
   * trzymana POZA `layout_config` (re-seed by ją zdmuchnął). Builder nakłada ją na
   * `layoutConfig` przy wczytaniu i przy zapisie na systemowym wysyła TĘ nakładkę
   * zamiast `layout_config`. `null` = brak nakładki.
   */
  tabOverrides: TabOverrides | null;
}

interface SystemModuleDetail {
  module: {
    slug: string;
    name: string;
    entityTableName: string;
    icon: string;
    color: string;
    /**
     * F12.N T5.6b: flaga helper-encji (Adresy / Konta bankowe / Działy ...).
     * Gdy zakładka subcollection wskazuje na moduł z `isHelperEntity=true`,
     * builder renderuje sekcje helper-encji inline w canvasie zamiast
     * placeholdera + przycisku navigującego.
     */
    isHelperEntity: boolean;
  };
  fields: SystemModuleField[];
  layouts: SystemModuleLayout[];
  categories: SystemModuleCategory[];
}

interface RoleTemplate {
  id: string;
  name: string;
  isSystem: boolean;
}

export type TabType =
  | 'fields'
  | 'subcollection'
  | 'audit_log'
  | 'tree_picker'
  | 'satellite'
  /** New: real additional table tab (has tableSlug). */
  | 'additional'
  /**
   * F22 (widoki dodatkowe) — sekcja read-only zasilana zapytaniem SQL (ma `viewSlug`).
   * Mirror duplikatu typu w `layout-shape-adapter.ts` (patrz komentarz tam). Tworzona
   * przez `TabFormModal` (wybór typu „Widok SQL"); WYBÓR widoku ustawiany później w
   * canvasie przez `AdditionalQueryConfigPanel` (mirror `AdditionalTableConfigPanel`).
   */
  | 'additional_query'
  /** New: neutral placeholder tab (security/account — no tableSlug). */
  | 'panel'
  /**
   * Etap 2 kartoteki: zakładka Akceptacje — seed ją tworzy (F17 T17.1.8),
   * builder wyświetla read-only placeholder (ProcessHistoryTab renderuje runtime po
   * stronie tenanta). Nie edytowalny przez admina w builderze formularza.
   */
  | 'approvals'
  /**
   * Zakładka-kontener bloków (konfigurowalne zakładki, Faza 1) — mirror duplikatu w
   * `layout-shape-adapter.ts`. Zawiera wyłącznie bloki-widoki (`contractor_*_view` /
   * `attachments_view` / `process_view` / `record_links_view`…) bez sekcji z polami.
   * Builder renderuje ją przez ścieżkę non-fields (paleta klocków + NonFieldsExtrasPanel).
   */
  | 'block';

interface LayoutTab {
  id: string;
  slug?: string;
  title: string;
  /** F12.N Etap 4 E4.T2/T4: nazwa ikony z lucide-react (np. `'FileText'`). Opcjonalna. */
  icon?: string;
  type: TabType;
  hidden: boolean;
  sortOrder: number;
  visibleRoleIds?: string[]; // NEW — per-role visibility (empty = all roles see it)
  /**
   * ETAP4 S7: ISO timestamp publikacji zakładki (gdy układ wysłano do klientów).
   * Ustawiany przez silnik publikacji; zakładka z znacznikiem jest trwała —
   * builder ukrywa kosz/ołówek (zostaje przesuwanie). Nieustawiony = wersja robocza.
   */
  publishedAt?: string;
  /** Tylko dla `type='fields'`. */
  sections?: LayoutSection[];
  /** Dla `type='subcollection'`: nazwa subkolekcji (np. 'contractor_contacts'). */
  collection?: string;
  /** F12S Etap C T12.S.C.4: slug tabeli dodatkowej powiązanej z tą zakładką. */
  tableSlug?: string;
  /**
   * Układ kolumn tabeli dodatkowej (`type='additional'`) albo widoku SQL (`type='additional_query'`)
   * tej zakładki. Mirror pola w `layout-shape-adapter.ts` (zapis: `settings.tableColumns`).
   * Brak = układ domyślny.
   */
  tableColumns?: EmbeddedTableColumn[];
  /**
   * Maska ukrytych kolumn tabeli dodatkowej (`type='additional'`) albo widoku SQL
   * (`type='additional_query'`) tej zakładki — na układzie systemowym ukrycia tenanta. Mirror pola
   * w `layout-shape-adapter.ts` (zapis: `settings.hiddenColumnIds`). Brak / pusta = brak maski.
   */
  hiddenColumnIds?: string[];
  /** F22: slug widoku dodatkowego (`ext_additional_views.slug`) powiązanego z tą zakładką. */
  viewSlug?: string;
  /**
   * F12.N E2.T1: extras — dodatkowe klocki layoutu (heading/alert/separator/...)
   * dodane przez admina do zakładki POZA główną zawartością. Przy save adapter
   * dorzuca je do `TabItem.children` po placeholder (lub po sekcjach dla 'fields').
   */
  extras?: LayoutElement[];
  /**
   * F12 PR 2 pkt 2 v3 (2026-05-19): customowy form layout dla zakładki
   * `type='subcollection'`. Sekcje + pola modułu wskazywanego (helper-encja albo
   * pełen moduł), niezależne od layoutu samego modułu wskazywanego. Default seed
   * = klon layoutu helper-encji przy pierwszym wjeździe. Persistuje w `TabItem.children`
   * jako element typu `subcollection_form_layout` z children=sections.
   */
  subcollectionFormLayout?: LayoutSection[];
}

interface LayoutSection {
  id: string;
  title: string | null;
  columns: 1 | 2 | 3;
  elements: LayoutElement[];
}

/**
 * Element w layoutcie — pełen model rekurencyjny SellCall-style (F12.N E1.T1).
 *
 * Pole `type` decyduje co konkretny element robi i które pola są wypełnione:
 *
 *  - `'field'` → pole modułu (wymaga `fieldId`, używa field-rendererów core/ext)
 *  - `'section' | 'card' | 'row' | 'columns' | 'fieldset' | 'panel'` → kontener
 *    z dziećmi (`children: LayoutElement[]`)
 *  - `'tabs'` → element zakładek, ma `tabs: TabItem[]` (osobne pole, 1:1 z SellCall)
 *  - `'accordion'` → element akordeonu, ma `accordionItems: AccordionItem[]`
 *  - `'stepper' | 'wizard'` → element kroków, ma `steps: Step[]`
 *  - `'heading' | 'divider' | 'alert' | 'image' | 'text' | ...` → atomowe elementy
 *    treści/komunikatów, bez dzieci, tylko `settings` per typ
 *
 * Renderer dispatch'uje na `type`:
 *  - `type === 'field'` → `FieldRegistry.renderInput(...)`
 *  - inny `type` → `LayoutElementRegistry.render(...)` (side-effect import w App.tsx)
 */
interface LayoutElement {
  id: string;
  type: string;
  /** Tylko dla `type === 'field'`. */
  fieldId?: string;
  /**
   * Ukrycie elementu przez nakładkę per-tenant systemowego layoutu (ikonka oka na polu).
   * `deriveSystemOverrides` czyta tę flagę do `hiddenFieldIds`; `applyTabOverrides`
   * przywraca ją na odczycie; karta pomija pole z tą flagą.
   */
  hidden?: boolean;
  /** Konfiguracja per typ elementu. Dla field: `{ label, required }`. Dla heading: `{ text, level }` itp. */
  settings?: Record<string, unknown>;
  /** Style override per element (background, padding, border itp.). */
  style?: Record<string, unknown>;
  /** Conditional visibility — wspólne dla pól i elementów layoutu. */
  conditionalField?: string | null;
  conditionalOperator?: string | null;
  conditionalValue?: unknown;

  // ── F12.N E1.T1: rekurencyjne zagnieżdżenie (1:1 z SellCall) ──

  /**
   * Dzieci dla kontenerów (section/card/row/columns/fieldset/panel/...).
   * Pusta tablica = pusty kontener. Brak pola = element atomowy (heading/divider/...).
   */
  children?: LayoutElement[];

  /**
   * Zakładki dla `type === 'tabs'` (1:1 z SellCall — osobne pole zamiast czysto
   * rekurencyjnego children). Każda TabItem ma własne `children`.
   */
  tabs?: TabItem[];

  /**
   * Panele dla `type === 'accordion'` (1:1 z SellCall). Każdy AccordionItem
   * ma własne `children` i opcjonalne `defaultExpanded`.
   */
  accordionItems?: AccordionItem[];

  /**
   * Kroki dla `type === 'stepper'` lub `'wizard'` (1:1 z SellCall). Każdy Step
   * ma własne `children` i opcjonalną walidację przy przejściu.
   */
  steps?: Step[];
}

/**
 * Pojedyncza zakładka wewnątrz elementu `type === 'tabs'` (F12.N E1.T1).
 *
 * NIE jest LayoutElementem — to osobny typ kontener-grupy. Zgodnie z SellCall
 * (osobne pole `tabs[]` w LayoutElement zamiast czysto rekurencyjnego children).
 */
interface TabItem {
  id: string;
  /** Identyfikator stabilny używany m.in. do deep-linkingu URL `#contacts`. */
  slug: string;
  title: string;
  /** Nazwa ikony z lucide-react (np. `'FileText'`, `'Calendar'`). */
  icon?: string;
  /** Per-role visibility — pusta tablica/brak = wszyscy widzą. */
  visibleRoleIds?: string[];
  /** Czy uruchamiać walidację po przejściu z tej zakładki. */
  validateOnChange?: boolean;
  /** Czy blokować przejście do innej zakładki gdy są błędy walidacji. */
  blockOnError?: boolean;
  /** Domyślnie ukryta przed userem — admin może odsłonić w buildery. */
  hidden?: boolean;
  /** Sortowanie zakładek w obrębie elementu `type === 'tabs'`. */
  sortOrder?: number;
  /** Zawartość zakładki — rekurencyjnie. */
  children: LayoutElement[];
}

/**
 * Pojedynczy panel akordeonu wewnątrz elementu `type === 'accordion'` (F12.N E1.T1).
 */
interface AccordionItem {
  id: string;
  title: string;
  /** Czy panel jest rozwinięty przy pierwszym render (gdy multiple = false, ostatni z true wygrywa). */
  defaultExpanded?: boolean;
  /** Per-role visibility. */
  visibleRoleIds?: string[];
  /** Sortowanie paneli w obrębie akordeonu. */
  sortOrder?: number;
  children: LayoutElement[];
}

/**
 * Pojedynczy krok wewnątrz elementu `type === 'stepper'` lub `'wizard'` (F12.N E1.T1).
 */
interface Step {
  id: string;
  title: string;
  /** Nazwa ikony z lucide-react. */
  icon?: string;
  /** Czy walidować pola tego kroku przed przejściem do następnego. */
  validation?: boolean;
  /** Sortowanie kroków. */
  sortOrder?: number;
  children: LayoutElement[];
}

interface LayoutConfig {
  /** C.7 shape — tabs na top-level. */
  tabs?: LayoutTab[];
  /** Pre-C.7 shape (sections na top-level) — auto-migracja przy hydracji. */
  sections?: LayoutSection[];
}

/**
 * F12.N T5.6b — stan lokalnej edycji layoutu helper-encji (Adresy, Konta bankowe...).
 *
 * Klucz w `helperEdits: Map<collection, HelperEditState>` to slug helper-encji
 * (np. `'addresses'`, `'contractor_bank_accounts'`). Gdy admin klika na zakładkę
 * subcollection w buildery modułu macierzystego (np. Kontrahent → Adresy):
 *  1. Fetch `/api/system-modules/<collection>` (jeden strzał z module+fields+layouts).
 *  2. Sprawdzamy `module.isHelperEntity=true` (inaczej zostaje stary placeholder).
 *  3. Default form layout helper-encji rozpakowujemy do `tabs[]` przez ten sam
 *     adapter co macierzysty (loadTabsFromConfig) — helper-encja ma 1 zakładkę 'data'.
 *  4. `helperEdits.set(collection, { layoutId, tabs, fields, ... })`.
 *  5. Canvas renderuje FieldsTabContent z tym scopem (paleta + sekcje + properties).
 *
 * Save w mutation iteruje po `helperEdits` i per dirty helper PUT-uje osobny
 * endpoint `/api/system-modules/<collection>/layouts/<layoutId>`.
 */
interface HelperEditState {
  /** ID aktywnego layoutu helper-encji w bazie (do PUT na save). */
  layoutId: string;
  /** Płaski stan zakładek (1 zakładka 'data' z sekcjami). */
  tabs: LayoutTab[];
  /** Pełna lista pól helper-encji (paleta po lewej). */
  fields: SystemModuleField[];
  /** Aktualnie zaznaczona sekcja w canvasie helper-encji (per collection). */
  selectedSectionId: string | null;
  /** Czy są niezapisane zmiany (osobno per helper-encja). */
  dirty: boolean;
}

// ─── Page root ──────────────────────────────────────────────────────────────

export interface SystemModuleBuilderPageProps {
  /**
   * F12.M Etap E (PR 2 pkt 1): gdy `true`, builder jest renderowany INLINE
   * wewnątrz `SystemModuleSettingsPage` tab `layout` (jak w SellCall — settings + builder
   * w jednym hub-screen). Header z Back+Title jest ukryty (settings ma swój),
   * LayoutSwitcher+Save renderowane w kompaktowym mini-headerze nad canvasem.
   * Domyślnie `false` — zachowanie standalone page na `/system-modules/:slug/builder`.
   */
  embeddedMode?: boolean;

  /**
   * Etap 2 kartoteki — prop-refaktor (analogia do SystemModuleListBuilderPage).
   *
   * Gdy podany, zastępuje `slug` z `useParams()`. Trasowe użycie (`/system-modules/:slug/builder`)
   * nie przekazuje tego propsa — zachowanie bez zmian (fallback na useParams).
   */
  slug?: string;

  /**
   * Etap 2 kartoteki — initialny `layoutConfig` do załadowania zamiast `activeLayout.layoutConfig`
   * z tenant-scope detailQuery. Używany gdy budujemy per-modułowy override kartoteki systemowej
   * (super admin osadza builder w ModuleTemplateEditor).
   *
   * Gdy podany i `onSaveKartotekaLayout` też podany:
   *  - State hydrowany z tego configu na start (zamiast z tenant-scope activeLayout).
   *  - Zapis przez `onSaveKartotekaLayout` zamiast PUT na tenant-scope endpoint.
   */
  initialLayoutConfig?: Record<string, unknown>;
  /**
   * `initialLayoutConfig` to SEED wygenerowany przez wołającego (np. nowy widok biblioteki
   * kartoteki: zakładka „Dane" ze wszystkimi polami), a nie zapisany układ — builder startuje
   * jako „zmieniony", żeby „Zapisz" zapisał to, co projektant widzi (bez tego zapis menu
   * zostawiał pusty układ).
   */
  initialDirty?: boolean;

  /**
   * Etap 2 kartoteki — callback zapisu override layoutu kartoteki (zamiast tenant-scope PUT).
   * Wywoływany z pełnym `layoutConfig` po kliknięciu Save.
   * Gdy podany razem z `initialLayoutConfig`, saveMutation wywołuje ten callback zamiast PUT.
   */
  onSaveKartotekaLayout?: (layoutConfig: Record<string, unknown>) => Promise<void>;

  /**
   * Fix 401 „Dodaj pole" (2026-07-02) — slug kartoteki systemowej dla adminowej
   * ścieżki dodawania pola. W trybie admin-embedded tenantowy `AddSystemFieldModal`
   * (POST `/system-modules/:slug/fields`) dostaje 401 — sesja jest adminowa (cookie),
   * nie tenantowa. Gdy prop podany: „Dodaj pole" otwiera `AddKartotekaFieldModal`
   * (POST `/admin/kartoteki/:slug/fields`, add-only); paleta odświeża się przez
   * invalidację `['admin','kartoteki',slug,'definition']` → `externalFields`.
   * Gdy NIE podany w trybie admin-embedded: przycisk „Dodaj pole" jest ukryty
   * (pola encji `new_table` dodaje się w edytorze szablonu, zakładka „Pola").
   * W trybie tenantowym ignorowany.
   */
  addFieldKartotekaSlug?: string;

  /**
   * Etap 2 kartoteki — lista typów zakładek read-only w trybie osadzonym.
   * Zakładki tych typów: pasek zakładek bez drag/add/delete; content non-fields
   * pokazuje tylko placeholder (bez przycisków akcji). Zakładki 'fields' i 'subcollection'
   * są zawsze edytowalne (gdy nie ma ich w tej liście).
   */
  readOnlyTabTypes?: TabType[];

  /**
   * ETAP4 S7 — odsprzęga „zakładki read-only co do TREŚCI" (`readOnlyTabTypes`) od
   * „czy można dodawać/usuwać zakładki". Gdy `true`: przycisk „+ Dodaj zakładkę"
   * jest widoczny nawet przy ustawionym `readOnlyTabTypes`, a kosz/ołówek przy
   * zakładkach user-added pojawia się dla zakładek NIEOPUBLIKOWANYCH (znacznik
   * `publishedAt`). Domyślnie `false` — zachowanie bez zmian (tenant-scope builder).
   */
  allowTabManagement?: boolean;

  /**
   * Etap 2 kartoteki — lista kolekcji (zakładek `type='subcollection'`) read-only w trybie
   * osadzonym. Zakładka `subcollection` jest read-only gdy jej pole `collection` należy do tej
   * listy. Uzupełnia `readOnlyTabTypes`: zakładka jest read-only gdy spełnia warunek dla
   * któregokolwiek z obu propsów.
   *
   * Przykład: `['contractor_company_access']` blokuje zakładkę „Dostępy firm"
   * (edytowalne pozostają: Kontakty, Adresy, Konta bankowe).
   */
  readOnlyTabCollections?: string[];

  /**
   * Etap 2 kartoteki — pola palety podane z zewnątrz zamiast fetchowane przez
   * tenant-scope `/api/system-modules/{slug}`.
   *
   * Gdy podany (razem z `initialLayoutConfig` + `onSaveKartotekaLayout`), builder
   * jest w trybie "admin-embedded": ZERO zapytań tenant-scope (`detailQuery`,
   * `rolesQuery`, `helperDetailQuery` wyłączone). Pola palety, pole `fieldById`
   * i etykiety pól w canvasie pochodzą z tej listy.
   *
   * Format: identyczny z `SystemModuleDetail.fields` (tenant-scope). Przekazywane
   * przez `ReferenceFormBuilderEmbedded` z `useKartotekaDefinition(slug).data.fields`.
   */
  externalFields?: SystemModuleField[];

  /**
   * Etap 2 kartoteki — pola helper-encji (podkolekcji) per slug kolekcji, podane
   * z zewnątrz zamiast fetchowane przez tenant-scope `/api/system-modules/{collection}`.
   *
   * Gdy podany w trybie embedded (`isAdminEmbedded=true`), `SubcollectionLayoutEditor`
   * otrzymuje pola palety z tej mapy zamiast strzelać tenant-scope query.
   * Klucze to slugi kolekcji (np. 'contractor_contacts'), wartości to tablice pól.
   *
   * Format: kompatybilny z `TargetField` — musi mieć id/slug/name/fieldType/isCore/sortOrder.
   * Przekazywane przez `ReferenceFormBuilderEmbedded` z
   * `useKartotekaDefinition(slug).data.helperFields`.
   */
  externalHelperFields?: Record<
    string,
    Pick<TargetField, 'id' | 'slug' | 'name' | 'fieldType' | 'isCore' | 'sortOrder'>[]
  >;
  /**
   * Domyślne layouty (sekcje + pola) helper-encji per slug kolekcji.
   * Gdy embedded=true i zakładka subcollection nie ma jeszcze zapisanego formLayout,
   * SubcollectionLayoutEditor używa tych layoutów do zainicjowania canvasu sekcjami
   * (zamiast tenant-scope layoutQ, który w embedded jest wyłączony).
   * Przekazywane z `useKartotekaDefinition(slug).data.helperDefaultLayouts`.
   */
  externalHelperLayouts?: Record<string, unknown>;

  /**
   * Slot przełącznika layoutu w mini-headerze trybu embedded. Gdy podany (tryb
   * admin-embedded), zastępuje WBUDOWANY `LayoutSwitcher` ORAZ wbudowane przyciski
   * resetu (slot przejmuje wybór layoutu + reset). Przycisk „Zapisz" zostaje.
   *
   * Używany przez `MenuItemFormBuilderModal`, żeby NIE dublować przełączników:
   * jeden rząd kontrolek (dropdown nazwanych layoutów + reset + Zapisz) zamiast
   * osobnego dropdownu w nagłówku modala i drugiego w pasku buildera.
   */
  embeddedLayoutSwitcher?: React.ReactNode;

  /**
   * Zgłasza zmianę realnego stanu „są niezapisane zmiany" (globalDirty) do rodzica.
   * Używane przez MenuItemFormBuilderModal do strażnika zamknięcia — żeby pytać o
   * niezapisane zmiany TYLKO gdy user faktycznie coś zmienił w layoucie, a nie po
   * samym kliknięciu (np. w dropdown). Mount/odczyt = false (czysto).
   */
  onDirtyChange?: (dirty: boolean) => void;

  /**
   * Gdy true: builder otwiera się w trybie podglądu (read-only).
   * Przycisk Zapisz jest ukryty. Canvas jest nieedytowalny (pointer-events: none).
   * Używane przez MenuItemFormBuilderModal gdy otworzony przez „Podgląd" na domyślnym layoucie.
   * Domyślnie false — zachowanie bez zmian (edytowalny).
   */
  readOnlyMode?: boolean;

  /**
   * Gdy true: w mini-headerze trybu embedded NIE renderujemy wbudowanego dropdownu
   * `LayoutSwitcher` (przyciski resetu + Zapisz zostają). Używane przez
   * `TenantKartotekaFormBuilderModal` — layout wybiera się w akordeonie pozycji menu,
   * modal edytuje dokładnie jeden layout, więc dropdown „Domyślny/…" tylko myli
   * (pokazywałby „Domyślny", gdy edytujemy np. „Widok minimalny"). Nie działa, gdy
   * podano `embeddedLayoutSwitcher` (slot i tak zastępuje wbudowany switcher).
   * Domyślnie false — zachowanie bez zmian.
   */
  hideEmbeddedLayoutSwitcher?: boolean;
}

/** Wirtualne id layoutu „Domyślny" w trybie embedded (nie istnieje w bazie — reprezentuje pełny domyślny układ renderowany z initialLayoutConfig). */
const EMBEDDED_DEFAULT_LAYOUT_ID = '__embedded_default__';

/**
 * Kontenery `isContainer===true` z ODRĘBNĄ obsługą w canvasie — wykluczone z generycznej
 * gałęzi `ContainerInCanvas` (drop do `children[]`):
 *  - `accordion`/`stepper`/`wizard`/`tabs` — inny model dzieci (accordionItems/steps/tabs),
 *    mają dedykowane komponenty in-canvas / osobne gałęzie,
 *  - `columns` — dedykowany `ColumnsInCanvas`.
 *
 * `panel` NIE jest tu wykluczony — diagnoza T5 potwierdziła, że to zwykły kontener
 * `children[]` (nie ma nic wspólnego ze stepperem/kreatorem), więc łapie się do
 * generycznej gałęzi `ContainerInCanvas` jak card/row/grid/fieldset.
 */
const CONTAINER_DEDICATED_TYPES = new Set<string>([
  'accordion',
  'stepper',
  'wizard',
  'tabs',
  'columns',
]);

/**
 * Wykrywanie kolizji dla kanwy buildera — fix zagnieżdżonych stref zrzutu.
 *
 * Problem: element `columns`/kontener leży WEWNĄTRZ sekcji; obie rejestrują `useDroppable`.
 * Domyślny `closestCenter` porównuje odległość do ŚRODKÓW globalnie — przy zrzucie pola
 * „na kolumnę" wygrywa sekcja-rodzic (większa, jej środek bywa bliżej), więc pole ląduje
 * OBOK kontenera zamiast w środku. Testy jednostkowe tego nie łapią (podają `over` na sztywno).
 *
 * Fix: dla zrzutu z palety i przenoszenia elementów wybieramy NAJGŁĘBSZĄ (najmniejszą
 * powierzchnią) strefę pod kursorem (`pointerWithin`) — pole upuszczone na kolumnie trafia
 * DO kolumny, a nad istniejącym polem → obok niego (reorder). Reorder sekcji / paneli
 * akordeonu / kroków steppera zostaje na `closestCenter` (duże równorzędne kontenery —
 * heurystyka „najmniejszy pod kursorem" myliłaby cel na zagnieżdżone dzieci).
 */
/**
 * Z listy kolizji pod kursorem wybiera NAJGŁĘBSZĄ = o najmniejszej powierzchni droppable.
 * Wyodrębnione i eksportowane dla deterministycznego testu (bez internals dnd-kit).
 * Zagnieżdżony kontener (columns/card/...) ma mniejszy prostokąt niż sekcja-rodzic, więc
 * wygrywa — pole upuszczone „na kolumnie" trafia do kolumny, nie do sekcji.
 */
export function pickInnermostCollision(
  collisions: Collision[],
  droppableRects: Map<string | number, { width: number; height: number }>,
): Collision[] {
  if (collisions.length === 0) return collisions;
  let best = collisions[0]!;
  let bestArea = Number.POSITIVE_INFINITY;
  for (const collision of collisions) {
    const rect = droppableRects.get(collision.id);
    if (!rect) continue;
    const area = rect.width * rect.height;
    if (area < bestArea) {
      bestArea = area;
      best = collision;
    }
  }
  return [best];
}

const builderCanvasCollisionDetection: CollisionDetection = (args) => {
  const source = (args.active.data.current as { source?: string } | undefined)?.source;
  if (source === 'section' || source === 'accordion-panel' || source === 'stepper-step') {
    return closestCenter(args);
  }
  const pointerCollisions = pointerWithin(args);
  if (pointerCollisions.length === 0) return closestCenter(args);
  return pickInnermostCollision(pointerCollisions, args.droppableRects);
};

/**
 * Typy klocków `tab.extras` (zakładki NON-FIELDS), które można zaznaczyć i edytować
 * w prawym panelu przez ich `propertiesEditor` (rodzina obiegów akceptacji). Pozostałe
 * typy zostają „podgląd + usuń". Dołożenie kolejnego typu = jedna pozycja w tej liście.
 */
const EXTRAS_WITH_PROPERTIES_PANEL = ['process_view'] as const;

/** Czy klocek extras ma panel właściwości: typ z whitelisty ORAZ zdefiniowany `propertiesEditor`. */
function extraHasPropertiesPanel(type: string): boolean {
  return (
    (EXTRAS_WITH_PROPERTIES_PANEL as readonly string[]).includes(type) &&
    Boolean(LayoutElementRegistry.get(type)?.propertiesEditor)
  );
}

/**
 * Klocki wykluczone z palety dla zakładek NON-FIELDS (`tab.extras`, renderowane przez
 * `NonFieldsExtrasPanel` — podgląd + usuń; `propertiesEditor` tylko dla typów z
 * `EXTRAS_WITH_PROPERTIES_PANEL`).
 * Klocek wymagający wyboru tabeli/widoku/definicji wpuszczony tam nigdy nie dostałby
 * wartości — cicho renderowałby się jako `null` na realnej kartotece (pre-commit review
 * finding [B]). `widget_view` (Faza 23, Etap 3 T2) wymaga wyboru definicji widgetu w
 * `propertiesEditor` — ten sam powód wykluczenia co tabela/widok dodatkowy. `sql_button`
 * („Przycisk SQL”) wymaga napisu i wyboru procedury — bez edytora nigdy by ich nie dostał.
 */
const NON_FIELDS_EXTRAS_EXCLUDED_TYPES = [
  'additional_view',
  'additional_query_view',
  'widget_view',
  'sql_button',
];

/** Czy typ elementu jest „jeden na layout” (`layoutSingleton` w rejestrze) — wejście dla czystych helperów drzewa. */
function isLayoutSingletonType(type: string): boolean {
  return LayoutElementRegistry.get(type)?.layoutSingleton === true;
}

/** Czy typ elementu jest kontenerem (`isContainer` w rejestrze) — wejście dla czystych helperów drzewa. */
function isLayoutContainerType(type: string): boolean {
  return LayoutElementRegistry.get(type)?.isContainer === true;
}

export function SystemModuleBuilderPage({
  embeddedMode = false,
  slug: slugProp,
  initialLayoutConfig,
  initialDirty = false,
  onSaveKartotekaLayout,
  addFieldKartotekaSlug,
  readOnlyTabTypes,
  allowTabManagement = false,
  readOnlyTabCollections,
  externalFields,
  externalHelperFields,
  externalHelperLayouts,
  embeddedLayoutSwitcher,
  onDirtyChange,
  readOnlyMode = false,
  hideEmbeddedLayoutSwitcher = false,
}: SystemModuleBuilderPageProps = {}) {
  const { slug: slugParam } = useParams<{ slug: string }>();
  const slug = slugProp ?? slugParam;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();

  /**
   * Tryb "admin-embedded": builder osadzony w panelu super admina (ModuleTemplateEditor)
   * z layoutem + polami podanymi z zewnątrz (nie z tenant-scope).
   * Gdy true — ZERO zapytań tenant-scope (detailQuery, rolesQuery, helperDetailQuery).
   */
  const isAdminEmbedded = embeddedMode && !!initialLayoutConfig && !!onSaveKartotekaLayout;
  // Okna kreatora u tenanta (ustawienia elementu menu, kartoteki) też są „osadzone", ale działają
  // w zakresie tenanta — endpointy tenanta są tam dostępne. Bez danych tenanta zostaje tylko
  // kreator osadzony w panelu ITE.
  const hostScope = useHostScope();
  const builderHasTenantData = !isAdminEmbedded || hostScope === 'tenant';

  // ── Data
  // isAdminEmbedded: dane układu i pól przychodzą z propsów (initialLayoutConfig +
  // externalFields) — NIE przez tenant-scope. Wyłączamy wszystkie tenant-scope zapytania:
  // endpointy tenanta odpowiadają w panelu super admina 401, więc zamiast danych byłyby tylko
  // zbędne zapytania i komunikaty o błędzie.
  const detailQuery = useQuery({
    queryKey: ['system-module', slug, 'detail'],
    queryFn: async () => {
      const res = await api.get<SystemModuleDetail>(`/system-modules/${slug}`);
      return res.data;
    },
    enabled: !!slug && !isAdminEmbedded,
  });

  const rolesQuery = useQuery({
    queryKey: ['role-templates'],
    queryFn: async () => {
      const res = await api.get<{ roleTemplates: RoleTemplate[] }>('/role-templates');
      return res.data.roleTemplates;
    },
    // isAdminEmbedded: role-templates to tenant-scope endpoint — wyłączamy w panelu admina.
    // LayoutSwitcher + TabPropertiesPanel otrzymają pustą tablicę ról (read-only nie potrzebuje).
    enabled: !isAdminEmbedded,
  });

  // ── Selected layout (deep-link ?layoutId=X, default = first form layout)
  const requestedLayoutId = params.get('layoutId');
  const layouts = useMemo(
    () => (detailQuery.data?.layouts ?? []).filter((l) => l.layoutType === 'form'),
    [detailQuery.data?.layouts],
  );
  const activeLayout = useMemo(() => {
    if (!layouts.length) return null;
    if (requestedLayoutId) {
      const found = layouts.find((l) => l.id === requestedLayoutId);
      if (found) return found;
    }
    return layouts.find((l) => l.isDefault) ?? layouts[0]!;
  }, [layouts, requestedLayoutId]);

  // #6 — blokada systemowego layoutu: gdy tenant ogląda layout zasiany systemowo
  // (`is_system_managed`, migracja 0121), struktura jest NIETYKALNA — zero zmiany nazwy
  // zakładki, dodawania/usuwania zakładek. Tenant edytuje wyłącznie WŁASNE kopie (Duplikuj)
  // i nowe layouty (`isSystemManaged=false`). W trybie admin-embedded (ITE projektuje
  // szablon systemowy) blokady NIE ma — admin edytuje właśnie ten szablon.
  const isSystemManagedLayout = !isAdminEmbedded && activeLayout?.isSystemManaged === true;

  // ── Local edit state (F12.N E2.T2b krok 2: tree-native state)
  // State przechowuje rekurencyjny `LayoutElement[]` (top-level
  // `[{type:'tabs', tabs: [...]}]`) — naturalny kształt zgodny z backend
  // walidatorem (E1.T7) i SellCall. Wcześniej state był `LayoutTab[]`
  // (płaski, F12.M), konwertowany przy save przez `saveTabsToConfig` z
  // adaptera. Teraz adapter trafia tylko do `loadTabsFromConfig` jako
  // fallback dla starych baz; save bierze `elements` bezpośrednio.
  //
  // Dla kompatybilności z istniejącymi komponentami (SortableTabPill,
  // FieldsTabContent itp.) trzymamy derived `tabs: LayoutTab[]` przez
  // useMemo. Wszystkie setTabs(updater) call sites są opakowane w wrapper
  // setElementsFromTabsUpdater (poniżej).
  const [elements, setElements] = useState<LayoutElement[]>([]);
  const [selectedTabId, setSelectedTabId] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string | null>(null);
  /** Zaznaczony blok TREŚCI (non-field) w canvasie — niezależne od `selectedSectionId`
   * (klik na blok robi `stopPropagation`, patrz `LayoutElementBlock`/`SortableSectionView`).
   * Pokazuje `propertiesEditor` zarejestrowanego typu w prawym panelu (jeśli zdefiniowany). */
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  /** Zaznaczony klocek `tab.extras` zakładki NON-FIELDS (tylko typy z
   * `EXTRAS_WITH_PROPERTIES_PANEL`). Osobny od `selectedElementId` (zakładki fields), żeby
   * nie mieszać selekcji między dwoma ścieżkami; czyszczony przy zmianie zakładki. */
  const [selectedExtraId, setSelectedExtraId] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [layoutFormOpen, setLayoutFormOpen] = useState(false);
  const [layoutFormMode, setLayoutFormMode] = useState<'create' | 'edit'>('create');
  const [layoutFormInitial, setLayoutFormInitial] = useState<Partial<LayoutFormValues>>({});
  const [pendingDeleteLayout, setPendingDeleteLayout] = useState<LayoutOption | null>(null);
  // Import layoutu formularza z pliku (przeniesienie z innego tenanta).
  const [formImportOpen, setFormImportOpen] = useState(false);
  // F12.N D3.3 — zakładka oczekująca na potwierdzenie usunięcia (ConfirmDialog).
  const [pendingDeleteTabId, setPendingDeleteTabId] = useState<string | null>(null);
  // F12.N Etap 4 E4.T2/T3 — modal dodawania/edycji user-added zakładki.
  const [tabFormModalState, setTabFormModalState] = useState<{
    mode: 'add' | 'edit';
    initial?: Partial<TabFormValues>;
    tabId?: string;
  } | null>(null);
  // F12.N Etap 4 E4.T5 — guard niezapisanych zmian przy klik „Wstecz".
  // Gdy `dirty=true` i admin klika Wstecz, otwieramy ConfirmDialog z polem ostrzegającym,
  // że niezapisane zmiany zostaną utracone. Po potwierdzeniu — nawigacja do settings.
  const [pendingBackNavigation, setPendingBackNavigation] = useState(false);
  // Embedded mode — reset canvasu: 'default' = wróć do initialLayoutConfig, 'blank' = wyczyść.
  // null = brak oczekującego resetu. Gdy globalDirty=false — akcja wykonywana od razu
  // bez ConfirmDialog; gdy dirty — ustawiamy ten stan i pokazujemy ConfirmDialog.
  const [pendingReset, setPendingReset] = useState<null | 'default' | 'blank'>(null);
  // F12.N T5.6b — layoutId oczekujący na przełączenie po potwierdzeniu porzucenia
  // niezapisanych zmian (ConfirmDialog zamiast window.confirm, §4.12).
  const [pendingLayoutSwitch, setPendingLayoutSwitch] = useState<string | null>(null);
  // Krok A — parytet lewego panelu z modułami własnymi
  const [leftPanelTab, setLeftPanelTab] = useState<'fields' | 'elements'>('fields');
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);

  // F12.N T5.6b — inline edycja helper-encji (Adresy / Konta bankowe / ...).
  // Mapa scope helper-encji per kolekcja. Init przez `useEffect` poniżej po
  // udanym fetchu `/api/system-modules/<collection>` z `isHelperEntity=true`.
  // Stan przeżywa przełączanie zakładek macierzystych (dirty helper-encji
  // nie ginie gdy admin skacze między zakładkami). Reset tylko przy zmianie
  // macierzystego `activeLayout` (osobny useEffect).
  const [helperEdits, setHelperEdits] = useState<Map<string, HelperEditState>>(new Map());

  // F12.N E2.T2b krok 2: derived `tabs` z `elements`. Single source of truth
  // = `elements`. Każda mutacja `setTabs(updater)` w istniejących handlerach
  // jest zastąpiona przez `updateTabs(updater)` poniżej — wrapper który
  // konwertuje state przez adapter, woła updater, zapisuje z powrotem do
  // elements. To pozwala zachować istniejące handlerzy bez głębokiego refactoru.
  const tabs = useMemo<LayoutTab[]>(() => loadTabsFromConfig({ elements }), [elements]);

  /**
   * Wrapper dla setTabs — pozwala zachować istniejący kod handlerów
   * (które operują na LayoutTab[]) z nowym state (LayoutElement[]).
   *
   * Konwersja: elements → tabs → updater(tabs) → elements.
   * Adapter saveTabsToConfig zwraca shape `{ elements: [...] }`.
   */
  const updateTabs = useCallback((updater: (prev: LayoutTab[]) => LayoutTab[]) => {
    setElements((prevElements) => {
      const prevTabs = loadTabsFromConfig({ elements: prevElements });
      const nextTabs = updater(prevTabs);
      // T2: zachowaj elementy view-level (rodzeństwo elementu `tabs`, np. page-header/
      // highlight) — inaczej każda mutacja zakładek by je skasowała na round-tripie.
      const viewLevelSiblings = prevElements.filter((el) => el.type !== 'tabs');
      return saveTabsToConfig(nextTabs, viewLevelSiblings).elements;
    });
  }, []);

  // Hydrate elements gdy zmienia się activeLayout.
  // F12.N E2.T2b krok 2: state to teraz `LayoutElement[]`. Load przechodzi przez
  // adapter `loadTabsFromConfig` (zwraca LayoutTab[]) + `saveTabsToConfig` (pakuje
  // LayoutTab[] z powrotem w `{elements: [...]}`) — żeby zachować legacy fallback
  // (pre-C.7 sections, F12.M płaski) bez duplikacji logiki konwersji.
  // Etap 2 kartoteki: pomijamy gdy `initialLayoutConfig` podany — ten useEffect
  // obsługuje tylko przypadek tenant-scope.
  useEffect(() => {
    if (initialLayoutConfig) return; // tryb override — osobny useEffect poniżej
    if (!activeLayout?.layoutConfig) return;
    // Nakładka per-tenant (ukrycia/kolejność zakładek na layoucie systemowym) —
    // nałóż na config zanim zbudujesz stan zakładek, żeby oko/kolejność w builderze
    // odzwierciedlały bieżącą nakładkę (żyje osobno od layout_config).
    const configWithOverrides = applyTabOverrides(
      activeLayout.layoutConfig,
      activeLayout.tabOverrides ?? null,
    );
    const normalizedTabs = loadTabsFromConfig(configWithOverrides);
    // T2: zachowaj elementy view-level z zapisanego layoutu (rodzeństwo przed `tabs`).
    const viewLevelSiblings = extractViewLevelElements(configWithOverrides);
    const nextElements = saveTabsToConfig(normalizedTabs, viewLevelSiblings).elements;
    setElements(nextElements);
    setDirty(false);
    setSelectedTabId(normalizedTabs[0]?.id ?? null);
    setSelectedSectionId(normalizedTabs[0]?.sections?.[0]?.id ?? null);
    // F12.N T5.6b — reset scopes helper-encji przy zmianie macierzystego layoutu.
    // Nowy layout = inne ID, dirty cache nie jest porównywalny. Helper-encje
    // zostaną ponownie zainicjalizowane przy najbliższym klikenięciu na zakładkę
    // subcollection.
    setHelperEdits(new Map());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLayout?.id, initialLayoutConfig]);

  // Etap 2 kartoteki — hydrate z initialLayoutConfig (override per-modułowy super admina).
  // Uruchamiany raz przy mount (initialLayoutConfig jest stabilny per render parenta).
  useEffect(() => {
    if (!initialLayoutConfig) return;
    const normalizedTabs = loadTabsFromConfig(initialLayoutConfig);
    const viewLevelSiblings = extractViewLevelElements(initialLayoutConfig);
    const nextElements = saveTabsToConfig(normalizedTabs, viewLevelSiblings).elements;
    setElements(nextElements);
    setDirty(false);
    setSelectedTabId(normalizedTabs[0]?.id ?? null);
    setSelectedSectionId(normalizedTabs[0]?.sections?.[0]?.id ?? null);
    setHelperEdits(new Map());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Seed od wołającego = niezapisana zmiana — TYLKO RAZ przy mount (po hydratacji wyżej, która
  // zeruje `dirty`). Zależność od `initialLayoutConfig` zapalała „zmieniony" ponownie po każdym
  // odświeżeniu danych rodzica (nowa tożsamość configu), także tuż po udanym zapisie.
  useEffect(() => {
    if (initialDirty && initialLayoutConfig) setDirty(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // ── Save mutation (PUT layoutu macierzystego + per dirty helper-encja w nowym kształcie)
  // F12.N pre-E2: adapter `saveTabsToConfig` pakuje stary płaski state buildera
  // (`LayoutTab[]`) w nowy rekurencyjny kształt (`{ elements: [{ type: 'tabs',
  // tabs: [...] }] }`) wymagany przez walidator backendu (E1.T7).
  //
  // F12.N T5.6b: po PUT macierzystego, iteracja po `helperEdits` — per dirty helper
  // PUT do `/api/system-modules/<collection>/layouts/<layoutId>`. Sekwencyjnie
  // żeby przy pierwszym błędzie zatrzymać dalsze PUT-y, ale zachować info o tym
  // co się już udało zapisać (sprzątanie dirty per scope w onSuccess).
  const saveMutation = useMutation({
    mutationFn: async () => {
      const result: {
        successScopes: string[]; // '__main__' albo collection slug
        failedScope: string | null;
        failedError: unknown;
      } = { successScopes: [], failedScope: null, failedError: null };

      // 1. Macierzysty layout (gdy dirty)
      if (dirty) {
        try {
          // F12.N E2.T2b krok 3: state to już `LayoutElement[]` (tree-native),
          // nie ma potrzeby konwersji przez adapter. Pakujemy bezpośrednio
          // w shape `{ elements: [...] }` wymagany przez walidator.
          const layoutConfig = { elements };

          if (onSaveKartotekaLayout) {
            // Etap 2 kartoteki: zapis per-modułowego override przez callback parenta.
            // PUT /api/admin/module-templates/[code]/entities/[entitySlug]/kartoteka-layout
            await onSaveKartotekaLayout(layoutConfig);
          } else if (activeLayout && isSystemManagedLayout) {
            // Layout SYSTEMOWY: struktura/treść nietykalna — jedyne dozwolone zmiany to
            // UKRYCIA (zakładki + kolejność zakładek, ukryte zwykłe pola, ukryte kolumny/pola
            // formatek). Zapisujemy je jako NAKŁADKĘ per-tenant (`tabOverrides`), wyprowadzoną
            // z całego drzewa `elements`, NIE cały `layout_config` — inaczej re-seed by je zdmuchnął.
            await api.put<{ layout: SystemModuleLayout }>(
              `/system-modules/${slug}/layouts/${activeLayout.id}`,
              { tabOverrides: deriveSystemOverrides(elements) },
            );
          } else if (activeLayout) {
            // Normalny tenant-scope PUT (layout niesystemowy — pełna edycja treści)
            await api.put<{ layout: SystemModuleLayout }>(
              `/system-modules/${slug}/layouts/${activeLayout.id}`,
              { layoutConfig },
            );
          }
          result.successScopes.push('__main__');
        } catch (err) {
          result.failedScope = '__main__';
          result.failedError = err;
          return result;
        }
      }

      // 2. Per dirty helper-encja
      for (const [collection, edit] of helperEdits.entries()) {
        if (!edit.dirty) continue;
        try {
          const layoutConfig = saveTabsToConfig(edit.tabs);
          await api.put<{ layout: SystemModuleLayout }>(
            `/system-modules/${collection}/layouts/${edit.layoutId}`,
            { layoutConfig },
          );
          result.successScopes.push(collection);
        } catch (err) {
          result.failedScope = collection;
          result.failedError = err;
          return result;
        }
      }

      return result;
    },
    onSuccess: async (result) => {
      // Sprzątanie dirty per zapisany scope (zachowanie state'u dla nie-zapisanych).
      if (result.successScopes.includes('__main__')) {
        setDirty(false);
      }
      const savedHelperScopes = result.successScopes.filter((s) => s !== '__main__');
      if (savedHelperScopes.length > 0) {
        setHelperEdits((prev) => {
          const next = new Map(prev);
          for (const collection of savedHelperScopes) {
            const cur = next.get(collection);
            if (cur) next.set(collection, { ...cur, dirty: false });
          }
          return next;
        });
      }

      if (result.failedScope) {
        const err = result.failedError;
        // F12.N E2.T5: backend zwraca `issues: string[]` przy 400 LAYOUT_CONFIG_INVALID
        // (validateLayoutConfig zwraca konkretne błędy semantyki — max depth, duplikat
        // fieldId, duplikat slug, niepoprawny typ). Pokazujemy je w description toastu
        // zamiast generic error code, żeby admin wiedział co poprawić.
        const data = isAxiosError(err) ? (err.response?.data as Record<string, unknown>) : null;
        const issues = data && Array.isArray(data.issues) ? (data.issues as unknown[]) : null;
        const errorCode = data && typeof data.error === 'string' ? data.error : null;
        // S7: czytelny komunikat dla bramy publikacji zakładek (zamiast surowego kodu).
        const isPublishedTabError =
          errorCode === 'TAB_PUBLISHED_CANNOT_DELETE' ||
          errorCode === 'TAB_PUBLISHED_CANNOT_RENAME';
        // Serwer odrzuca layout z drugim blokiem typu „jeden na layout" kodem
        // `LAYOUT_SINGLETON_DUPLICATE` (+ `types` — typy elementów); `issues` są po angielsku, więc
        // komunikat budujemy z tłumaczonych nazw typów (każda we własnych cudzysłowach, liczba
        // mnoga wg liczby typów — `layoutSingletonRejectedMessage`). Pusta lista `types` = brak nazw
        // do pokazania → komunikat z `issues` / ogólny kod, nie zdanie z pustym „”.
        const singletonTypes = extractLayoutSingletonTypes(err);
        const description = singletonTypes
          ? layoutSingletonRejectedMessage(t, singletonTypes)
          : issues && issues.length > 0
            ? issues.map((i) => String(i)).join('\n')
            : isPublishedTabError
              ? t('tenant.systemModuleBuilder.tabPublishedTooltip')
              : (errorCode ?? undefined);
        toast.show({
          variant: 'error',
          title: t('tenant.systemModuleBuilder.toasts.savePartialError', {
            scope: result.failedScope === '__main__' ? slug : result.failedScope,
            defaultValue: 'Błąd zapisu layoutu „{{scope}}"',
          }),
          description,
        });
      } else {
        toast.show({ variant: 'success', title: t('tenant.systemModuleBuilder.toasts.saved') });
      }

      // Refetch wszystkich modułów które były dotknięte (macierzysty + helper-encje).
      await queryClient.refetchQueries({ queryKey: ['system-module', slug] });
      for (const collection of savedHelperScopes) {
        await queryClient.refetchQueries({
          queryKey: ['system-module', collection, 'detail'],
        });
      }
      // Inwaliduj CACHE AKTYWNEGO LAYOUTU kart — inaczej po zapisie w builderze karta rekordu
      // pokazuje STARY layout aż do twardego reloadu (ukrycie pola/kolumny wygląda jak niedziałające).
      // Jeden klucz: hook `useSystemModuleActiveLayout` (`system-module-layout-active`) — z niego
      // korzystają też zakładki „Dane". Dla macierzystego modułu i dotkniętych helper-encji
      // (np. `addresses`).
      const touchedLayoutScopes = [slug, ...savedHelperScopes].filter(
        (s): s is string => typeof s === 'string' && s.length > 0,
      );
      for (const s of touchedLayoutScopes) {
        await queryClient.invalidateQueries({ queryKey: ['system-module-layout-active', s] });
      }
    },
    onError: (err) => {
      // Tu trafiamy tylko gdy mutationFn rzuci niespodziewanie (mutationFn
      // standardowo zwraca result, nawet przy błędach pojedynczych PUT).
      const message = isAxiosError(err) ? err.response?.data?.error : null;
      toast.show({
        variant: 'error',
        title: t('tenant.systemModuleBuilder.toasts.saveError'),
        description: message ? String(message) : undefined,
      });
    },
  });

  // ── Layout meta mutations (create/edit/delete) — bez zmian z C.4
  const createLayoutMutation = useMutation({
    mutationFn: async (values: LayoutFormValues) => {
      const res = await api.post<{ layout: SystemModuleLayout }>(
        `/system-modules/${slug}/layouts`,
        {
          layoutType: 'form',
          name: values.name,
          isDefault: values.isDefault,
          previewDockEnabled: values.previewDockEnabled,
          categoryId: values.categoryId,
          roleIds: values.roleIds,
          layoutConfig: { elements: [] },
        },
      );
      return res.data.layout;
    },
    onSuccess: async (created) => {
      toast.show({
        variant: 'success',
        title: t('tenant.systemModuleBuilder.toasts.layoutCreated'),
      });
      setLayoutFormOpen(false);
      await queryClient.refetchQueries({ queryKey: ['system-module', slug] });
      const next = new URLSearchParams(params);
      next.set('layoutId', created.id);
      setParams(next, { replace: true });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.systemModuleBuilder.toasts.layoutCreateError'),
      });
    },
  });

  // Duplikacja layoutu — kopia struktury (layoutConfig) źródła jako nowy layout (isDefault=false).
  const duplicateLayoutMutation = useMutation({
    mutationFn: async (sourceId: string) => {
      const source = (detailQuery.data?.layouts ?? []).find((l) => l.id === sourceId);
      if (!source) throw new Error('LAYOUT_NOT_FOUND');
      const res = await api.post<{ layout: SystemModuleLayout }>(
        `/system-modules/${slug}/layouts`,
        {
          layoutType: 'form',
          name: t('tenant.systemModuleBuilder.layoutCopyName', { name: source.name }),
          isDefault: false,
          previewDockEnabled: source.previewDockEnabled,
          categoryId: source.categoryId,
          roleIds: source.roleIds,
          layoutConfig: source.layoutConfig,
        },
      );
      return res.data.layout;
    },
    onSuccess: async (created) => {
      toast.show({
        variant: 'success',
        title: t('tenant.systemModuleBuilder.toasts.layoutDuplicated'),
      });
      await queryClient.refetchQueries({ queryKey: ['system-module', slug] });
      const next = new URLSearchParams(params);
      next.set('layoutId', created.id);
      setParams(next, { replace: true });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.systemModuleBuilder.toasts.layoutDuplicateError'),
      });
    },
  });

  const updateLayoutMetaMutation = useMutation({
    mutationFn: async ({ layoutId, values }: { layoutId: string; values: LayoutFormValues }) => {
      const res = await api.put<{ layout: SystemModuleLayout }>(
        `/system-modules/${slug}/layouts/${layoutId}`,
        {
          name: values.name,
          isDefault: values.isDefault,
          previewDockEnabled: values.previewDockEnabled,
          categoryId: values.categoryId,
          roleIds: values.roleIds,
        },
      );
      return res.data.layout;
    },
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('tenant.systemModuleBuilder.toasts.layoutUpdated'),
      });
      setLayoutFormOpen(false);
      await queryClient.refetchQueries({ queryKey: ['system-module', slug] });
    },
    onError: (err) => {
      // B.2+B.3 — layout centralny: komunikat czytelny zamiast generycznego (mirror
      // wzorca isPublishedTabError wyżej w tym pliku).
      const code = (isAxiosError(err) ? err.response?.data?.error : null) as string | null;
      toast.show({
        variant: 'error',
        title: t('tenant.systemModuleBuilder.toasts.layoutUpdateError'),
        description:
          code === 'CENTRAL_LAYOUT_READONLY'
            ? t('tenant.systemModuleBuilder.layoutCentralTooltip')
            : undefined,
      });
    },
  });

  // B.2+B.3 — szybkie „ustaw jako domyślny" bez otwierania modala edycji (jedyna
  // ścieżka UI ustawiająca isDefault na layoucie CENTRALNYM — pełny modal edycji
  // wysyła name/categoryId/roleIds razem z isDefault, co backend blokuje jako
  // zmianę treści producenta; ten PUT wysyła WYŁĄCZNIE {isDefault: true}, co
  // backend jawnie przepuszcza dla layoutów centralnych i lokalnych jednakowo).
  const setLayoutDefaultMutation = useMutation({
    mutationFn: async (layoutId: string) => {
      const res = await api.put<{ layout: SystemModuleLayout }>(
        `/system-modules/${slug}/layouts/${layoutId}`,
        { isDefault: true },
      );
      return res.data.layout;
    },
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('tenant.systemModuleBuilder.toasts.layoutSetDefault'),
      });
      await queryClient.refetchQueries({ queryKey: ['system-module', slug] });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.systemModuleBuilder.toasts.layoutSetDefaultError'),
      });
    },
  });

  // B.2+B.3 — analogiczna wąska ścieżka dla `previewDockEnabled` (dok podglądu
  // załączników). Ten sam wzorzec co `setLayoutDefaultMutation`: payload
  // JEDNOPOLOWY {previewDockEnabled} przechodzi bramkę na layoucie centralnym,
  // bo nie niesie pól treści producenta (name/categoryId/roleIds). W odróżnieniu
  // od isDefault to TOGGLE per layout (nie wzajemnie wykluczający) — wołający
  // przekazuje docelową wartość jawnie (zwykle `!layout.previewDockEnabled`).
  const setLayoutPreviewDockMutation = useMutation({
    mutationFn: async ({ layoutId, next }: { layoutId: string; next: boolean }) => {
      const res = await api.put<{ layout: SystemModuleLayout }>(
        `/system-modules/${slug}/layouts/${layoutId}`,
        { previewDockEnabled: next },
      );
      return res.data.layout;
    },
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('tenant.systemModuleBuilder.toasts.layoutPreviewDockUpdated'),
      });
      await queryClient.refetchQueries({ queryKey: ['system-module', slug] });
    },
    onError: () => {
      toast.show({
        variant: 'error',
        title: t('tenant.systemModuleBuilder.toasts.layoutPreviewDockUpdateError'),
      });
    },
  });

  const deleteLayoutMutation = useMutation({
    mutationFn: async (layoutId: string) => {
      await api.delete(`/system-modules/${slug}/layouts/${layoutId}`);
    },
    onSuccess: async () => {
      toast.show({
        variant: 'success',
        title: t('tenant.systemModuleBuilder.toasts.layoutDeleted'),
      });
      await queryClient.refetchQueries({ queryKey: ['system-module', slug] });
      const next = new URLSearchParams(params);
      next.delete('layoutId');
      setParams(next, { replace: true });
    },
    onError: (err) => {
      const code = (isAxiosError(err) ? err.response?.data?.error : null) as string | null;
      toast.show({
        variant: 'error',
        title: t('tenant.systemModuleBuilder.toasts.layoutDeleteError'),
        description:
          code === 'CENTRAL_LAYOUT_READONLY'
            ? t('tenant.systemModuleBuilder.layoutCentralTooltip')
            : undefined,
      });
    },
  });

  // ── Selected tab helpers
  const selectedTab = tabs.find((t) => t.id === selectedTabId) ?? null;
  // Zmiana zakładki czyści selekcję klocka extras (nie wraca po powrocie na zakładkę).
  useEffect(() => {
    setSelectedExtraId(null);
  }, [selectedTabId]);
  // Selekcja klocków extras tylko na własnym layoucie i poza podglądem — tak jak edycja pól
  // (layout systemowy jest dla tenanta tylko-do-odczytu, a podgląd nie edytuje).
  const extrasSelectionEnabled = !readOnlyMode && !isSystemManagedLayout;
  const selectedExtra =
    extrasSelectionEnabled && selectedTab && selectedTab.type !== 'fields' && selectedExtraId
      ? ((selectedTab.extras ?? []).find(
          (e) => e.id === selectedExtraId && extraHasPropertiesPanel(e.type),
        ) ?? null)
      : null;
  const selectedTabSections = selectedTab?.type === 'fields' ? (selectedTab.sections ?? []) : [];
  // T2: elementy view-level (rodzeństwo `tabs`) — renderowane nad paskiem zakładek.
  const viewLevelElements = useMemo(() => elements.filter((el) => el.type !== 'tabs'), [elements]);
  // Typy singletonów już obecne — do wykluczenia z palety (blokada drugiego page-header).
  const excludedViewLevelSingletonTypes = useMemo(
    () =>
      viewLevelElements
        .filter((el) => LayoutElementRegistry.get(el.type)?.viewLevelSingleton)
        .map((el) => el.type),
    [viewLevelElements],
  );
  // Typy `layoutSingleton` (np. `document_items_view`, `vat_register_view`) już obecne GDZIEKOLWIEK
  // w layoucie — całe drzewo `elements` (zakładki, sekcje i ich dzieci, klocki luźne zakładek,
  // panele akordeonu, kroki, rodzeństwo view-level). Nakładka per-tenant jest kluczowana typem,
  // więc drugi blok tego typu nie ma sensu. Usunięcie bloku zwalnia typ w palecie.
  const presentLayoutSingletonTypes = useMemo(
    () => Array.from(collectElementTypes(elements)).filter(isLayoutSingletonType),
    [elements],
  );
  // Wykluczenia palety w zakładce 'fields' (+ drop z palety) — view-level i layoutowe singletony.
  const excludedSingletonTypes = useMemo(
    () => [...excludedViewLevelSingletonTypes, ...presentLayoutSingletonTypes],
    [excludedViewLevelSingletonTypes, presentLayoutSingletonTypes],
  );
  // Wykluczenia palety w zakładkach non-fields (klocki luźne `tab.extras`).
  const nonFieldsExcludedTypes = useMemo(
    () => [...NON_FIELDS_EXTRAS_EXCLUDED_TYPES, ...presentLayoutSingletonTypes],
    [presentLayoutSingletonTypes],
  );
  /** Nazwa typu elementu do komunikatów: tłumaczona (`labelKey` z rejestru), inaczej `label`, inaczej `type`. */
  function layoutElementDisplayName(type: string): string {
    return layoutElementDisplayNameOf(t, type);
  }
  /**
   * Defensywna blokada dodania drugiego bloku typu `layoutSingleton` — niezależnie od wykluczenia
   * w palecie (drop, klik, duplikat sekcji). Decyzję podejmuje czysta `isLayoutSingletonTaken`;
   * tu tylko toast. Zwraca `true` (i pokazuje toast), gdy typ już jest w layoucie, a dodanie trzeba
   * przerwać.
   */
  function rejectLayoutSingletonDuplicate(type: string): boolean {
    if (!isLayoutSingletonTaken(elements, type, isLayoutSingletonType)) return false;
    toast.show({
      variant: 'warning',
      title: t('tenant.systemModuleBuilder.toasts.layoutSingletonExists', {
        element: layoutElementDisplayName(type),
      }),
    });
    return true;
  }

  // F12.N T5.6b — collection helper-encji dla aktywnej zakładki subcollection.
  // null gdy zakładka nie jest subcollection, lub gdy collection nie ustawione.
  // Faktyczna decyzja „inline" zapada dopiero gdy fetch wraca z isHelperEntity=true
  // (poniżej w `helperDetailQuery` + useEffect init).
  const activeHelperCollection =
    selectedTab?.type === 'subcollection' && selectedTab.collection ? selectedTab.collection : null;

  // Fetch szczegółów helper-encji (module + fields + layouts) — jeden strzał
  // jak macierzysty `detailQuery`. Cache 2 min — przełączenie zakładek między
  // helper-encjami nie spamuje API.
  // isAdminEmbedded: helper-encje przez tenant-scope też wyłączone — zakładki
  // subcollection w trybie embedded pokazują placeholder (read-only view).
  const helperDetailQuery = useQuery({
    queryKey: ['system-module', activeHelperCollection, 'detail'],
    queryFn: async () => {
      const res = await api.get<SystemModuleDetail>(`/system-modules/${activeHelperCollection}`);
      return res.data;
    },
    enabled: !!activeHelperCollection && !isAdminEmbedded,
    staleTime: 2 * 60 * 1000,
  });

  // Init helperEdits dla danej kolekcji po pomyślnym fetchu — tylko gdy backend
  // potwierdza `isHelperEntity=true`. Inaczej zostaje stary placeholder + przycisk
  // navigujący do osobnego buildera (np. dla `contractor_contacts`).
  useEffect(() => {
    if (!activeHelperCollection) return;
    const data = helperDetailQuery.data;
    if (!data) return;
    if (!data.module.isHelperEntity) return;
    setHelperEdits((prev) => {
      if (prev.has(activeHelperCollection)) return prev;
      const forms = (data.layouts ?? []).filter((l) => l.layoutType === 'form');
      const defaultLayout = forms.find((l) => l.isDefault) ?? forms[0] ?? null;
      if (!defaultLayout) return prev;
      const helperTabs = loadTabsFromConfig(defaultLayout.layoutConfig);
      const next = new Map(prev);
      next.set(activeHelperCollection, {
        layoutId: defaultLayout.id,
        tabs: helperTabs,
        fields: data.fields,
        selectedSectionId: helperTabs[0]?.sections?.[0]?.id ?? null,
        dirty: false,
      });
      return next;
    });
  }, [activeHelperCollection, helperDetailQuery.data]);

  // Aktywny scope helper-encji (computed). Gdy obecny i layout zaczął się
  // ładować — render w canvasie używa tego stanu zamiast macierzystego.
  const activeHelperEdit = activeHelperCollection
    ? (helperEdits.get(activeHelperCollection) ?? null)
    : null;

  // Wirtualna zakładka helper-encji (1 zakładka 'data' z sekcjami pól).
  // FieldsTabContent oczekuje `selectedTab: LayoutTab` — w helper-mode podajemy
  // pierwszą (i jedyną) zakładkę helper-encji jako wirtualny selectedTab.
  const helperVirtualTab = activeHelperEdit?.tabs[0] ?? null;
  const helperVirtualTabSections = useMemo(
    () => helperVirtualTab?.sections ?? [],
    [helperVirtualTab?.sections],
  );

  // isAdminEmbedded: pola palety z `externalFields` (podanych przez ReferenceFormBuilderEmbedded
  // z useKartotekaDefinition) zamiast tenant-scope detailQuery.data.fields.
  // `useMemo` (nie plain const) — stabilna referencja `allFields`, żeby zależne od niej
  // useMemo (availableFields/usedFields/fieldById) nie dostały ostrzeżenia o niestabilnej
  // zależności. Pola WYLICZANE procesu (readonly) dokładamy z rejestru w kodzie
  // (`computedProcessPaletteFields`, ADR-0093 — nie ma ich w katalogu system_module_fields),
  // żeby dało się je przeciągnąć na layout (render readonly zapewnia config faktur).
  const allFields = useMemo<SystemModuleField[]>(() => {
    const base = externalFields ?? detailQuery.data?.fields ?? [];
    const computed = computedProcessPaletteFields(slug, new Set(base.map((f) => f.slug)), t);
    return computed.length > 0 ? [...base, ...computed] : base;
  }, [externalFields, detailQuery.data, slug, t]);

  // Faza 2 (konfigurowalne zakładki): pola są WIELOKROTNEGO użytku — paleta jest
  // ŹRÓDŁEM, nie koszykiem jednorazowym. To samo pole można wrzucić wiele razy (na tej
  // samej i innych zakładkach). Dlatego NIE wykluczamy „już użytych" ani ich nie
  // wyszarzamy — cała lista pól jest zawsze przeciągalna, `usedFields` puste.
  const availableFields = allFields;
  const usedFields = useMemo<SystemModuleField[]>(() => [], []);

  const fieldById = useMemo(() => {
    const map = new Map<string, SystemModuleField>();
    for (const f of allFields) map.set(f.id, f);
    return map;
  }, [allFields]);

  // ── F12.N T5.6b — paleta dla helper-encji (analogicznie do macierzystych) ──
  // Faza 2: pola wielokrotnego użytku — cała lista przeciągalna, nic nie wyszarzone.
  const helperAvailableFields = activeHelperEdit?.fields ?? [];
  const helperUsedFields = useMemo<SystemModuleField[]>(() => [], []);

  const helperFieldById = useMemo(() => {
    const map = new Map<string, SystemModuleField>();
    for (const f of activeHelperEdit?.fields ?? []) map.set(f.id, f);
    return map;
  }, [activeHelperEdit?.fields]);

  // ── Tab operations (top-bar)
  function updateTab(tabId: string, patch: Partial<LayoutTab>) {
    updateTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, ...patch } : t)));
    setDirty(true);
  }

  function toggleTabHidden(tabId: string) {
    updateTabs((prev) => prev.map((t) => (t.id === tabId ? { ...t, hidden: !t.hidden } : t)));
    setDirty(true);
  }

  function onTabsDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIdx = tabs.findIndex((t) => t.id === active.id);
    const newIdx = tabs.findIndex((t) => t.id === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    updateTabs((prev) =>
      arrayMove(prev, oldIdx, newIdx).map((t, idx) => ({ ...t, sortOrder: idx })),
    );
    setDirty(true);
  }

  // E2.T2a — dodaj nową zakładkę z paska tabs wewnątrz canvasu ("+ Nowa").
  // F12.N Etap 4 E4.T2: zamiast bezpośredniego dodania z auto-gen nazwą, otwieramy
  // modal w którym admin wpisuje nazwę + (opcjonalnie) edytuje slug + wybiera ikonę.
  function openAddTabModal() {
    setTabFormModalState({ mode: 'add', initial: undefined });
  }

  // F12.N Etap 4 E4.T3: otwiera modal edycji nazwy/ikony/slug user-added zakładki.
  function openEditTabModal(tab: LayoutTab) {
    setTabFormModalState({
      mode: 'edit',
      initial: { title: tab.title, slug: tab.slug ?? '', icon: tab.icon },
      tabId: tab.id,
    });
  }

  // F12.N Etap 4: callback z modal'a — faktycznie tworzy lub aktualizuje zakładkę.
  // `values.tabType === 'additional_query'` → nowa zakładka „Widok SQL"; 'additional' →
  // nowa zakładka „Tabela dodatkowa" — obie bez sections, cel (widok/tabela) wybiera się
  // później w canvasie (`AdditionalQueryConfigPanel` / `AdditionalTableConfigPanel`).
  function handleTabFormSubmit(values: TabFormValues) {
    const state = tabFormModalState;
    if (!state) return;
    if (state.mode === 'add') {
      const newTabId = crypto.randomUUID();
      // Emitować dosłownie 'additional', NIGDY 'satellite' — bez `tableSlug` 'satellite'
      // cichnie zamienia się w 'panel' przy najbliższym `updateTabs` (layout-shape-adapter.ts).
      const newTabType: TabType =
        values.tabType === 'additional_query'
          ? 'additional_query'
          : values.tabType === 'additional'
            ? 'additional'
            : 'fields';
      const newTab: LayoutTab = {
        id: newTabId,
        slug: values.slug,
        title: values.title,
        ...(values.icon ? { icon: values.icon } : {}),
        type: newTabType,
        hidden: false,
        sortOrder: tabs.length,
        ...(newTabType === 'fields' ? { sections: [] } : {}),
      };
      updateTabs((prev) => [...prev, newTab]);
      setSelectedTabId(newTabId);
      setSelectedSectionId(null);
    } else if (state.tabId) {
      // edit mode — aktualizujemy istniejącą zakładkę
      updateTab(state.tabId, {
        title: values.title,
        slug: values.slug,
        icon: values.icon ?? undefined,
      });
    }
    setDirty(true);
    setTabFormModalState(null);
  }

  // F12.N D3.3 — usuwanie user-added zakładki. `SortableTabPill` widzi przycisk
  // tylko gdy `isUserAddedTab(tab)` (slug pasuje do `^tab-[0-9a-f]{8}$`).
  // Akcja idzie przez ConfirmDialog (bo destrukcyjna, CLAUDE.md §4.12 zero window.confirm).
  function removeTab(tabId: string) {
    updateTabs((prev) => {
      const next = prev.filter((t) => t.id !== tabId).map((t, idx) => ({ ...t, sortOrder: idx }));
      // Jeśli usuwana zakładka była aktywna, wybierz pierwszą z pozostałych.
      if (selectedTabId === tabId) {
        setSelectedTabId(next[0]?.id ?? null);
        setSelectedSectionId(null);
      }
      return next;
    });
    setDirty(true);
  }

  // ── Section operations (canvas dla taba 'fields')
  function addSection() {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    const newSection: LayoutSection = {
      id: crypto.randomUUID(),
      title: null,
      columns: 2,
      elements: [],
    };
    updateTab(selectedTab.id, {
      sections: [...(selectedTab.sections ?? []), newSection],
    });
    setSelectedSectionId(newSection.id);
  }

  function removeSection(sectionId: string) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    updateTab(selectedTab.id, {
      sections: (selectedTab.sections ?? []).filter((s) => s.id !== sectionId),
    });
    if (selectedSectionId === sectionId) setSelectedSectionId(null);
  }

  // F12.N E2.T4: duplicate sekcji — klonuje sekcję z wszystkimi elementami,
  // wstawia ZA oryginałem. Nowe UUID dla sekcji + każdego elementu (żeby
  // walidator backendu nie wykrył duplikatu id). Title dostaje sufiks „(kopia)".
  function duplicateSection(sectionId: string) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    const sections = selectedTab.sections ?? [];
    const idx = sections.findIndex((s) => s.id === sectionId);
    if (idx < 0) return;
    const source = sections[idx]!;
    // Kopia NIE może powielić typu „jeden na layout" (document_items_view / vat_register_view) —
    // oryginał zostaje w layoucie, więc klon dostaje sekcję bez niego (+ toast).
    const duplicatedSingletons = Array.from(collectElementTypes(source.elements)).filter(
      isLayoutSingletonType,
    );
    for (const type of duplicatedSingletons) rejectLayoutSingletonDuplicate(type);
    const clonedElements =
      duplicatedSingletons.length > 0
        ? removeElementsOfTypes(source.elements, new Set(duplicatedSingletons))
        : source.elements;
    // Kopia, w której po wycięciu bloków „jeden na layout" nie został żaden element poza kontenerami
    // (sama skorupa: sekcje/zakładki/akordeony bez zawartości), nie ma sensu — zostaje sam toast,
    // bez nowej pustej sekcji. Źródło, które od początku było puste, duplikuje się normalnie.
    if (
      duplicatedSingletons.length > 0 &&
      !hasNonContainerElement(clonedElements, isLayoutContainerType)
    ) {
      return;
    }
    const cloned: LayoutSection = {
      id: crypto.randomUUID(),
      title: source.title ? `${source.title} (kopia)` : null,
      columns: source.columns,
      elements: clonedElements.map((el) => ({ ...el, id: crypto.randomUUID() })),
    };
    updateTab(selectedTab.id, {
      sections: [...sections.slice(0, idx + 1), cloned, ...sections.slice(idx + 1)],
    });
    setSelectedSectionId(cloned.id);
  }

  function updateSection(sectionId: string, patch: Partial<LayoutSection>) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    updateTab(selectedTab.id, {
      sections: (selectedTab.sections ?? []).map((s) =>
        s.id === sectionId ? { ...s, ...patch } : s,
      ),
    });
  }

  /**
   * Merge `patch` do `settings` bloku treści. Łatki przychodzą z DWÓCH źródeł: z panelu właściwości
   * (`propertiesEditor` typu zarejestrowanego w `LayoutElementRegistry`, host w prawym panelu —
   * patrz `ElementProperties` niżej) oraz z kontrolek na płótnie (`onSettingsChange` przekazane do
   * `render` elementu, np. edytory układu kolumn tabel czy ikonka oka formatek). Blok może siedzieć w
   * DOWOLNEJ sekcji zakładki (nie tylko `selectedSectionId` — klik na blok robi
   * `stopPropagation`, więc sekcja i element mogą wskazywać różne miejsca), więc
   * szukamy go po wszystkich sekcjach zamiast zakładać `selectedSectionId`.
   * `updateInTree`/`findInTree` (patrz `removeFieldFromSection` niżej) ogarniają
   * też zagnieżdżenie w panelu akordeonu/kroku steppera W OBRĘBIE danej sekcji.
   */
  function updateElementSettings(elementId: string, patch: Record<string, unknown>) {
    // Układ systemowy: ukrycia elementu z kluczem nakładki trafiają do wszystkich jego kopii
    // w drzewie (patrz `applySystemHidePatch`), nie tylko do klikniętej.
    if (isSystemManagedLayout && isOverlayMaskPatch(patch)) {
      const target = findInTree(elements, elementId);
      const overlayKey = target && 'type' in target ? elementOverrideKey(target) : undefined;
      if (overlayKey !== undefined) {
        applySystemHidePatch(overlayKey, patch);
        return;
      }
    }
    // T2: element view-level (rodzeństwo `tabs` w `elements`) — aktualizuj bezpośrednio,
    // poza ścieżką sekcji/zakładek (nie żyje w żadnej zakładce).
    const viewLevelTarget = elements.find((el) => el.id === elementId && el.type !== 'tabs');
    if (viewLevelTarget) {
      setElements((prev) =>
        prev.map((el) =>
          el.id === elementId ? { ...el, settings: { ...(el.settings ?? {}), ...patch } } : el,
        ),
      );
      setDirty(true);
      return;
    }
    if (!selectedTab) return;
    // Blok „luźny" (extras) — zakładka-kontener 'block' albo klocek dodany do zakładki
    // non-fields. Aktualizuj po id w `tab.extras` (inline eye-toggle formatek: adres,
    // Kontakty itd.). Musi być PRZED gałęzią 'fields', bo blok żyje w extras, nie w sekcji.
    if ((selectedTab.extras ?? []).some((e) => e.id === elementId)) {
      updateTab(selectedTab.id, {
        extras: (selectedTab.extras ?? []).map((e) =>
          e.id === elementId ? { ...e, settings: { ...(e.settings ?? {}), ...patch } } : e,
        ),
      });
      return;
    }
    if (selectedTab.type !== 'fields') return;
    updateTab(selectedTab.id, {
      sections: (selectedTab.sections ?? []).map((s) => {
        const target = findInTree(s.elements, elementId);
        if (!target || !('type' in target)) return s;
        const currentSettings = (target as LayoutElement).settings ?? {};
        return {
          ...s,
          elements: updateInTree(s.elements, elementId, {
            settings: { ...currentSettings, ...patch },
          }),
        };
      }),
    });
  }

  /**
   * Układ systemowy: łatka ukryć (`hiddenColumnIds` / `hiddenFields`) trafia do KAŻDEGO elementu
   * drzewa o kluczu nakładki `overlayKey` (`applyOverlayPatchByKey`). Nakładka ma jeden wpis na
   * klucz, więc kopie tej samej tabeli dodatkowej (klocek w sekcji, cała zakładka) muszą nieść tę
   * samą maskę — inaczej zapis wziąłby maskę ostatniej kopii w drzewie.
   */
  function applySystemHidePatch(overlayKey: string, patch: Record<string, unknown>): void {
    setElements((prev) => applyOverlayPatchByKey(prev, overlayKey, patch));
    setDirty(true);
  }

  /**
   * Klucz nakładki tabeli albo widoku SQL CAŁEJ zakładki — z jej elementu w drzewie. Opiera się
   * na kolejności dzieci z adaptera (`buildChildrenFromLayoutTab`: element tabeli zakładki
   * `additional` / `satellite` albo widoku zakładki `additional_query` jest PIERWSZYM dzieckiem,
   * dalej ewentualne klocki dodatkowe). `elementOverrideKey` zwraca dla nich odpowiednio
   * `additional_table:<tableSlug>` i `additional_query:<viewSlug>`.
   */
  function tabTableOverrideKey(tabId: string): string | undefined {
    const tabItem = elements.find((el) => el.type === 'tabs')?.tabs?.find((t) => t.id === tabId);
    const tableElement = tabItem?.children[0];
    return tableElement ? elementOverrideKey(tableElement) : undefined;
  }

  // Ukrycie/odkrycie ZWYKŁEGO pola na layoucie SYSTEMOWYM (ikonka oka na klocku pola).
  // Odwraca flagę `hidden` na elemencie pola w drzewie — `deriveSystemOverrides` czyta ją
  // do nakładki per-tenant (`hiddenFieldIds`), a karta pomija pole z tą flagą. Pola żyją
  // w sekcjach zakładki 'fields' (mogą być zagnieżdżone) — ta sama maszyneria drzewa co
  // `updateElementSettings`. Na własnym layoucie ukrywanie robi się usuwając pole (delete),
  // więc ten toggle jest ścieżką WYŁĄCZNIE systemowego layoutu.
  function toggleFieldHidden(elementId: string) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    updateTab(selectedTab.id, {
      sections: (selectedTab.sections ?? []).map((s) => {
        const target = findInTree(s.elements, elementId);
        if (!target || !('type' in target)) return s;
        const current = (target as TreeLayoutElement).hidden === true;
        return {
          ...s,
          elements: updateInTree(s.elements, elementId, { hidden: !current }),
        };
      }),
    });
  }

  function addFieldToActiveSection(fieldId: string) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    const sections = selectedTab.sections ?? [];
    if (sections.length === 0) {
      const newSection: LayoutSection = {
        id: crypto.randomUUID(),
        title: null,
        columns: 2,
        elements: [{ id: crypto.randomUUID(), type: 'field', fieldId }],
      };
      updateTab(selectedTab.id, { sections: [newSection] });
      setSelectedSectionId(newSection.id);
      return;
    }
    const targetId = selectedSectionId ?? sections[0]!.id;
    updateTab(selectedTab.id, {
      sections: sections.map((s) =>
        s.id === targetId
          ? {
              ...s,
              elements: [...s.elements, { id: crypto.randomUUID(), type: 'field', fieldId }],
            }
          : s,
      ),
    });
  }

  /**
   * Krok B (2026-05-14) — dodaj element layoutu (heading/divider/alert/...)
   * do aktywnej sekcji. Settings z `LayoutElementRegistry.defaultSettings(type)`.
   * Wymaga: aktywna zakładka type='fields' (paleta Elementy widoczna tylko tam).
   */
  function addLayoutElementToCanvas(type: string) {
    // Typ „jeden na layout" (document_items_view / vat_register_view) już jest → nie dodawaj drugiego.
    if (rejectLayoutSingletonDuplicate(type)) return;
    // T2: elementy view-level (page-header/highlight) NIE idą do sekcji/extras —
    // lądują jako rodzeństwo PRZED `tabs`, nad paskiem zakładek.
    if (LayoutElementRegistry.get(type)?.viewLevel) {
      addViewLevelElement(type);
      return;
    }
    if (!selectedTab) return;
    const defaultSettings = LayoutElementRegistry.defaultSettings(type);
    const newEl: LayoutElement = {
      id: crypto.randomUUID(),
      type,
      settings: defaultSettings,
    };

    // F12.N E3.T1: akordeon dostaje 1 default panel (`defaultExpanded=true`, pusta zawartość).
    // `accordionItems[]` siedzi na poziomie LayoutElement (poza settings, 1:1 z SellCall +
    // backend `accordionItemSchema`). Per-panel CRUD obsługuje `AccordionInCanvas`.
    if (type === 'accordion') {
      newEl.accordionItems = [
        {
          id: crypto.randomUUID(),
          title: 'Sekcja 1',
          defaultExpanded: true,
          children: [],
        },
      ];
    }
    // Etap 3 T2 (2026-05-17): stepper/wizard dostają 1 default krok. Wspólny model.
    if (type === 'stepper' || type === 'wizard') {
      newEl.steps = [
        {
          id: crypto.randomUUID(),
          title: 'Krok 1',
          icon: 'Circle',
          children: [],
        },
      ];
    }

    // F12.N E2.T1: dla zakładek non-fields klocki idą do `extras`. Builder pokaże
    // je pod placeholderem zakładki w trybie edit (NonFieldsTabContent).
    if (selectedTab.type !== 'fields') {
      const extras = selectedTab.extras ?? [];
      updateTab(selectedTab.id, { extras: [...extras, newEl] });
      return;
    }

    const sections = selectedTab.sections ?? [];
    if (sections.length === 0) {
      const newSection: LayoutSection = {
        id: crypto.randomUUID(),
        title: null,
        columns: 2,
        elements: [newEl],
      };
      updateTab(selectedTab.id, { sections: [newSection] });
      setSelectedSectionId(newSection.id);
      return;
    }
    const targetId = selectedSectionId ?? sections[0]!.id;
    updateTab(selectedTab.id, {
      sections: sections.map((s) =>
        s.id === targetId ? { ...s, elements: [...s.elements, newEl] } : s,
      ),
    });
  }

  // ── T2: elementy VIEW-LEVEL (page-header/highlight nad paskiem zakładek) ──
  // Trzymane jako rodzeństwo PRZED elementem `tabs` w `elements`. Insert/remove
  // operują bezpośrednio na `elements` (poza adapterem tabs), bo to nie jest treść
  // zakładki. Round-trip zachowuje je (updateTabs + hydrate podają je do saveTabsToConfig).
  function addViewLevelElement(type: string) {
    const def = LayoutElementRegistry.get(type);
    const newEl: LayoutElement = {
      id: crypto.randomUUID(),
      type,
      settings: LayoutElementRegistry.defaultSettings(type),
    };
    let inserted = false;
    setElements((prev) => {
      // Blokada singletona (np. tylko jeden page-header na widok) — defensywnie,
      // niezależnie od wykluczenia w palecie.
      if (def?.viewLevelSingleton && prev.some((el) => el.type === type)) return prev;
      inserted = true;
      const tabsIdx = prev.findIndex((el) => el.type === 'tabs');
      if (tabsIdx < 0) return [newEl, ...prev];
      return [...prev.slice(0, tabsIdx), newEl, ...prev.slice(tabsIdx)];
    });
    if (inserted) {
      setSelectedElementId(newEl.id);
      setDirty(true);
    }
  }

  function removeViewLevelElement(elementId: string) {
    setElements((prev) => prev.filter((el) => el.id !== elementId));
    setSelectedElementId((cur) => (cur === elementId ? null : cur));
    setDirty(true);
  }

  // Usuwa DZIECKO kontenera view-level (np. pole/element upuszczony do wnętrza
  // `highlight`) — `removeFromTree` szuka rekurencyjnie w całym drzewie `elements`
  // (rodzeństwo `tabs` włącznie), więc działa niezależnie od tego, w którym
  // kontenerze view-level dziecko faktycznie siedzi. Parytet z `removeFieldFromSection`,
  // ale bez ograniczenia do `selectedTab.sections` (highlight żyje POZA zakładkami).
  function removeChildFromViewLevelElement(elementId: string) {
    setElements((prev) => removeFromTree(prev, elementId));
    setDirty(true);
  }

  // F12.N E3.T1: CRUD paneli wewnątrz akordeonu. `accordionItems[]` siedzi na
  // poziomie LayoutElement (poza `settings`, 1:1 z SellCall + backend schema).
  // Mutacje przez `updateInTree` na state tree-native.

  function addAccordionItem(accordionId: string) {
    setElements((prev) => {
      const accordion = findInTree(prev, accordionId) as LayoutElement | null;
      if (!accordion || accordion.type !== 'accordion') return prev;
      const current = accordion.accordionItems ?? [];
      const newPanel: AccordionItem = {
        id: crypto.randomUUID(),
        title: `Sekcja ${current.length + 1}`,
        children: [],
      };
      return updateInTree(prev, accordionId, { accordionItems: [...current, newPanel] });
    });
    setDirty(true);
  }

  function updateAccordionItemTitle(accordionId: string, panelId: string, newTitle: string) {
    setElements((prev) => {
      const accordion = findInTree(prev, accordionId) as LayoutElement | null;
      if (!accordion || accordion.type !== 'accordion') return prev;
      const items = (accordion.accordionItems ?? []).map((p) =>
        p.id === panelId ? { ...p, title: newTitle } : p,
      );
      return updateInTree(prev, accordionId, { accordionItems: items });
    });
    setDirty(true);
  }

  function removeAccordionItem(accordionId: string, panelId: string) {
    setElements((prev) => {
      const accordion = findInTree(prev, accordionId) as LayoutElement | null;
      if (!accordion || accordion.type !== 'accordion') return prev;
      const items = (accordion.accordionItems ?? []).filter((p) => p.id !== panelId);
      return updateInTree(prev, accordionId, { accordionItems: items });
    });
    setDirty(true);
  }

  // ── CRUD kroków steppera/wizarda (Etap 3 T2, 2026-05-17) ────────────────
  // Wzorzec analogiczny do akordeonu — operuje na `LayoutElement.steps` (zamiast
  // `accordionItems`). Wspólny dla `type='stepper'` i `type='wizard'` (decyzja UX
  // 2026-05-17: ten sam edytor, różny tylko runtime renderer).
  function addStep(stepperId: string) {
    setElements((prev) => {
      const stepper = findInTree(prev, stepperId) as LayoutElement | null;
      if (!stepper || (stepper.type !== 'stepper' && stepper.type !== 'wizard')) return prev;
      const current = stepper.steps ?? [];
      const newStep: Step = {
        id: crypto.randomUUID(),
        title: `Krok ${current.length + 1}`,
        icon: 'Circle',
        children: [],
      };
      return updateInTree(prev, stepperId, { steps: [...current, newStep] });
    });
    setDirty(true);
  }

  function updateStepTitle(stepperId: string, stepId: string, newTitle: string) {
    setElements((prev) => {
      const stepper = findInTree(prev, stepperId) as LayoutElement | null;
      if (!stepper || (stepper.type !== 'stepper' && stepper.type !== 'wizard')) return prev;
      const steps = (stepper.steps ?? []).map((s) =>
        s.id === stepId ? { ...s, title: newTitle } : s,
      );
      return updateInTree(prev, stepperId, { steps });
    });
    setDirty(true);
  }

  function updateStepIcon(stepperId: string, stepId: string, newIcon: string) {
    setElements((prev) => {
      const stepper = findInTree(prev, stepperId) as LayoutElement | null;
      if (!stepper || (stepper.type !== 'stepper' && stepper.type !== 'wizard')) return prev;
      const steps = (stepper.steps ?? []).map((s) =>
        s.id === stepId ? { ...s, icon: newIcon } : s,
      );
      return updateInTree(prev, stepperId, { steps });
    });
    setDirty(true);
  }

  function removeStep(stepperId: string, stepId: string) {
    setElements((prev) => {
      const stepper = findInTree(prev, stepperId) as LayoutElement | null;
      if (!stepper || (stepper.type !== 'stepper' && stepper.type !== 'wizard')) return prev;
      const steps = (stepper.steps ?? []).filter((s) => s.id !== stepId);
      return updateInTree(prev, stepperId, { steps });
    });
    setDirty(true);
  }

  /**
   * F12.N E3.T1 (Sub-tura 2c) — drop pola/elementu z palety do dowolnego kontenera
   * w drzewie. `containerId` może być:
   *  - id sekcji (`LayoutSection.id`, top-level w aktywnej zakładce)
   *  - id panelu akordeonu (`AccordionItem.id`)
   *  - id zakładki / kroku (przyszłość — stepper/wizard)
   *
   * Wzorzec SellCall: każdy container ma to samo data shape (`target='section'`)
   * w `useDroppable`, dispatcher znajduje go w drzewie przez `findInTree` i wstawia
   * dziecko przez `insertIntoContainer`. Nie używamy `selectedSectionId` — drop
   * trafia DOKŁADNIE tam gdzie kursor.
   */
  function addFromPaletteToContainer(
    kind: 'field' | 'layout-element',
    payload: string,
    containerId: string,
  ) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    // Drop z palety: typ „jeden na layout" już jest w layoucie → odrzuć (paleta go nie oferuje,
    // ale drag może wystartować ze starej selekcji / z innego widoku palety).
    if (kind === 'layout-element' && rejectLayoutSingletonDuplicate(payload)) return;
    setElements((prev) => {
      let newEl: LayoutElement;
      if (kind === 'field') {
        newEl = {
          id: crypto.randomUUID(),
          type: 'field',
          fieldId: payload,
        };
      } else {
        const defaultSettings = LayoutElementRegistry.defaultSettings(payload);
        newEl = {
          id: crypto.randomUUID(),
          type: payload,
          settings: defaultSettings,
        };
        if (payload === 'accordion') {
          newEl.accordionItems = [
            {
              id: crypto.randomUUID(),
              title: 'Sekcja 1',
              defaultExpanded: true,
              children: [],
            },
          ];
        }
      }
      return insertIntoContainer(prev, containerId, newEl);
    });
    setDirty(true);
  }

  // F12.N E2.T1: usuwanie klocka z `extras` zakładki (po kliknięciu „×" w UI).
  function removeExtraFromTab(tabId: string, elementId: string) {
    const tab = tabs.find((t) => t.id === tabId);
    if (!tab) return;
    const extras = (tab.extras ?? []).filter((el) => el.id !== elementId);
    updateTab(tabId, { extras });
  }

  function removeFieldFromSection(sectionId: string, elementId: string) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    // F12.N pakiet B Sub-tura 1 — tree-wide remove (removeFromTree) zamiast
    // płaskiego filter. Bez tego dzieci paneli akordeonu nie dało się usunąć
    // przez kosz w klocku pola (filter szukał tylko na poziomie s.elements).
    updateTab(selectedTab.id, {
      sections: (selectedTab.sections ?? []).map((s) =>
        s.id === sectionId ? { ...s, elements: removeFromTree(s.elements, elementId) } : s,
      ),
    });
  }

  // F12.N D3 — sygnatura czysta (activeId, overId); usePaletteDnD w FieldsTabContent
  // wywoła tę funkcję po rozpoznaniu drag sekcji w jednym top-level dispatcherze.
  function reorderSections(activeId: string, overId: string) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    const sections = selectedTab.sections ?? [];
    const oldIdx = sections.findIndex((s) => s.id === activeId);
    const newIdx = sections.findIndex((s) => s.id === overId);
    if (oldIdx < 0 || newIdx < 0) return;
    updateTab(selectedTab.id, { sections: arrayMove(sections, oldIdx, newIdx) });
  }

  /**
   * Pakiet B Sub-2/3 — sortowanie/przenoszenie elementów między kontenerami.
   *  - Same-container (`sourceContainerId === targetContainerId`): reorder w obrębie kontenera
   *    przez `arrayMove` (sekcja top-level) lub `reorderInContainer` (panel akordeonu w drzewie).
   *  - Cross-container (Sub-3, różne containery): wyciąga element z source, wstawia do target
   *    na pozycji `overId` (jeśli overId == targetContainerId → append na końcu pustego kontenera).
   *
   * `containerId` rozpoznawany jest jako sekcja top-level (LayoutSection.id w `selectedTab.sections`)
   * LUB panel akordeonu (`AccordionItem.id` zagnieżdżony w drzewie LayoutElement[]).
   *
   * Zastępuje wcześniejszy noop „elementy nie są sortable per element" (świadoma luka od F12.N D3,
   * naprawiona w pakiecie B Sub-2/3 2026-05-17).
   */
  function reorderElementsInContainer(
    sourceContainerId: string,
    activeId: string,
    targetContainerId: string,
    overId: string,
  ) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    const sections = selectedTab.sections ?? [];

    // SAME CONTAINER — Sub-2 path.
    if (sourceContainerId === targetContainerId) {
      updateTab(selectedTab.id, {
        sections: sections.map((s) => {
          // Sekcja top-level — bezpośredni arrayMove (s.elements jest poza drzewem LayoutElement).
          if (s.id === sourceContainerId) {
            const oldIdx = s.elements.findIndex((e) => e.id === activeId);
            const newIdx = s.elements.findIndex((e) => e.id === overId);
            if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return s;
            return { ...s, elements: arrayMove(s.elements, oldIdx, newIdx) };
          }
          // Panel akordeonu (zagnieżdżony) — przez pure helper reorderInContainer.
          return {
            ...s,
            elements: reorderInContainer(s.elements, sourceContainerId, activeId, overId),
          };
        }),
      });
      return;
    }

    // CROSS-CONTAINER — Sub-3 path.
    // 1. Wyciągnij active z source (sekcja LUB panel w drzewie).
    let extracted: TreeLayoutElement | null = null;
    const sectionsWithoutActive = sections.map((s) => {
      if (extracted) return s;
      if (s.id === sourceContainerId) {
        const idx = s.elements.findIndex((e) => e.id === activeId);
        if (idx >= 0) {
          extracted = s.elements[idx]!;
          return { ...s, elements: s.elements.filter((e) => e.id !== activeId) };
        }
      }
      const found = findInTree(s.elements, activeId);
      if (found && 'type' in found) {
        extracted = found as TreeLayoutElement;
        return { ...s, elements: removeFromTree(s.elements, activeId) };
      }
      return s;
    });
    if (!extracted) return;

    // 2. Wstaw extracted do target (sekcja top-level LUB panel w drzewie).
    const targetIsTopSection = sections.some((s) => s.id === targetContainerId);
    const updatedSections = sectionsWithoutActive.map((s) => {
      // Target = sekcja top-level.
      if (targetIsTopSection && s.id === targetContainerId) {
        // overId może być id elementu w sekcji LUB id samej sekcji (drop na puste).
        const targetIdx = s.elements.findIndex((e) => e.id === overId);
        const insertAt = targetIdx >= 0 ? targetIdx : s.elements.length;
        return {
          ...s,
          elements: [...s.elements.slice(0, insertAt), extracted!, ...s.elements.slice(insertAt)],
        };
      }
      // Target = panel akordeonu (zagnieżdżony) — sprawdź czy w tej sekcji.
      if (!targetIsTopSection) {
        const targetNode = findInTree(s.elements, targetContainerId);
        if (targetNode) {
          const targetChildren = (targetNode as { children?: TreeLayoutElement[] }).children ?? [];
          const childIdx = targetChildren.findIndex((c) => c.id === overId);
          const insertAt = childIdx >= 0 ? childIdx : undefined;
          return {
            ...s,
            elements: insertIntoContainer(s.elements, targetContainerId, extracted!, insertAt),
          };
        }
      }
      return s;
    });

    updateTab(selectedTab.id, { sections: updatedSections });
  }

  /**
   * Pakiet B Sub-2 — sortowanie paneli akordeonu w obrębie jednego akordeonu.
   */
  function reorderAccordionPanelsHandler(accordionId: string, activeId: string, overId: string) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    const sections = selectedTab.sections ?? [];
    updateTab(selectedTab.id, {
      sections: sections.map((s) => ({
        ...s,
        elements: reorderAccordionPanels(s.elements, accordionId, activeId, overId),
      })),
    });
  }

  /** Etap 3 T2 — sortowanie kroków steppera/wizarda w obrębie jednego steppera. */
  function reorderStepsHandler(stepperId: string, activeId: string, overId: string) {
    if (!selectedTab || selectedTab.type !== 'fields') return;
    const sections = selectedTab.sections ?? [];
    updateTab(selectedTab.id, {
      sections: sections.map((s) => ({
        ...s,
        elements: reorderSteps(s.elements, stepperId, activeId, overId),
      })),
    });
  }

  // F12.N T5.6b — globalny dirty (macierzysty || dowolny helper dirty).
  // Save button reaguje na obie zmiany. Liczone z `helperEdits` żeby zawsze
  // odpowiadać aktualnemu stanowi (bez cached count).
  const helperDirtyCount = useMemo(() => {
    let count = 0;
    for (const edit of helperEdits.values()) {
      if (edit.dirty) count++;
    }
    return count;
  }, [helperEdits]);
  const globalDirty = dirty || helperDirtyCount > 0;

  // Zgłaszaj realny stan „niezapisane zmiany" do rodzica (strażnik zamknięcia modala).
  // Tylko realne edycje ustawiają dirty=true; mount/odczyt = false (klik bez zmiany nie pyta).
  useEffect(() => {
    onDirtyChange?.(globalDirty);
  }, [globalDirty, onDirtyChange]);

  // ── Embedded mode — reset canvasu ────────────────────────────────────────
  // Dwa warianty: 'default' = re-hydratacja z initialLayoutConfig (jak useEffect mount),
  // 'blank' = czyszczenie canvasu do stanu pustego.
  // Gdy globalDirty — wyświetlamy ConfirmDialog (pendingReset state) przed wykonaniem.
  // setDirty(true) po resecie sygnalizuje niezapisane zmiany (user musi zapisać lub odrzucić).

  function executeReset(type: 'default' | 'blank') {
    if (type === 'default' && initialLayoutConfig) {
      const normalizedTabs = loadTabsFromConfig(initialLayoutConfig);
      const viewLevelSiblings = extractViewLevelElements(initialLayoutConfig);
      const nextElements = saveTabsToConfig(normalizedTabs, viewLevelSiblings).elements;
      setElements(nextElements);
      setSelectedTabId(normalizedTabs[0]?.id ?? null);
      setSelectedSectionId(normalizedTabs[0]?.sections?.[0]?.id ?? null);
      setHelperEdits(new Map());
      setDirty(true);
    } else if (type === 'blank') {
      setElements([]);
      setSelectedTabId(null);
      setSelectedSectionId(null);
      setHelperEdits(new Map());
      setDirty(true);
    }
  }

  function handleResetToDefault() {
    if (globalDirty) {
      setPendingReset('default');
    } else {
      executeReset('default');
    }
  }

  function handleResetCanvasBlank() {
    if (globalDirty) {
      setPendingReset('blank');
    } else {
      executeReset('blank');
    }
  }

  // ── F12.N T5.6b — operacje na scope helper-encji ─────────────────────────
  // Wszystkie callbacks mutują wpis w `helperEdits.get(activeHelperCollection)`
  // przez `updateHelperEdit`. Symetria z macierzystymi `updateTab/addSection/...`.

  function updateHelperEdit(collection: string, patch: (prev: HelperEditState) => HelperEditState) {
    setHelperEdits((prev) => {
      const current = prev.get(collection);
      if (!current) return prev;
      const next = new Map(prev);
      next.set(collection, patch(current));
      return next;
    });
  }

  function updateHelperVirtualTab(collection: string, tabPatch: Partial<LayoutTab>) {
    updateHelperEdit(collection, (prev) => {
      if (prev.tabs.length === 0) return prev;
      const [first, ...rest] = prev.tabs;
      return {
        ...prev,
        tabs: [{ ...first!, ...tabPatch }, ...rest],
        dirty: true,
      };
    });
  }

  function setHelperSelectedSection(collection: string, sectionId: string | null) {
    updateHelperEdit(collection, (prev) => ({ ...prev, selectedSectionId: sectionId }));
  }

  function addHelperSection() {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const newSection: LayoutSection = {
      id: crypto.randomUUID(),
      title: null,
      columns: 2,
      elements: [],
    };
    updateHelperVirtualTab(activeHelperCollection, {
      sections: [...(helperVirtualTab.sections ?? []), newSection],
    });
    setHelperSelectedSection(activeHelperCollection, newSection.id);
  }

  function removeHelperSection(sectionId: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    updateHelperVirtualTab(activeHelperCollection, {
      sections: (helperVirtualTab.sections ?? []).filter((s) => s.id !== sectionId),
    });
    if (activeHelperEdit?.selectedSectionId === sectionId) {
      setHelperSelectedSection(activeHelperCollection, null);
    }
  }

  function updateHelperSection(sectionId: string, patch: Partial<LayoutSection>) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    updateHelperVirtualTab(activeHelperCollection, {
      sections: (helperVirtualTab.sections ?? []).map((s) =>
        s.id === sectionId ? { ...s, ...patch } : s,
      ),
    });
  }

  function addFieldToActiveHelperSection(fieldId: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    if (sections.length === 0) {
      const newSection: LayoutSection = {
        id: crypto.randomUUID(),
        title: null,
        columns: 2,
        elements: [{ id: crypto.randomUUID(), type: 'field', fieldId }],
      };
      updateHelperVirtualTab(activeHelperCollection, { sections: [newSection] });
      setHelperSelectedSection(activeHelperCollection, newSection.id);
      return;
    }
    const targetId = activeHelperEdit?.selectedSectionId ?? sections[0]!.id;
    updateHelperVirtualTab(activeHelperCollection, {
      sections: sections.map((s) =>
        s.id === targetId
          ? {
              ...s,
              elements: [...s.elements, { id: crypto.randomUUID(), type: 'field', fieldId }],
            }
          : s,
      ),
    });
  }

  function addLayoutElementToHelperCanvas(type: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const defaultSettings = LayoutElementRegistry.defaultSettings(type);
    const newEl: LayoutElement = {
      id: crypto.randomUUID(),
      type,
      settings: defaultSettings,
    };
    // Pakiet B Sub-5 — akordeon w canvasie helper-encji (parytet z macierzystym builderem).
    if (type === 'accordion') {
      newEl.accordionItems = [
        {
          id: crypto.randomUUID(),
          title: 'Sekcja 1',
          defaultExpanded: true,
          children: [],
        },
      ];
    }
    // Etap 3 T2 — stepper/wizard w canvasie helper-encji (parytet z macierzystym).
    if (type === 'stepper' || type === 'wizard') {
      newEl.steps = [
        {
          id: crypto.randomUUID(),
          title: 'Krok 1',
          icon: 'Circle',
          children: [],
        },
      ];
    }
    const sections = helperVirtualTab.sections ?? [];
    if (sections.length === 0) {
      const newSection: LayoutSection = {
        id: crypto.randomUUID(),
        title: null,
        columns: 2,
        elements: [newEl],
      };
      updateHelperVirtualTab(activeHelperCollection, { sections: [newSection] });
      setHelperSelectedSection(activeHelperCollection, newSection.id);
      return;
    }
    const targetId = activeHelperEdit?.selectedSectionId ?? sections[0]!.id;
    updateHelperVirtualTab(activeHelperCollection, {
      sections: sections.map((s) =>
        s.id === targetId ? { ...s, elements: [...s.elements, newEl] } : s,
      ),
    });
  }

  function removeFieldFromHelperSection(sectionId: string, elementId: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    // Pakiet B Sub-1 (parytet z macierzystym): tree-wide remove zamiast płaskiego filter,
    // żeby dzieci paneli akordeonu w helper-encji też dało się usunąć przez kosz.
    updateHelperVirtualTab(activeHelperCollection, {
      sections: (helperVirtualTab.sections ?? []).map((s) =>
        s.id === sectionId ? { ...s, elements: removeFromTree(s.elements, elementId) } : s,
      ),
    });
  }

  /**
   * Pakiet B Sub-5 — CRUD paneli akordeonu w canvasie helper-encji (parytet
   * z macierzystym `addAccordionItem` / `updateAccordionItemTitle` / `removeAccordionItem`,
   * tylko operuje na `helperVirtualTab.sections` zamiast top-level `elements`).
   */
  function addAccordionItemHelper(accordionId: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    const newSections = sections.map((s) => {
      const found = findInTree(s.elements, accordionId);
      if (found && 'type' in found && (found as LayoutElement).type === 'accordion') {
        const current = ((found as LayoutElement).accordionItems ?? []) as AccordionItem[];
        const newPanel: AccordionItem = {
          id: crypto.randomUUID(),
          title: `Sekcja ${current.length + 1}`,
          children: [],
        };
        return {
          ...s,
          elements: updateInTree(s.elements, accordionId, {
            accordionItems: [...current, newPanel],
          }),
        };
      }
      return s;
    });
    updateHelperVirtualTab(activeHelperCollection, { sections: newSections });
  }

  function updateAccordionItemTitleHelper(accordionId: string, panelId: string, newTitle: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    const newSections = sections.map((s) => {
      const found = findInTree(s.elements, accordionId);
      if (found && 'type' in found && (found as LayoutElement).type === 'accordion') {
        const items = (((found as LayoutElement).accordionItems ?? []) as AccordionItem[]).map(
          (p) => (p.id === panelId ? { ...p, title: newTitle } : p),
        );
        return {
          ...s,
          elements: updateInTree(s.elements, accordionId, { accordionItems: items }),
        };
      }
      return s;
    });
    updateHelperVirtualTab(activeHelperCollection, { sections: newSections });
  }

  function removeAccordionItemHelper(accordionId: string, panelId: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    const newSections = sections.map((s) => {
      const found = findInTree(s.elements, accordionId);
      if (found && 'type' in found && (found as LayoutElement).type === 'accordion') {
        const items = (((found as LayoutElement).accordionItems ?? []) as AccordionItem[]).filter(
          (p) => p.id !== panelId,
        );
        return {
          ...s,
          elements: updateInTree(s.elements, accordionId, { accordionItems: items }),
        };
      }
      return s;
    });
    updateHelperVirtualTab(activeHelperCollection, { sections: newSections });
  }

  /**
   * Etap 3 T2 — CRUD kroków steppera/wizarda w helper-encji (parytet z macierzystym
   * `addStep` / `updateStepTitle` / `removeStep`, ale operuje na `helperVirtualTab.sections`).
   */
  function addStepHelper(stepperId: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    const newSections = sections.map((s) => {
      const found = findInTree(s.elements, stepperId);
      const isStepper =
        found &&
        'type' in found &&
        ((found as LayoutElement).type === 'stepper' || (found as LayoutElement).type === 'wizard');
      if (isStepper) {
        const current = ((found as LayoutElement).steps ?? []) as Step[];
        const newStep: Step = {
          id: crypto.randomUUID(),
          title: `Krok ${current.length + 1}`,
          icon: 'Circle',
          children: [],
        };
        return {
          ...s,
          elements: updateInTree(s.elements, stepperId, { steps: [...current, newStep] }),
        };
      }
      return s;
    });
    updateHelperVirtualTab(activeHelperCollection, { sections: newSections });
  }

  function updateStepTitleHelper(stepperId: string, stepId: string, newTitle: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    const newSections = sections.map((s) => {
      const found = findInTree(s.elements, stepperId);
      const isStepper =
        found &&
        'type' in found &&
        ((found as LayoutElement).type === 'stepper' || (found as LayoutElement).type === 'wizard');
      if (isStepper) {
        const steps = (((found as LayoutElement).steps ?? []) as Step[]).map((st) =>
          st.id === stepId ? { ...st, title: newTitle } : st,
        );
        return { ...s, elements: updateInTree(s.elements, stepperId, { steps }) };
      }
      return s;
    });
    updateHelperVirtualTab(activeHelperCollection, { sections: newSections });
  }

  function updateStepIconHelper(stepperId: string, stepId: string, newIcon: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    const newSections = sections.map((s) => {
      const found = findInTree(s.elements, stepperId);
      const isStepper =
        found &&
        'type' in found &&
        ((found as LayoutElement).type === 'stepper' || (found as LayoutElement).type === 'wizard');
      if (isStepper) {
        const steps = (((found as LayoutElement).steps ?? []) as Step[]).map((st) =>
          st.id === stepId ? { ...st, icon: newIcon } : st,
        );
        return { ...s, elements: updateInTree(s.elements, stepperId, { steps }) };
      }
      return s;
    });
    updateHelperVirtualTab(activeHelperCollection, { sections: newSections });
  }

  function removeStepHelper(stepperId: string, stepId: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    const newSections = sections.map((s) => {
      const found = findInTree(s.elements, stepperId);
      const isStepper =
        found &&
        'type' in found &&
        ((found as LayoutElement).type === 'stepper' || (found as LayoutElement).type === 'wizard');
      if (isStepper) {
        const steps = (((found as LayoutElement).steps ?? []) as Step[]).filter(
          (st) => st.id !== stepId,
        );
        return { ...s, elements: updateInTree(s.elements, stepperId, { steps }) };
      }
      return s;
    });
    updateHelperVirtualTab(activeHelperCollection, { sections: newSections });
  }

  function reorderHelperStepsHandler(stepperId: string, activeId: string, overId: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    updateHelperVirtualTab(activeHelperCollection, {
      sections: sections.map((s) => ({
        ...s,
        elements: reorderSteps(s.elements, stepperId, activeId, overId),
      })),
    });
  }

  /**
   * Pakiet B Sub-2/3/5 — reorder elementów w helper-encji (parytet z macierzystym
   * `reorderElementsInContainer`). Wspiera same-container i cross-container.
   */
  function reorderHelperElementsInContainer(
    sourceContainerId: string,
    activeId: string,
    targetContainerId: string,
    overId: string,
  ) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];

    if (sourceContainerId === targetContainerId) {
      updateHelperVirtualTab(activeHelperCollection, {
        sections: sections.map((s) => {
          if (s.id === sourceContainerId) {
            const oldIdx = s.elements.findIndex((e) => e.id === activeId);
            const newIdx = s.elements.findIndex((e) => e.id === overId);
            if (oldIdx < 0 || newIdx < 0 || oldIdx === newIdx) return s;
            return { ...s, elements: arrayMove(s.elements, oldIdx, newIdx) };
          }
          return {
            ...s,
            elements: reorderInContainer(s.elements, sourceContainerId, activeId, overId),
          };
        }),
      });
      return;
    }

    let extracted: LayoutElement | null = null;
    const sectionsWithoutActive = sections.map((s) => {
      if (extracted) return s;
      if (s.id === sourceContainerId) {
        const idx = s.elements.findIndex((e) => e.id === activeId);
        if (idx >= 0) {
          extracted = s.elements[idx]!;
          return { ...s, elements: s.elements.filter((e) => e.id !== activeId) };
        }
      }
      const found = findInTree(s.elements, activeId);
      if (found && 'type' in found) {
        extracted = found as LayoutElement;
        return { ...s, elements: removeFromTree(s.elements, activeId) };
      }
      return s;
    });
    if (!extracted) return;

    const targetIsTopSection = sections.some((s) => s.id === targetContainerId);
    const updatedSections = sectionsWithoutActive.map((s) => {
      if (targetIsTopSection && s.id === targetContainerId) {
        const targetIdx = s.elements.findIndex((e) => e.id === overId);
        const insertAt = targetIdx >= 0 ? targetIdx : s.elements.length;
        return {
          ...s,
          elements: [...s.elements.slice(0, insertAt), extracted!, ...s.elements.slice(insertAt)],
        };
      }
      if (!targetIsTopSection) {
        const targetNode = findInTree(s.elements, targetContainerId);
        if (targetNode) {
          const targetChildren = (targetNode as { children?: LayoutElement[] }).children ?? [];
          const childIdx = targetChildren.findIndex((c) => c.id === overId);
          const insertAt = childIdx >= 0 ? childIdx : undefined;
          return {
            ...s,
            elements: insertIntoContainer(s.elements, targetContainerId, extracted!, insertAt),
          };
        }
      }
      return s;
    });

    updateHelperVirtualTab(activeHelperCollection, { sections: updatedSections });
  }

  function reorderHelperAccordionPanelsHandler(
    accordionId: string,
    activeId: string,
    overId: string,
  ) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    updateHelperVirtualTab(activeHelperCollection, {
      sections: sections.map((s) => ({
        ...s,
        elements: reorderAccordionPanels(s.elements, accordionId, activeId, overId),
      })),
    });
  }

  function reorderHelperSections(activeId: string, overId: string) {
    if (!activeHelperCollection || !helperVirtualTab) return;
    const sections = helperVirtualTab.sections ?? [];
    const oldIdx = sections.findIndex((s) => s.id === activeId);
    const newIdx = sections.findIndex((s) => s.id === overId);
    if (oldIdx < 0 || newIdx < 0) return;
    updateHelperVirtualTab(activeHelperCollection, {
      sections: arrayMove(sections, oldIdx, newIdx),
    });
  }

  // ── Layout switcher options
  const switcherOptions: LayoutOption[] = useMemo(() => {
    // Tryb embedded (kreator przy pozycji menu / detal encji): zapytanie o zapisane
    // layouty jest wyłączone, więc lista bywa pusta i switcher pokazywał „Brak layoutów"
    // — mimo że treść canvasu jest pełnym domyślnym układem. Wstrzykujemy wirtualną
    // pozycję „Domyślny" reprezentującą ten układ, żeby od razu coś było w dropdownie.
    if (isAdminEmbedded && layouts.length === 0) {
      return [
        {
          id: EMBEDDED_DEFAULT_LAYOUT_ID,
          name: t('tenant.systemModuleBuilder.defaultLayoutName'),
          categoryId: null,
          isDefault: true,
          previewDockEnabled: false,
          layoutType: 'form',
          roleIds: [],
        },
      ];
    }
    return layouts.map((l) => ({
      id: l.id,
      name: l.name,
      categoryId: l.categoryId,
      isDefault: l.isDefault,
      previewDockEnabled: l.previewDockEnabled,
      layoutType: l.layoutType,
      roleIds: l.roleIds,
      sourceViewSlug: l.sourceViewSlug,
      menuSourceModuleId: l.menuSourceModuleId ?? null,
    }));
  }, [layouts, isAdminEmbedded, t]);

  function applyLayoutSwitch(layoutId: string) {
    const next = new URLSearchParams(params);
    next.set('layoutId', layoutId);
    setParams(next, { replace: true });
  }

  function handleSelectLayout(layoutId: string) {
    // Embedded: jedyna pozycja to wirtualny „Domyślny" — nie ma między czym przełączać,
    // a wybór nie może zmieniać ?layoutId (id wirtualne nie istnieje w bazie).
    if (isAdminEmbedded) return;
    // F12.N T5.6b — globalDirty obejmuje macierzysty + helper-encje. Gdy są
    // niezapisane zmiany, przełączenie idzie przez ConfirmDialog (§4.12).
    if (globalDirty) {
      setPendingLayoutSwitch(layoutId);
      return;
    }
    applyLayoutSwitch(layoutId);
  }

  // Eksport layoutu formularza do pliku — działa też dla layoutu systemowego/centralnego.
  async function handleExportLayout(layout: LayoutOption) {
    try {
      await exportFormLayout(`/system-modules/${slug}/layouts`, layout.id);
      toast.show({
        variant: 'success',
        title: t('tenant.formTransfer.exported', { name: layout.name }),
      });
    } catch {
      toast.show({ variant: 'error', title: t('tenant.formTransfer.exportError') });
    }
  }

  // Po imporcie: odśwież listę layoutów i przełącz kreator na nowy (z pytaniem przy
  // niezapisanych zmianach — ta sama ścieżka co ręczny wybór z listy).
  async function handleFormImported(layoutId: string) {
    await queryClient.refetchQueries({ queryKey: ['system-module', slug] });
    handleSelectLayout(layoutId);
  }

  function openCreateLayoutModal() {
    setLayoutFormMode('create');
    setLayoutFormInitial({});
    setLayoutFormOpen(true);
  }

  function openEditLayoutModal(layout: LayoutOption) {
    setLayoutFormMode('edit');
    setLayoutFormInitial({
      name: layout.name,
      categoryId: layout.categoryId,
      isDefault: layout.isDefault,
      roleIds: layout.roleIds ?? [],
      previewDockEnabled: layout.previewDockEnabled ?? false,
    });
    setLayoutFormOpen(true);
  }

  function handleLayoutFormSubmit(values: LayoutFormValues) {
    if (layoutFormMode === 'create') {
      createLayoutMutation.mutate(values);
    } else if (activeLayout) {
      updateLayoutMetaMutation.mutate({ layoutId: activeLayout.id, values });
    }
  }

  // ── Render
  if (!slug) {
    return (
      <main className="p-6 text-error-500">{t('tenant.systemModuleSettings.missingSlug')}</main>
    );
  }

  // isAdminEmbedded: detailQuery wyłączone — pomijamy loading/error guards dla tego trybu.
  // Tenant-scope (SystemModuleSettingsPage): nadal czeka na fetch.
  if (!isAdminEmbedded && detailQuery.isLoading) {
    return (
      <main className="flex min-h-full items-center justify-center bg-bg-secondary p-6">
        <Spinner />
      </main>
    );
  }

  if (!isAdminEmbedded && (detailQuery.isError || !detailQuery.data)) {
    return (
      <main className="p-6">
        <p className="text-error-500">{t('tenant.systemModuleSettings.loadError')}</p>
      </main>
    );
  }

  // isAdminEmbedded: brak danych z detailQuery — syntetyczne puste wartości zastępują.
  const mod = detailQuery.data?.module ?? {
    slug: slug ?? '',
    name: slug ?? '',
    icon: '',
    color: '',
    isHelperEntity: false,
    entityTableName: '',
  };
  const rawCategories = detailQuery.data?.categories ?? [];
  const categories = rawCategories.map((c) => ({
    id: c.id,
    name: c.name,
    color: c.color ?? '#6B7280',
  }));

  // Pasek kategorii-LAYOUTU (`system_module_categories`, field_overrides) i skrót do słownika grup
  // USUNIĘTE z buildera kartotek systemowych (decyzja właścicielki 2026-08-18). Filtrem listy jest
  // POLE `groups` (wielokrotny wybór ze słownika, stawiane na canvasie jak każde inne pole).
  // UWAGA: dotyczy WYŁĄCZNIE kategorii-layoutu. Osobna oś kategorii-TYPU (`record_categories`, jedna
  // na rekord, NIE steruje layoutem) wróciła na kartoteki systemowe jako przenośny klocek (ADR-0093b,
  // pilot kontrahenci) — patrz `RecordCategoryPicker` / `RecordCategoriesTab`.

  const builderMain = (
    <main
      className={
        isAdminEmbedded
          ? 'flex flex-col bg-bg-secondary'
          : embeddedMode
            ? 'flex flex-col bg-bg-secondary -m-6'
            : 'flex min-h-full flex-col bg-bg-secondary'
      }
      data-testid="system-module-builder-page"
    >
      {/* Header — pełny dla standalone page (back + title + switcher + save).
          Embedded mode: kompaktowy mini-header (tylko LayoutSwitcher + Save) — back/title
          ma settings hub. */}
      {embeddedMode ? (
        <header
          className="flex items-center justify-end gap-3 border-b border-border-primary bg-bg-primary px-6 py-2"
          data-testid="system-module-builder-mini-header"
        >
          {/* Slot przełącznika layoutu (np. dropdown nazwanych layoutów z
              MenuItemFormBuilderModal) — gdy podany, zastępuje wbudowany LayoutSwitcher
              i wbudowane przyciski resetu (slot przejmuje wybór + reset), bez dublowania. */}
          {embeddedLayoutSwitcher ?? (
            <>
              {!hideEmbeddedLayoutSwitcher && (
                <LayoutSwitcher
                  layouts={switcherOptions}
                  currentLayoutId={
                    activeLayout?.id ??
                    (isAdminEmbedded && layouts.length === 0 ? EMBEDDED_DEFAULT_LAYOUT_ID : null)
                  }
                  categories={categories}
                  roleTemplates={rolesQuery.data}
                  onSelect={handleSelectLayout}
                  onCreate={isAdminEmbedded ? undefined : openCreateLayoutModal}
                  onEdit={isAdminEmbedded ? undefined : openEditLayoutModal}
                  onDuplicate={
                    isAdminEmbedded ? undefined : (l) => duplicateLayoutMutation.mutate(l.id)
                  }
                  onDelete={isAdminEmbedded ? undefined : (l) => setPendingDeleteLayout(l)}
                  onSetDefault={
                    isAdminEmbedded ? undefined : (l) => setLayoutDefaultMutation.mutate(l.id)
                  }
                  onTogglePreviewDock={
                    isAdminEmbedded
                      ? undefined
                      : (l) =>
                          setLayoutPreviewDockMutation.mutate({
                            layoutId: l.id,
                            next: !l.previewDockEnabled,
                          })
                  }
                  onExport={isAdminEmbedded ? undefined : (l) => void handleExportLayout(l)}
                  onImport={isAdminEmbedded ? undefined : () => setFormImportOpen(true)}
                />
              )}
              {isAdminEmbedded && !readOnlyMode && (
                <>
                  <Button
                    variant="secondary"
                    onClick={handleResetToDefault}
                    data-testid="system-module-builder-reset-to-default"
                  >
                    {t('tenant.systemModuleBuilder.resetToDefault')}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleResetCanvasBlank}
                    data-testid="system-module-builder-reset-blank"
                  >
                    {t('tenant.systemModuleBuilder.startBlank')}
                  </Button>
                </>
              )}
            </>
          )}
          {!readOnlyMode && (
            <Button
              variant="primary"
              leftIcon={<Save className="h-4 w-4" />}
              onClick={() => saveMutation.mutate()}
              disabled={
                !globalDirty || (!isAdminEmbedded && !activeLayout) || saveMutation.isPending
              }
              loading={saveMutation.isPending}
              data-testid="system-module-builder-save"
            >
              {t('tenant.systemModuleBuilder.save')}
            </Button>
          )}
        </header>
      ) : (
        <header className="flex items-center gap-3 border-b border-border-primary bg-bg-primary px-6 py-3">
          <button
            type="button"
            onClick={() => {
              // F12.N Etap 4 E4.T5: guard niezapisanych zmian.
              if (dirty) {
                setPendingBackNavigation(true);
              } else {
                navigate(`/system-modules/${slug}/settings?tab=layout`);
              }
            }}
            aria-label={t('common.back')}
            className="rounded-md p-1 text-text-muted hover:bg-bg-tertiary"
            data-testid="system-module-builder-back-btn"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold text-text-primary">
              {t('tenant.systemModuleBuilder.title', { name: mod.name })}
            </h1>
          </div>

          <LayoutSwitcher
            layouts={switcherOptions}
            currentLayoutId={activeLayout?.id ?? null}
            categories={categories}
            roleTemplates={rolesQuery.data}
            onSelect={handleSelectLayout}
            onCreate={openCreateLayoutModal}
            onEdit={openEditLayoutModal}
            onDuplicate={(l) => duplicateLayoutMutation.mutate(l.id)}
            onDelete={(l) => setPendingDeleteLayout(l)}
            onSetDefault={(l) => setLayoutDefaultMutation.mutate(l.id)}
            onTogglePreviewDock={(l) =>
              setLayoutPreviewDockMutation.mutate({
                layoutId: l.id,
                next: !l.previewDockEnabled,
              })
            }
            onExport={(l) => void handleExportLayout(l)}
            onImport={() => setFormImportOpen(true)}
          />

          <Button
            variant="primary"
            leftIcon={<Save className="h-4 w-4" />}
            onClick={() => saveMutation.mutate()}
            disabled={!globalDirty || !activeLayout || saveMutation.isPending}
            loading={saveMutation.isPending}
            data-testid="system-module-builder-save"
          >
            {t('tenant.systemModuleBuilder.save')}
          </Button>
        </header>
      )}

      {/* #6 — informacja dla tenanta: layout systemowy jest tylko-do-odczytu; edycja
          przez „Duplikuj" (dropdown layoutów w nagłówku, poza zablokowanym obszarem). */}
      {isSystemManagedLayout && (
        <div
          className="flex items-center gap-2 border-b border-amber-200 bg-amber-50 px-6 py-2 text-sm text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-300"
          data-testid="system-module-builder-system-layout-notice"
        >
          <Lock className="h-4 w-4 shrink-0" aria-hidden={true} />
          <span>{t('tenant.systemModuleBuilder.systemLayoutLocked')}</span>
        </div>
      )}

      {/* E2.T2a — pasek zakładek przeniesiony z meta-nawigacji do WNĘTRZA canvasu
          (jak w SellCall). Render via TabsBarBlock per scenariusz:
           - fields → przekazany jako tabsBarSlot do FieldsTabContent (góra canvasu)
           - non-fields → inline w środkowej kolumnie
           - brak selectedTab → standalone (empty state lub wybór pierwszej zakładki) */}
      {(() => {
        // Etap 2 kartoteki: helper sprawdzający czy dana zakładka jest read-only.
        // Zakładka jest read-only gdy:
        //  (a) jej `type` należy do `readOnlyTabTypes`, LUB
        //  (b) jej `collection` należy do `readOnlyTabCollections`
        //      (np. 'contractor_company_access' → zakładka „Dostępy firm").
        const isTabReadOnly = (tab: LayoutTab): boolean => {
          if (readOnlyTabTypes && readOnlyTabTypes.includes(tab.type)) return true;
          if (
            readOnlyTabCollections &&
            tab.collection &&
            readOnlyTabCollections.includes(tab.collection)
          )
            return true;
          return false;
        };

        // Zakładka read-only = blokujemy drag/add/delete/edit/rename/toggle hidden.
        // onAddTab i onDeleteTabRequest biorą tabId lub void — przekazujemy no-op gdy read-only.
        // T2: slot view-level (page-header/highlight) renderowany NAD paskiem zakładek.
        // Wpięty w `tabsBarBlock`, więc pokazuje się dla KAŻDEGO typu zakładki (fields i inne).
        // Wzorzec SellCall: „Wyróżnienie" NIE jest klockiem z palety (highlight ma
        // `hiddenFromPalette`) — zamiast tego, gdy żaden highlight jeszcze nie istnieje,
        // pokazujemy stały slot z przerywaną ramką „+ Dodaj wyróżnienie" tuż nad zakładkami.
        // Renderer dziecka kontenera view-level (highlight) — parytet z
        // `renderContainerChild` w `SortableSectionView`, adaptowany do usuwania przez
        // `removeChildFromViewLevelElement` (drzewo `elements`, nie `selectedTab.sections`).
        const renderViewLevelChild = (
          child: TreeLayoutElement,
          containerId: string,
        ): React.ReactNode => {
          if (child.type === 'field') {
            const f = child.fieldId ? fieldById.get(child.fieldId) : null;
            if (!f) {
              return (
                <div className="rounded border border-warning-300 bg-warning-50 px-3 py-2 text-xs text-warning-700">
                  {t('tenant.systemModuleBuilder.unknownField')}
                </div>
              );
            }
            return (
              <FieldBlock
                elementId={child.id}
                field={f}
                onRemove={removeChildFromViewLevelElement}
                sortableContainerId={containerId}
                systemHideOnly={isSystemManagedLayout}
                hidden={child.hidden === true}
                onToggleHidden={toggleFieldHidden}
              />
            );
          }
          const childPreview = tryRenderLayoutElement({
            element: {
              id: child.id,
              type: child.type,
              settings: (child.settings ?? {}) as Record<string, unknown>,
            },
            children: [],
            mode: 'builder',
            systemHideOnly: isSystemManagedLayout,
            // Podgląd (readOnlyMode) nie zapisuje: bez callbacku kontrolki inline klocka nie mutują stanu.
            onSettingsChange: readOnlyMode
              ? undefined
              : (patch) => updateElementSettings(child.id, patch),
          }) ?? (
            <p className="text-xs text-text-tertiary">
              {t('tenant.systemModuleBuilder.unknownElement', { type: child.type })}
            </p>
          );
          return (
            <LayoutElementBlock
              elementId={child.id}
              elementType={child.type}
              preview={childPreview}
              onRemove={removeChildFromViewLevelElement}
              sortableContainerId={containerId}
            />
          );
        };

        const hasHighlightElement = viewLevelElements.some((el) => el.type === 'highlight');
        const showAddHighlight = !hasHighlightElement && !readOnlyMode;
        // Wzorzec SellCall (ModuleBuilderPage.tsx: „Highlight zone - always above tabs") —
        // ikona Zap, tekst z „+" wbudowanym. Kolorystyka neutralna (szara), spójna z resztą
        // placeholderów „+ Dodaj …" w builderze — bursztyn/warning wyglądał zbyt agresywnie.
        const addHighlightButton = showAddHighlight ? (
          <Button
            variant="ghost"
            onClick={() => addViewLevelElement('highlight')}
            leftIcon={<Zap className="h-3.5 w-3.5" />}
            className="mb-3 w-full justify-center rounded-lg border-2 border-dashed border-border-primary py-2.5 text-xs font-medium text-text-muted hover:border-accent-500 hover:bg-transparent hover:text-accent-600"
            data-testid="system-module-builder-add-highlight"
          >
            {t('tenant.systemModuleBuilder.viewLevel.addHighlight')}
          </Button>
        ) : null;
        const viewLevelSlot =
          viewLevelElements.length > 0 ? (
            <>
              <ViewLevelBar
                elements={viewLevelElements}
                selectedElementId={selectedElementId}
                onSelectElement={readOnlyMode ? undefined : setSelectedElementId}
                onRemoveElement={readOnlyMode ? undefined : removeViewLevelElement}
                renderChild={renderViewLevelChild}
              />
              {addHighlightButton}
            </>
          ) : (
            addHighlightButton
          );

        const tabsBarBlock = (
          <>
            {viewLevelSlot}
            {/* W podglądzie (readOnlyMode) ORAZ na layoucie systemowym (#6) wrapper ciała
                buildera ma pointer-events:none. Pasek zakładek MUSI zostać klikalny
                (nawigacja), więc odblokowujemy tylko jego — `viewLevelSlot` (kontrolki
                elementów widoku = edycja) zostaje zablokowany pod pointer-events:none rodzica. */}
            <div
              style={readOnlyMode || isSystemManagedLayout ? { pointerEvents: 'auto' } : undefined}
            >
              <TabsBarBlock
                tabs={tabs}
                selectedTabId={selectedTabId}
                sensors={sensors}
                onTabsDragEnd={onTabsDragEnd}
                onSelectTab={(tabId) => {
                  setSelectedTabId(tabId);
                  const tab = tabs.find((tt) => tt.id === tabId);
                  setSelectedSectionId(tab?.sections?.[0]?.id ?? null);
                }}
                onToggleTabHidden={(tabId) => {
                  const tab = tabs.find((t) => t.id === tabId);
                  if (tab && !isTabReadOnly(tab)) toggleTabHidden(tabId);
                }}
                onRenameTab={(tabId, title) => {
                  const tab = tabs.find((t) => t.id === tabId);
                  // S7: opublikowanej zakładki nie wolno przemianować (znacznik publishedAt).
                  // #6: layout systemowy = zero zmiany nazwy.
                  if (tab && !isSystemManagedLayout && !isTabReadOnly(tab) && !tab.publishedAt)
                    updateTab(tabId, { title });
                }}
                // S7: „+ Dodaj zakładkę" odblokowane przez allowTabManagement nawet gdy
                // treść zakładek systemowych jest read-only (readOnlyTabTypes).
                // #6: na layoucie systemowym dodawanie zakładek zablokowane.
                onAddTab={
                  !isSystemManagedLayout && (allowTabManagement || !readOnlyTabTypes)
                    ? openAddTabModal
                    : undefined
                }
                onEditTabRequest={(tabId) => {
                  const tab = tabs.find((t) => t.id === tabId);
                  if (tab && !isSystemManagedLayout && !isTabReadOnly(tab) && !tab.publishedAt)
                    openEditTabModal(tab);
                }}
                onDeleteTabRequest={(tabId) => {
                  const tab = tabs.find((t) => t.id === tabId);
                  // S7: opublikowanej zakładki nie wolno usunąć (backend dubluje bramą 409).
                  // W trybie admin-embedded (projektowanie layoutu) usunięcie = pominięcie
                  // zakładki w tym layoucie — bypass read-only (struktura kartoteki nietknięta).
                  // #6: layout systemowy = zero usuwania.
                  if (
                    tab &&
                    !isSystemManagedLayout &&
                    !tab.publishedAt &&
                    (isAdminEmbedded || !isTabReadOnly(tab))
                  )
                    setPendingDeleteTabId(tabId);
                }}
                // #6: na layoucie NIESYSTEMOWYM (kopia/nowy) KAŻDA zakładka jest usuwalna —
                // także odziedziczona z Default (systemowy slug). Na systemowym: kosz ukryty
                // (structureLocked). Admin-embedded (projektowanie szablonu) jak dotąd.
                allowDeleteAny={
                  isAdminEmbedded || (!!activeLayout && !activeLayout.isSystemManaged)
                }
                structureLocked={isSystemManagedLayout}
                readOnly={readOnlyMode}
              />
            </div>
          </>
        );

        // F12.N T5.6b — czy aktywna zakładka subcollection wskazuje na helper-encję
        // której scope został już zainicjalizowany przez fetch + useEffect?
        // Jeśli tak — canvas renderuje sekcje pól helper-encji inline (jak macierzysty
        // FieldsTabContent), a paleta po lewej operuje na polach helper-encji.
        const isHelperInline = !!(activeHelperEdit && helperVirtualTab && activeHelperCollection);
        // Loading state: helper-collection rozpoznana, ale fetch nadal trwa.
        const isHelperLoading =
          selectedTab?.type === 'subcollection' &&
          !!selectedTab.collection &&
          helperDetailQuery.isFetching &&
          !activeHelperEdit;
        // Cała zakładka z tabelą dodatkową (typ `additional`; `satellite` = starszy zapis tego samego).
        const isAdditionalTableTab =
          selectedTab?.type === 'satellite' || selectedTab?.type === 'additional';
        // Cała zakładka z widokiem SQL (typ `additional_query`).
        const isAdditionalQueryTab = selectedTab?.type === 'additional_query';

        return (
          <div
            className="flex flex-1 overflow-hidden"
            {...(readOnlyMode || isSystemManagedLayout
              ? {
                  // Podgląd (readOnlyMode) ORAZ layout systemowy (#6): blokujemy edycję palety
                  // i canvasu myszą (pointer-events) + zaznaczanie tekstu. NIE używamy tu `inert`
                  // — obejmowałby też pasek zakładek (jest dzieckiem tego wrappera), a `inert`
                  // przodka nie da się nadpisać u dziecka, więc zablokowałby nawigację między
                  // zakładkami. Pasek zakładek odblokowujemy z powrotem `pointer-events:auto`
                  // (patrz `tabsBarBlock`) i renderujemy w nim tylko nawigację (bez edycji).
                  // Na layoucie systemowym tenant edytuje przez „Duplikuj" (dropdown layoutów,
                  // POZA tym wrapperem — pozostaje klikalny).
                  style: { pointerEvents: 'none', userSelect: 'none' },
                }
              : {})}
            aria-disabled={readOnlyMode || isSystemManagedLayout || undefined}
          >
            {selectedTab?.type === 'fields' ? (
              <FieldsTabContent
                sections={selectedTabSections}
                availableFields={availableFields}
                usedFields={usedFields}
                fieldById={fieldById}
                selectedSectionId={selectedSectionId}
                onSelectSection={setSelectedSectionId}
                onAddSection={addSection}
                onRemoveSection={removeSection}
                onDuplicateSection={duplicateSection}
                onUpdateSection={updateSection}
                onAddFieldToSection={addFieldToActiveSection}
                onRemoveField={removeFieldFromSection}
                onAddAccordionItem={addAccordionItem}
                onUpdateAccordionItemTitle={updateAccordionItemTitle}
                onRemoveAccordionItem={removeAccordionItem}
                onAddStep={addStep}
                onUpdateStepTitle={updateStepTitle}
                onUpdateStepIcon={updateStepIcon}
                onRemoveStep={removeStep}
                onAddFromPaletteToContainer={addFromPaletteToContainer}
                onSectionReorder={reorderSections}
                onElementReorder={reorderElementsInContainer}
                onAccordionPanelReorder={reorderAccordionPanelsHandler}
                onStepReorder={reorderStepsHandler}
                selectedTab={selectedTab}
                roleTemplates={rolesQuery.data ?? []}
                onUpdateTab={(patch) => updateTab(selectedTab.id, patch)}
                leftPanelTab={leftPanelTab}
                setLeftPanelTab={setLeftPanelTab}
                // Admin-embedded bez adminowej ścieżki (addFieldKartotekaSlug) = brak
                // przycisku — tenantowy POST /system-modules/:slug/fields dałby 401.
                onAddFieldClick={
                  isAdminEmbedded && !addFieldKartotekaSlug
                    ? undefined
                    : () => setShowAddFieldModal(true)
                }
                onAddLayoutElement={addLayoutElementToCanvas}
                tabsBarSlot={tabsBarBlock}
                hideRoleVisibility={isAdminEmbedded}
                moduleSlug={slug}
                selectedElementId={selectedElementId}
                onSelectElement={setSelectedElementId}
                // Podgląd (readOnlyMode) nie zapisuje: bez callbacku kontrolki inline klocków
                // (np. edytor kolumn tabeli dodatkowej) są tylko podglądem, a stan się nie zmienia.
                onUpdateElementSettings={readOnlyMode ? undefined : updateElementSettings}
                systemHideOnly={isSystemManagedLayout}
                onToggleFieldHidden={toggleFieldHidden}
                viewLevelElements={viewLevelElements}
                excludeElementTypes={excludedSingletonTypes}
              />
            ) : isHelperInline ? (
              // F12.N T5.6b — inline edycja helper-encji.
              // Reuse FieldsTabContent z helper-scope propsami. selectedTab to
              // wirtualna zakładka 'data' helper-encji (1:1 z bazy). tabsBarSlot
              // pokazuje pasek macierzystych zakładek (nawigacja nadrzędna).
              // Note: zakładka helper-encji ma type='fields', więc casting bezpieczny.
              <FieldsTabContent
                sections={helperVirtualTabSections}
                availableFields={helperAvailableFields}
                usedFields={helperUsedFields}
                fieldById={helperFieldById}
                selectedSectionId={activeHelperEdit?.selectedSectionId ?? null}
                onSelectSection={(id) => setHelperSelectedSection(activeHelperCollection!, id)}
                onAddSection={addHelperSection}
                onRemoveSection={removeHelperSection}
                onUpdateSection={updateHelperSection}
                onAddFieldToSection={addFieldToActiveHelperSection}
                onRemoveField={removeFieldFromHelperSection}
                // Pakiet B Sub-5 (2026-05-17) — akordeon w canvasie helper-encji
                // już wspierany (parytet z macierzystym builderem). Wcześniejszy toast
                // „kolejna iteracja" zastąpiony prawdziwymi helper-handlerami.
                // Etap 3 T2 (2026-05-17) — analogicznie stepper/wizard w helper-encji.
                onAddAccordionItem={addAccordionItemHelper}
                onUpdateAccordionItemTitle={updateAccordionItemTitleHelper}
                onRemoveAccordionItem={removeAccordionItemHelper}
                onAddStep={addStepHelper}
                onUpdateStepTitle={updateStepTitleHelper}
                onUpdateStepIcon={updateStepIconHelper}
                onRemoveStep={removeStepHelper}
                onSectionReorder={reorderHelperSections}
                onElementReorder={reorderHelperElementsInContainer}
                onAccordionPanelReorder={reorderHelperAccordionPanelsHandler}
                onStepReorder={reorderHelperStepsHandler}
                selectedTab={helperVirtualTab!}
                roleTemplates={rolesQuery.data ?? []}
                onUpdateTab={(patch) => updateHelperVirtualTab(activeHelperCollection!, patch)}
                leftPanelTab={leftPanelTab}
                setLeftPanelTab={setLeftPanelTab}
                onAddFieldClick={() => {
                  // Helper-encje: dodawanie pól dodatkowych z buildera macierzystego
                  // niewspierane (admin idzie do Settings → helper-encja → Pola).
                  // Pokazujemy toast jako informacja, nie modal.
                  toast.show({
                    variant: 'error',
                    title: t('tenant.systemModuleBuilder.helperInline.addFieldNotSupported', {
                      defaultValue:
                        'Aby dodać pole dodatkowe do tej encji pomocniczej, otwórz ją w Ustawienia → Kartoteki systemowe',
                    }),
                  });
                }}
                onAddLayoutElement={addLayoutElementToHelperCanvas}
                tabsBarSlot={tabsBarBlock}
                helperInlineBanner={
                  <div
                    className="mb-2 rounded-lg border border-accent-300 bg-accent-50 px-3 py-2 text-xs text-accent-900 dark:bg-accent-900/20 dark:text-accent-100"
                    data-testid={`system-module-builder-helper-inline-banner-${activeHelperCollection}`}
                  >
                    {t('tenant.systemModuleBuilder.helperInline.banner', {
                      collection: activeHelperCollection,
                      defaultValue:
                        'Edytujesz pola encji pomocniczej „{{collection}}". Zmiany zapisują się osobno od layoutu macierzystego.',
                    })}
                  </div>
                }
                hideRoleVisibility={isAdminEmbedded}
                moduleSlug={activeHelperCollection ?? undefined}
              />
            ) : isHelperLoading ? (
              <div
                className="flex flex-1 flex-col items-stretch p-6"
                data-testid="system-module-builder-helper-loading"
              >
                {tabsBarBlock}
                <div className="mt-6 flex justify-center">
                  <Spinner />
                </div>
              </div>
            ) : selectedTab &&
              selectedTab.type === 'subcollection' &&
              selectedTab.collection &&
              !isTabReadOnly(selectedTab) ? (
              // F12 PR 2 pkt 2 v4 (2026-05-19, iter 2a): full-width SubcollectionLayoutEditor
              // z WŁASNĄ paletą po lewej (Pola helper-encji | Klocki layoutu) + sortable sekcje
              // + sortable pola w sekcjach. Globalna paleta klocków (LayoutElementsPalette)
              // NIEPOTRZEBNA tutaj — v4 ma swoją (tab Klocki). Pasek zakładek formularza
              // + TabPropertiesPanel po prawej zachowane.
              <>
                <div className="flex flex-1 flex-col overflow-hidden">
                  <div className="p-4 pb-0">{tabsBarBlock}</div>
                  <div className="flex flex-1 overflow-hidden">
                    <SubcollectionLayoutEditor
                      collectionSlug={selectedTab.collection}
                      formLayout={selectedTab.subcollectionFormLayout}
                      onFormLayoutChange={(next) => {
                        updateTab(selectedTab.id, { subcollectionFormLayout: next });
                      }}
                      element={
                        (selectedTab.extras ?? []).find(
                          (e) => e.type === 'related_records_table',
                        ) as SubcollectionLayoutElement | undefined
                      }
                      onChange={(newElement) => {
                        const otherExtras = (selectedTab.extras ?? []).filter(
                          (e) => e.type !== 'related_records_table',
                        );
                        updateTab(selectedTab.id, {
                          extras: [newElement, ...otherExtras],
                        });
                      }}
                      embedded={isAdminEmbedded}
                      externalFields={
                        isAdminEmbedded
                          ? (externalHelperFields?.[selectedTab.collection] ?? [])
                          : undefined
                      }
                      externalHelperLayout={
                        isAdminEmbedded
                          ? externalHelperLayouts?.[selectedTab.collection]
                          : undefined
                      }
                    />
                  </div>
                  {(selectedTab.extras ?? []).filter((e) => e.type !== 'related_records_table')
                    .length > 0 && (
                    <div className="border-t border-border-primary p-4">
                      <NonFieldsExtrasPanel
                        tab={selectedTab}
                        onRemoveExtra={(elementId) => removeExtraFromTab(selectedTab.id, elementId)}
                        systemHideOnly={isSystemManagedLayout}
                        selectedExtraId={selectedExtra?.id ?? null}
                        onSelectExtra={extrasSelectionEnabled ? setSelectedExtraId : undefined}
                      />
                    </div>
                  )}
                </div>
                <aside
                  className="w-72 shrink-0 overflow-y-auto border-l border-border-primary bg-bg-primary p-4"
                  data-testid="system-module-builder-tab-properties"
                >
                  <TabPropertiesPanel
                    tab={selectedTab}
                    roleTemplates={rolesQuery.data ?? []}
                    onChange={(patch) => updateTab(selectedTab.id, patch)}
                    hideRoleVisibility={isAdminEmbedded}
                  />
                  {selectedExtra && (
                    <ExtraElementProperties
                      element={selectedExtra}
                      onChange={(patch) => updateElementSettings(selectedExtra.id, patch)}
                      moduleSlug={slug}
                    />
                  )}
                </aside>
              </>
            ) : selectedTab ? (
              <>
                {/* F12.N E2.T1: lewy panel z paletą klocków dla zakładek non-fields
                    INNYCH niż subcollection (audit_log/tree_picker/satellite/additional/panel).
                    Klocki (heading/alert/separator/kontenery itp.) dodawane do tab.extras
                    przez addLayoutElementToCanvas — admin może wzbogacić każdą zakładkę. */}
                <aside
                  className="w-64 shrink-0 overflow-y-auto border-r border-border-primary bg-bg-secondary p-3"
                  data-testid="system-module-builder-non-fields-palette"
                >
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-muted">
                    {t('tenant.systemModuleBuilder.elementsPalette', {
                      defaultValue: 'Klocki layoutu',
                    })}
                  </p>
                  <LayoutElementsPalette
                    onAdd={addLayoutElementToCanvas}
                    moduleSlug={slug}
                    excludeTypes={nonFieldsExcludedTypes}
                  />
                </aside>
                <div className="flex flex-1 flex-col overflow-y-auto p-4">
                  {tabsBarBlock}
                  <div className="mt-3">
                    {isAdminEmbedded &&
                    !(builderHasTenantData && (isAdditionalTableTab || isAdditionalQueryTab)) ? (
                      /* W trybie embedded (super admin, panel ITE) zakładki read-only nie mogą
                         renderować żywych komponentów tenant-scope (tree_picker, additional
                         itd. strzelają w API tenanta → 401 → crash). Zamiast tego
                         pokazujemy neutralny placeholder. Wyjątek: kreator osadzony w zakresie
                         tenanta (`builderHasTenantData`) — tam CAŁA zakładka tabeli dodatkowej
                         (`isAdditionalTableTab`) albo widoku SQL (`isAdditionalQueryTab`) dostaje
                         panel z wyborem tabeli/widoku i edytorem kolumn jak poza oknem osadzonym;
                         gdy host oznacza ten typ zakładki jako tylko do odczytu (`isTabReadOnly`),
                         panel jest tylko do odczytu (wybór tabeli/widoku zablokowany, układ kolumn
                         bez kontrolek) — placeholder zostaje wyłącznie w kreatorze osadzonym
                         w panelu ITE. */
                      <EmbeddedReadOnlyTabPlaceholder tab={selectedTab} />
                    ) : isAdditionalTableTab ? (
                      <AdditionalTableConfigPanel
                        tab={selectedTab}
                        parentModuleSlug={slug ?? ''}
                        // Podgląd, layout systemowy i zakładka oznaczona przez hosta jako tylko do
                        // odczytu (`readOnlyTabTypes`/`readOnlyTabCollections`): wybór tabeli
                        // zablokowany (mimo `pointer-events: none` zmiana z klawiatury wyczyściłaby
                        // układ kolumn). Zmiana tabeli czyści układ kolumn i maskę poprzedniej (id
                        // kolumn = slugi jej pól).
                        onChangeTableSlug={
                          readOnlyMode || isSystemManagedLayout || isTabReadOnly(selectedTab)
                            ? undefined
                            : (tableSlug) =>
                                updateTab(selectedTab.id, {
                                  tableSlug,
                                  tableColumns: undefined,
                                  hiddenColumnIds: undefined,
                                })
                        }
                        // Układ własny: cały układ kolumn (tryb `full` — maska wtapiana w układ
                        // i czyszczona). Te same warunki co wybór tabeli.
                        onChangeColumns={
                          readOnlyMode || isSystemManagedLayout || isTabReadOnly(selectedTab)
                            ? undefined
                            : (tableColumns) =>
                                updateTab(selectedTab.id, {
                                  tableColumns,
                                  hiddenColumnIds: undefined,
                                })
                        }
                        // Układ systemowy: tenant tylko ukrywa kolumny (tryb `hideOnly`). Maska
                        // ląduje w `settings.hiddenColumnIds` elementu tabeli zakładki i każdej
                        // innej kopii tej tabeli w drzewie (`applySystemHidePatch`), a zapis
                        // wyprowadza z niej nakładkę tenanta pod kluczem tabeli. Podgląd i zakładka
                        // tylko do odczytu — bez zapisu.
                        onChangeHiddenColumns={
                          readOnlyMode || !isSystemManagedLayout || isTabReadOnly(selectedTab)
                            ? undefined
                            : (hiddenColumnIds) => {
                                const overlayKey = tabTableOverrideKey(selectedTab.id);
                                if (overlayKey === undefined) {
                                  // Element bez klucza nakładki nie dzieli maski z żadną kopią.
                                  updateTab(selectedTab.id, { hiddenColumnIds });
                                } else {
                                  applySystemHidePatch(overlayKey, { hiddenColumnIds });
                                }
                              }
                        }
                      />
                    ) : isAdditionalQueryTab ? (
                      <AdditionalQueryConfigPanel
                        tab={selectedTab}
                        parentModuleSlug={slug ?? ''}
                        // Jak przy tabeli dodatkowej: podgląd, layout systemowy i zakładka
                        // oznaczona przez hosta jako tylko do odczytu — wybór widoku zablokowany
                        // (zmiana widoku czyści układ kolumn, więc nie może być dostępna z klawiatury).
                        // Zmiana widoku czyści układ kolumn i maskę poprzedniego (id kolumn =
                        // nazwy kolumn jego zapytania).
                        onChangeViewSlug={
                          readOnlyMode || isSystemManagedLayout || isTabReadOnly(selectedTab)
                            ? undefined
                            : (viewSlug) =>
                                updateTab(selectedTab.id, {
                                  viewSlug,
                                  tableColumns: undefined,
                                  hiddenColumnIds: undefined,
                                })
                        }
                        // Układ własny: cały układ kolumn (tryb `full` — maska wtapiana w układ
                        // i czyszczona). Te same warunki co wybór widoku.
                        onChangeColumns={
                          readOnlyMode || isSystemManagedLayout || isTabReadOnly(selectedTab)
                            ? undefined
                            : (tableColumns) =>
                                updateTab(selectedTab.id, {
                                  tableColumns,
                                  hiddenColumnIds: undefined,
                                })
                        }
                        // Układ systemowy: tenant tylko ukrywa kolumny (tryb `hideOnly`); maska
                        // trafia do elementu widoku zakładki i każdej innej kopii tego widoku
                        // w drzewie (`applySystemHidePatch`, klucz `additional_query:<viewSlug>`),
                        // a zapis wyprowadza z niej nakładkę tenanta. Podgląd i zakładka tylko do
                        // odczytu — bez zapisu.
                        onChangeHiddenColumns={
                          readOnlyMode || !isSystemManagedLayout || isTabReadOnly(selectedTab)
                            ? undefined
                            : (hiddenColumnIds) => {
                                const overlayKey = tabTableOverrideKey(selectedTab.id);
                                if (overlayKey === undefined) {
                                  // Element bez klucza nakładki nie dzieli maski z żadną kopią.
                                  updateTab(selectedTab.id, { hiddenColumnIds });
                                } else {
                                  applySystemHidePatch(overlayKey, { hiddenColumnIds });
                                }
                              }
                        }
                      />
                    ) : selectedTab.type === 'block' ? (
                      <div
                        className="p-6 text-center text-sm text-text-tertiary"
                        data-testid="system-module-builder-tab-content-block"
                      >
                        {t('tenant.systemModuleBuilder.blockTabHint', {
                          defaultValue:
                            'Zakładka złożona z bloków — dodaj bloki z palety po lewej. Bloki tej zakładki widać poniżej (można je usuwać).',
                        })}
                      </div>
                    ) : (
                      <NonFieldsTabPlaceholder tab={selectedTab} parentModuleSlug={slug} />
                    )}
                    <NonFieldsExtrasPanel
                      tab={selectedTab}
                      onRemoveExtra={(elementId) => removeExtraFromTab(selectedTab.id, elementId)}
                      onUpdateExtraSettings={readOnlyMode ? undefined : updateElementSettings}
                      systemHideOnly={isSystemManagedLayout}
                      selectedExtraId={selectedExtra?.id ?? null}
                      onSelectExtra={extrasSelectionEnabled ? setSelectedExtraId : undefined}
                    />
                  </div>
                </div>
                <aside
                  className="w-72 shrink-0 overflow-y-auto border-l border-border-primary bg-bg-primary p-4"
                  data-testid="system-module-builder-tab-properties"
                >
                  <TabPropertiesPanel
                    tab={selectedTab}
                    roleTemplates={rolesQuery.data ?? []}
                    onChange={(patch) => updateTab(selectedTab.id, patch)}
                    hideRoleVisibility={isAdminEmbedded}
                  />
                  {selectedExtra && (
                    <ExtraElementProperties
                      element={selectedExtra}
                      onChange={(patch) => updateElementSettings(selectedExtra.id, patch)}
                      moduleSlug={slug}
                    />
                  )}
                </aside>
              </>
            ) : (
              <div className="flex flex-1 flex-col items-stretch p-6">
                {tabsBarBlock}
                <p className="mt-4 text-center text-sm text-text-tertiary">
                  {t('tenant.systemModuleBuilder.selectTabHint')}
                </p>
              </div>
            )}
          </div>
        );
      })()}

      <LayoutFormModal
        open={layoutFormOpen}
        mode={layoutFormMode}
        initialValues={layoutFormInitial}
        categories={categories}
        roleTemplates={rolesQuery.data}
        showPreviewDockToggle
        saving={createLayoutMutation.isPending || updateLayoutMetaMutation.isPending}
        onSubmit={handleLayoutFormSubmit}
        onClose={() => setLayoutFormOpen(false)}
      />

      {!isAdminEmbedded && (
        <FormImportModal
          open={formImportOpen}
          onClose={() => setFormImportOpen(false)}
          endpointBase={`/system-modules/${slug}/layouts`}
          onImported={(id) => void handleFormImported(id)}
        />
      )}

      <ConfirmDialog
        open={pendingDeleteLayout !== null}
        title={t('common.delete')}
        message={
          pendingDeleteLayout
            ? t('tenant.systemModuleBuilder.confirmDeleteLayout', {
                name: pendingDeleteLayout.name,
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDeleteLayout) deleteLayoutMutation.mutate(pendingDeleteLayout.id);
          setPendingDeleteLayout(null);
        }}
        onClose={() => setPendingDeleteLayout(null)}
      />

      {/* F12.N D3.3 — potwierdzenie usunięcia user-added zakładki. */}
      <ConfirmDialog
        open={pendingDeleteTabId !== null}
        title={t('tenant.systemModuleBuilder.confirmDeleteTabTitle')}
        message={
          pendingDeleteTabId
            ? t('tenant.systemModuleBuilder.confirmDeleteTabMessage', {
                name: tabs.find((tt) => tt.id === pendingDeleteTabId)?.title ?? '',
              })
            : ''
        }
        confirmLabel={t('common.delete')}
        onConfirm={() => {
          if (pendingDeleteTabId) removeTab(pendingDeleteTabId);
          setPendingDeleteTabId(null);
        }}
        onClose={() => setPendingDeleteTabId(null)}
      />

      {/* Krok A — modal "Dodaj pole dodatkowe" wywoływany z lewego panelu (tab "Pola").
          Tylko tenant-scope: w trybie admin-embedded POST /system-modules/:slug/fields = 401. */}
      {slug && !isAdminEmbedded && (
        <AddSystemFieldModal
          open={showAddFieldModal}
          onClose={() => setShowAddFieldModal(false)}
          moduleSlug={slug}
        />
      )}

      {/* Admin-embedded (kartoteka): adminowa ścieżka dodania pola — add-only,
          POST /admin/kartoteki/:slug/fields; invalidacja definicji odświeża paletę. */}
      {isAdminEmbedded && addFieldKartotekaSlug && (
        <AddKartotekaFieldModal
          open={showAddFieldModal}
          onClose={() => setShowAddFieldModal(false)}
          kartotekaSlug={addFieldKartotekaSlug}
        />
      )}

      {/* F12.N Etap 4 E4.T2/T3 — modal dodawania/edycji user-added zakładki. */}
      <TabFormModal
        open={tabFormModalState !== null}
        mode={tabFormModalState?.mode ?? 'add'}
        initial={tabFormModalState?.initial}
        existingSlugs={
          tabFormModalState?.mode === 'edit'
            ? tabs.filter((t) => t.id !== tabFormModalState.tabId).map((t) => t.slug ?? '')
            : tabs.map((t) => t.slug ?? '')
        }
        onSubmit={handleTabFormSubmit}
        onClose={() => setTabFormModalState(null)}
      />

      {/* F12 PR2 część 4 + T10 — modal „Dodaj/Edytuj kategorię" z chip-baru buildera. */}

      {/* T10 — potwierdzenie usunięcia kategorii z chip-baru buildera. */}

      {/* F12.N Etap 4 E4.T5 — guard niezapisanych zmian przy klik „Wstecz". */}
      <ConfirmDialog
        open={pendingBackNavigation}
        variant="danger"
        title={t('tenant.systemModuleBuilder.confirmUnsavedTitle')}
        message={t('tenant.systemModuleBuilder.confirmUnsavedMessage')}
        confirmLabel={t('tenant.systemModuleBuilder.confirmUnsavedDiscard')}
        onConfirm={() => {
          setPendingBackNavigation(false);
          navigate(`/system-modules/${slug}/settings?tab=layout`);
        }}
        onClose={() => setPendingBackNavigation(false)}
      />

      {/* Embedded mode — potwierdzenie destrukcyjnego resetu canvasu (§4.12). */}
      <ConfirmDialog
        open={pendingReset !== null}
        variant="danger"
        title={t('tenant.systemModuleBuilder.confirmReset.title')}
        message={
          pendingReset === 'default'
            ? t('tenant.systemModuleBuilder.confirmReset.messageDefault')
            : t('tenant.systemModuleBuilder.confirmReset.messageBlank')
        }
        confirmLabel={t('tenant.systemModuleBuilder.confirmReset.confirm')}
        onConfirm={() => {
          if (pendingReset) executeReset(pendingReset);
          setPendingReset(null);
        }}
        onClose={() => setPendingReset(null)}
      />

      {/* Przełączenie layoutu z niezapisanymi zmianami — potwierdzenie porzucenia (§4.12). */}
      <ConfirmDialog
        open={pendingLayoutSwitch !== null}
        variant="danger"
        title={t('tenant.systemModuleBuilder.confirmUnsavedTitle')}
        message={t('tenant.systemModuleBuilder.confirmSwitchUnsaved')}
        confirmLabel={t('tenant.systemModuleBuilder.confirmUnsavedDiscard')}
        onConfirm={() => {
          if (pendingLayoutSwitch) applyLayoutSwitch(pendingLayoutSwitch);
          setPendingLayoutSwitch(null);
        }}
        onClose={() => setPendingLayoutSwitch(null)}
      />
    </main>
  );

  // Środowisko kreatora dla klocków czytających dane tenanta (np. „Tabela dodatkowa" pokazuje
  // kolumny wybranej tabeli): w kreatorze osadzonym w panelu ITE endpointy tenanta zwracają 401,
  // więc tam klocek zostaje placeholderem, a panel właściwości nie pokazuje wyboru tabeli
  // (patrz `builderHasTenantData`) — zamiast odpowiedzi byłyby tylko zbędne zapytania i błędy.
  // `moduleSlug` dla klocków, których płótno musi znać kartotekę (np. „Przycisk SQL” sprawdza,
  // czy wybrana procedura jeszcze istnieje).
  return (
    <BuilderEnvironmentProvider tenantData={builderHasTenantData} moduleSlug={slug}>
      {builderMain}
    </BuilderEnvironmentProvider>
  );
}

// ─── C.7: Sortable tab pill (top-bar zakładki) ──────────────────────────────

interface SortableTabPillProps {
  tab: LayoutTab;
  selected: boolean;
  onSelect: () => void;
  onToggleHidden: () => void;
  onRename: (title: string) => void;
  /** F12.N D3.3 — handler usunięcia. Widoczny tylko dla zakładek user-added
      (slug pasuje do `^tab-[0-9a-f]{8}$` z `addTab` generującego `tab-{uuid8}`).
      Systemowe zakładki (data/categories/addresses/...) nie mają tej akcji. */
  onDelete: () => void;
  /** F12.N Etap 4 E4.T3 — handler edycji nazwy/ikony/slug (modal). Wołany z pencila
      TYLKO dla user-added zakładek — systemowe używają pencila do inline-rename
      (ten sam mechanizm co dwuklik na tytule), bez otwierania tego modala. */
  onEdit: () => void;
  /**
   * Gdy true — kosz (usuń) pokazuje się dla KAŻDEJ zakładki (nie tylko user-added).
   * Używane w trybie projektowania layoutu przez admina: usunięcie = pominięcie
   * zakładki w TYM layoucie (struktura kartoteki nietknięta).
   */
  allowDeleteAny?: boolean;
  /**
   * #6 — layout systemowy (`is_system_managed`). Nawigacja aktywna, ale znika zmiana
   * nazwy (ołówek + dwuklik), kosz i uchwyt przeciągania (reorder = edycja struktury).
   * Odróżnione od `readOnly` (podgląd): tu layout jest realny i edytowalny, tylko struktura
   * systemowa jest zamrożona. `readOnly` implikuje wszystkie te blokady dodatkowo.
   */
  structureLocked?: boolean;
  /**
   * Tryb podglądu (read-only). Nawigacja między zakładkami zostaje aktywna (klik
   * przełącza), ale znikają wszystkie kontrolki EDYCJI: uchwyt przeciągania,
   * przełącznik widoczności (oko), ołówek (edycja) i kosz; dwuklik nie wchodzi
   * w zmianę nazwy. Dzięki temu w podglądzie da się obejrzeć każdą zakładkę,
   * nie zmieniając struktury layoutu.
   */
  readOnly?: boolean;
}

/** F12.N D3.3 + Etap 4: heurystyka rozróżnienia user-added vs systemowa zakładka.
    Wszystkie user-added zakładki mają slug z prefiksem `tab-` — wcześniej był to
    `tab-{uuid8}` (D3 auto-gen), teraz `tab-<wpisane przez admina>` (Etap 4 modal).
    Systemowe sluggi są nazwane bez prefiksu (data, categories, addresses, ...). */
function isUserAddedTab(tab: LayoutTab): boolean {
  if (!tab.slug) return false;
  return tab.slug.startsWith('tab-');
}

function SortableTabPill({
  tab,
  selected,
  onSelect,
  onToggleHidden,
  onRename,
  onDelete,
  onEdit,
  allowDeleteAny = false,
  structureLocked = false,
  readOnly = false,
}: SortableTabPillProps) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: tab.id,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const [editing, setEditing] = useState(false);
  const [draftTitle, setDraftTitle] = useState(tab.title);
  // #6: struktura edytowalna tylko poza podglądem i poza layoutem systemowym.
  // Zmiana nazwy (ołówek+dwuklik), kosz ORAZ reorder (uchwyt przeciągania) zależą od tego.
  // DECYZJA (2026-08-21): na systemowym layoucie wolno TYLKO UKRYWAĆ (oko), NIE przesuwać —
  // więc uchwyt reorderu zakładek jest tu, jak reszta struktury, wyłączony na systemowym.
  // Chowanie zakładki (oko) świadomie POZA tym — to odwracalna preferencja widoku, dozwolona
  // także na systemowym (zapis do nakładki per-tenant, nie do layout_config).
  const canEditStructure = !readOnly && !structureLocked;
  // Reorder zakładek = tylko na edytowalnym layoucie (jak edycja struktury).
  const canReorderTab = canEditStructure;

  function commitRename() {
    const trimmed = draftTitle.trim();
    if (trimmed && trimmed !== tab.title) {
      onRename(trimmed);
    } else {
      setDraftTitle(tab.title);
    }
    setEditing(false);
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={() => !editing && onSelect()}
      className={
        'group flex cursor-pointer items-center gap-1 rounded-t-md border-b-2 px-3 py-2 transition ' +
        (selected
          ? 'border-accent-500 bg-bg-secondary'
          : 'border-transparent hover:bg-bg-tertiary') +
        (tab.hidden ? ' opacity-50' : '') +
        (isDragging ? ' z-10 opacity-50' : '')
      }
      data-testid={`system-module-builder-tab-${tab.slug ?? tab.id}`}
      data-selected={selected ? 'true' : 'false'}
      data-hidden={tab.hidden ? 'true' : 'false'}
    >
      {canReorderTab && (
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label={t('tenant.systemModuleBuilder.tabDragHandle')}
          onClick={(e) => e.stopPropagation()}
          className="cursor-grab rounded p-0.5 text-text-muted hover:bg-bg-tertiary"
        >
          <GripVertical className="h-3 w-3" />
        </button>
      )}

      {/* F12.N Etap 4 E4.T4: render ikony zakładki (jeśli set). */}
      {tab.icon && renderIconByName(tab.icon, 'h-3.5 w-3.5 text-text-muted')}

      {editing ? (
        <Input
          value={draftTitle}
          onChange={(e) => setDraftTitle(e.target.value)}
          onBlur={commitRename}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitRename();
            if (e.key === 'Escape') {
              // „Obsłużone" — okno kreatora, w którym stoi edytor, się nie zamyka.
              e.preventDefault();
              setDraftTitle(tab.title);
              setEditing(false);
            }
          }}
          autoFocus
          className="h-6 w-32 text-sm"
          data-testid={`system-module-builder-tab-${tab.slug ?? tab.id}-rename-input`}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span
          className="text-sm font-medium text-text-primary"
          onDoubleClick={(e) => {
            e.stopPropagation();
            // S7: opublikowanej zakładki nie wolno przemianować (dwuklik nie wchodzi w edycję).
            // Podgląd (readOnly) i layout systemowy (#6): zmiana nazwy zablokowana — dwuklik nawiguje.
            if (!tab.publishedAt && canEditStructure) setEditing(true);
          }}
          data-testid={`system-module-builder-tab-${tab.slug ?? tab.id}-title`}
        >
          {tab.title}
        </span>
      )}

      <span className="ml-1 text-[10px] text-text-muted">
        {t(`tenant.systemModuleBuilder.tabType.${tab.type}`, { defaultValue: tab.type })}
      </span>

      {!readOnly && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onToggleHidden();
          }}
          aria-label={
            tab.hidden
              ? t('tenant.systemModuleBuilder.showTab')
              : t('tenant.systemModuleBuilder.hideTab')
          }
          title={
            tab.hidden
              ? t('tenant.systemModuleBuilder.showTab')
              : t('tenant.systemModuleBuilder.hideTab')
          }
          className="rounded p-0.5 text-text-muted opacity-0 transition group-hover:opacity-100 hover:bg-bg-tertiary"
          data-testid={`system-module-builder-tab-${tab.slug ?? tab.id}-toggle-hidden`}
        >
          {tab.hidden ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
        </button>
      )}

      {/* F12.N Etap 4 E4.T3 — przycisk edit (pencil), widoczny dla KAŻDEJ zakładki
          (system czy user-added) — dwuklik na tytule jest odkrywalny wyłącznie
          przez przypadek, ołówek daje jawną afordancję dla tej samej akcji.
          Zakładki systemowe: pencil = ten sam inline-rename co dwuklik (tylko
          tytuł — slug/typ nietykalne, bo dispatch po slugu jest hardkodowany
          w kodzie stron, np. EmployeeDetail.tsx). Zakładki user-added: pencil
          otwiera pełny modal (tytuł+slug+ikona) — bez zmiany zachowania.
          ETAP4 S7: ukryty gdy zakładka opublikowana (nazwy nie wolno zmieniać).
          #6: ukryty na layoucie systemowym (canEditStructure=false). */}
      {!tab.publishedAt && canEditStructure && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (isUserAddedTab(tab)) onEdit();
            else setEditing(true);
          }}
          aria-label={t('tenant.systemModuleBuilder.editTab')}
          title={t('tenant.systemModuleBuilder.editTab')}
          className="rounded p-0.5 text-text-muted opacity-0 transition group-hover:opacity-100 hover:bg-bg-tertiary hover:text-accent-600"
          data-testid={`system-module-builder-tab-${tab.slug ?? tab.id}-edit`}
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}

      {/* F12.N D3.3 — ikona kosza. `allowDeleteAny` (layout niesystemowy tenanta lub
          admin-embedded) pokazuje kosz przy KAŻDEJ zakładce; inaczej tylko user-added.
          ETAP4 S7: ukryty gdy zakładka opublikowana (wysłana do klientów = trwała).
          #6: ukryty na layoucie systemowym (canEditStructure=false) — struktura nietykalna. */}
      {(isUserAddedTab(tab) || allowDeleteAny) && !tab.publishedAt && canEditStructure && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          aria-label={t('tenant.systemModuleBuilder.deleteTab')}
          title={t('tenant.systemModuleBuilder.deleteTab')}
          className="rounded p-0.5 text-text-muted opacity-0 transition group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
          data-testid={`system-module-builder-tab-${tab.slug ?? tab.id}-delete`}
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}

      {/* ETAP4 S7 — kłódka + tooltip dla opublikowanej zakładki user-added
          (kosz/ołówek ukryte; zostaje przesuwanie kolejności). */}
      {isUserAddedTab(tab) && tab.publishedAt && (
        <span
          className="rounded p-0.5 text-text-muted"
          title={t('tenant.systemModuleBuilder.tabPublishedTooltip')}
          aria-label={t('tenant.systemModuleBuilder.tabPublishedTooltip')}
          data-testid={`system-module-builder-tab-${tab.slug ?? tab.id}-published-lock`}
        >
          <Lock className="h-3 w-3" />
        </span>
      )}
    </div>
  );
}

// ─── E2.T2a: Pasek zakładek WEWNĄTRZ canvasu (jak SellCall) ─────────────────
//
// Zastępuje stary top-bar nad canvasem. Renderowany:
//  - w canvasie FieldsTabContent (przekazany jako `tabsBarSlot`)
//  - inline w środkowej kolumnie dla zakładek non-fields
//  - standalone gdy brak `selectedTab` (empty state z + Nowa)

interface TabsBarBlockProps {
  tabs: LayoutTab[];
  selectedTabId: string | null;
  sensors: ReturnType<typeof useSensors>;
  onTabsDragEnd: (event: DragEndEvent) => void;
  onSelectTab: (tabId: string) => void;
  onToggleTabHidden: (tabId: string) => void;
  onRenameTab: (tabId: string, title: string) => void;
  /**
   * Gdy undefined — przycisk „Dodaj zakładkę" jest ukryty (tryb read-only zakładek,
   * np. override kartoteki super admina — dodawanie nowych zakładek nie jest dozwolone).
   */
  onAddTab: (() => void) | undefined;
  /** F12.N D3.3 — handler żądania usunięcia zakładki (otwiera ConfirmDialog w root). */
  onDeleteTabRequest: (tabId: string) => void;
  /** F12.N Etap 4 E4.T3 — handler żądania edycji nazwy/ikony/slug zakładki (otwiera modal w root). */
  onEditTabRequest: (tabId: string) => void;
  /** Gdy true — kosz przy KAŻDEJ zakładce (tryb projektowania layoutu przez admina). */
  allowDeleteAny?: boolean;
  /**
   * #6 — layout systemowy (`is_system_managed`): struktura NIETYKALNA. Zakładki nadal
   * się PRZEŁĄCZAJĄ (nawigacja), ale znika zmiana nazwy (ołówek+dwuklik), kosz i „Nowa
   * zakładka". Odróżnione od `readOnly` (podgląd) — tu chodzi o realny, edytowalny widok
   * layoutu systemowego u tenanta, gdzie wolno tylko oglądać strukturę.
   */
  structureLocked?: boolean;
  /**
   * Tryb podglądu (read-only): zakładki nadal się PRZEŁĄCZAJĄ (nawigacja), ale
   * znikają wszystkie kontrolki edycji (dodawanie/usuwanie/zmiana nazwy/przeciąganie).
   */
  readOnly?: boolean;
}

function TabsBarBlock({
  tabs,
  selectedTabId,
  sensors,
  onTabsDragEnd,
  onSelectTab,
  onToggleTabHidden,
  onRenameTab,
  onAddTab,
  onDeleteTabRequest,
  onEditTabRequest,
  allowDeleteAny = false,
  structureLocked = false,
  readOnly = false,
}: TabsBarBlockProps) {
  // W podglądzie ORAZ na layoucie systemowym (#6) ukrywamy „+ Dodaj zakładkę" —
  // dodawanie to edycja struktury.
  const addTabHandler = readOnly || structureLocked ? undefined : onAddTab;
  const { t } = useTranslation();
  return (
    <div
      className="rounded-md border border-border-primary bg-bg-primary px-3 py-2"
      data-testid="system-module-builder-tabs-block"
    >
      <div className="mb-2 flex items-center gap-2">
        <LayoutIcon className="h-3.5 w-3.5 text-text-muted" />
        <h3 className="text-xs font-semibold uppercase tracking-wide text-text-muted">
          {t('tenant.systemModuleBuilder.tabsBlockTitle')}
        </h3>
      </div>
      {tabs.length === 0 ? (
        <div className="flex items-center justify-between gap-3 py-1">
          <p className="text-xs text-text-tertiary">
            {t('tenant.systemModuleBuilder.tabsEmptyInCanvas')}
          </p>
          {addTabHandler && (
            <Button
              variant="primary"
              size="sm"
              leftIcon={<Plus className="h-3 w-3" />}
              onClick={addTabHandler}
              data-testid="system-module-builder-add-tab-btn"
            >
              {t('tenant.systemModuleBuilder.addTab')}
            </Button>
          )}
        </div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onTabsDragEnd}>
          {/* F12.N D3.4 — responsive wrap: `flex-wrap` zamiast `min-w-max overflow-x-auto`
              żeby zakładki zawijały się do drugiego/trzeciego wiersza gdy nie mieszczą się
              w jednym. Strategia `rectSortingStrategy` z @dnd-kit/sortable wspiera 2D
              wrapped layouts (vs `horizontalListSortingStrategy` która zakłada 1D linię). */}
          <SortableContext items={tabs.map((tt) => tt.id)} strategy={rectSortingStrategy}>
            <div className="flex flex-wrap items-stretch gap-1">
              {tabs.map((tab) => (
                <SortableTabPill
                  key={tab.id}
                  tab={tab}
                  selected={tab.id === selectedTabId}
                  onSelect={() => onSelectTab(tab.id)}
                  onToggleHidden={() => onToggleTabHidden(tab.id)}
                  onRename={(title) => onRenameTab(tab.id, title)}
                  onDelete={() => onDeleteTabRequest(tab.id)}
                  onEdit={() => onEditTabRequest(tab.id)}
                  allowDeleteAny={allowDeleteAny}
                  structureLocked={structureLocked}
                  readOnly={readOnly}
                />
              ))}
              {addTabHandler && (
                <button
                  type="button"
                  onClick={addTabHandler}
                  className="ml-1 inline-flex items-center gap-1 self-center rounded-md border border-dashed border-border-primary px-2 py-1 text-xs font-medium text-text-muted transition hover:border-accent-500 hover:text-accent-600"
                  data-testid="system-module-builder-add-tab-btn"
                >
                  <Plus className="h-3 w-3" />
                  {t('tenant.systemModuleBuilder.addTab')}
                </button>
              )}
            </div>
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
}

// ─── C.7: Placeholder dla tabów innych niż 'fields' ─────────────────────────
//
// F12.N T5.6: dla zakladek typu `subcollection` z `collection` ustawionym na
// slug modułu/helper-encji, dodajemy przycisk „Edytuj pola w buildery" —
// nawiguje do `/system-modules/<collection>/builder` (osobny builder pol).
// Mechanizm dziala dla:
//  - Adresy (collection='addresses', helper-encja)
//  - Konta bankowe (collection='contractor_bank_accounts', helper-encja)
//  - Kontakty (collection='contractor_contacts', user-facing modul)
// Inne subcollection (np. contractor_company_access bez seedu modulu) wciaz
// nawiguja, ale builder pokaze MODULE_NOT_FOUND — akceptowalna friction
// (admin zobaczy i wycofa sie).

// ─── AdditionalTableConfigPanel — F12S Etap C T12.S.C.4 ──────────────────────

function AdditionalTableConfigPanel({
  tab,
  parentModuleSlug,
  onChangeTableSlug,
  onChangeColumns,
  onChangeHiddenColumns,
}: {
  tab: LayoutTab;
  parentModuleSlug: string;
  /**
   * Zmiana tabeli zakładki. Brak callbacku = wybór tabeli zablokowany (podgląd, układ systemowy);
   * zmiana tabeli czyści układ kolumn, więc nie może być dostępna z klawiatury w podglądzie.
   */
  onChangeTableSlug?: (slug: string) => void;
  /**
   * Zapis układu kolumn zakładki — układ własny, tryb `full` (`undefined` = układ domyślny).
   * Bez tego i bez `onChangeHiddenColumns` edytor kolumn jest tylko do odczytu (podgląd).
   */
  onChangeColumns?: (columns: EmbeddedTableColumn[] | undefined) => void;
  /**
   * Zapis maski ukrytych kolumn — układ systemowy, tryb `hideOnly` (`undefined` = brak maski).
   * Podawany zamiast `onChangeColumns`, nigdy obok niego.
   */
  onChangeHiddenColumns?: (hiddenColumnIds: string[] | undefined) => void;
}) {
  const { t } = useTranslation();

  const tablesQuery = useAdditionalTablesList();

  const filtered = filterAdditionalTablesForModule(tablesQuery.data ?? [], parentModuleSlug);
  const tableSlug = tab.tableSlug ?? '';

  return (
    <div className="flex flex-col gap-3 rounded border border-border-primary bg-bg-secondary p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('additionalTables.tableSelector.label')}
      </p>
      <p className="text-xs text-text-secondary">{t('additionalTables.tableSelector.hint')}</p>
      {tablesQuery.isPending ? (
        <p className="text-sm text-text-muted">{t('common.loading')}</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-text-tertiary">
          {t('additionalTables.tableSelector.noTablesForModule')}
        </p>
      ) : (
        <Dropdown
          className="rounded border border-border-primary bg-bg-primary px-2 py-1 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-500"
          value={tab.tableSlug ?? ''}
          onChange={(v) => onChangeTableSlug?.(v)}
          disabled={onChangeTableSlug === undefined}
          data-testid="additional-table-selector"
          options={[
            { value: '', label: t('additionalTables.tableSelector.placeholder') },
            ...filtered.map((tbl) => ({ value: tbl.slug, label: tbl.displayName })),
          ]}
        />
      )}
      {tableSlug !== '' && (
        <AdditionalTableColumnsSection
          tableSlug={tableSlug}
          columns={tab.tableColumns}
          hiddenColumnIds={tab.hiddenColumnIds}
          onChangeColumns={onChangeColumns}
          onChangeHiddenColumns={onChangeHiddenColumns}
        />
      )}
    </div>
  );
}

/**
 * Układ kolumn tabeli dodatkowej CAŁEJ zakładki (`type='additional'`) — edytor kolumn pod
 * wyborem tabeli. Edytor dostaje `tableColumns` zakładki i maskę `hiddenColumnIds` (maska działa
 * zawsze). Układ własny (`onChangeColumns`) — tryb `full`: cały `tableColumns`, maska wtapiana
 * w układ. Układ systemowy (`onChangeHiddenColumns`) — tryb `hideOnly`: sama maska, z której
 * zapis wyprowadza nakładkę tenanta. Dane tabeli (pola, deskryptory kolumn, tytuł) daje wspólny
 * model (`useAdditionalTableColumnsModel`) — sekcja tylko wybiera, jak pokazać stany inne niż
 * „gotowe". Zapytania żyją tu (montowane dopiero po wyborze tabeli), a nie w panelu wyboru. Bez
 * obu callbacków edytor jest podglądem bez kontrolek.
 */
function AdditionalTableColumnsSection({
  tableSlug,
  columns,
  hiddenColumnIds,
  onChangeColumns,
  onChangeHiddenColumns,
}: {
  tableSlug: string;
  columns: EmbeddedTableColumn[] | undefined;
  hiddenColumnIds: string[] | undefined;
  onChangeColumns?: (columns: EmbeddedTableColumn[] | undefined) => void;
  onChangeHiddenColumns?: (hiddenColumnIds: string[] | undefined) => void;
}) {
  const { t } = useTranslation();
  const model = useAdditionalTableColumnsModel(tableSlug);
  const settings = useMemo(
    () => ({ tableColumns: columns, hiddenColumnIds }),
    [columns, hiddenColumnIds],
  );
  // Układ własny ma pierwszeństwo; sam `onChangeHiddenColumns` = układ systemowy (tylko ukrywanie).
  const hideOnly = onChangeColumns === undefined && onChangeHiddenColumns !== undefined;
  const onChange = onChangeColumns
    ? (patch: Record<string, unknown>) =>
        onChangeColumns(parseEmbeddedTableColumns(patch.tableColumns))
    : onChangeHiddenColumns
      ? (patch: Record<string, unknown>) =>
          onChangeHiddenColumns(parseEmbeddedTableHiddenColumnIds(patch.hiddenColumnIds))
      : undefined;

  let body: ReactNode;
  if (model.status === 'pending') {
    body = <p className="text-sm text-text-muted">{t('common.loading')}</p>;
  } else if (model.status === 'error') {
    body = <p className="text-sm text-danger-600">{t('additionalTables.loadError')}</p>;
  } else if (model.status === 'notFound') {
    body = (
      <p className="text-sm text-text-tertiary">
        {t('additionalTables.tableNotFound', { slug: tableSlug })}
      </p>
    );
  } else {
    body = (
      <TableLayoutEditor
        descriptors={model.descriptors}
        settings={settings}
        onChange={onChange}
        mode={hideOnly ? 'hideOnly' : 'full'}
        title={model.title ?? tableSlug}
        testIdPrefix="additional-table-tab-columns"
      />
    );
  }

  return (
    <div
      className="flex flex-col gap-2 border-t border-border-primary pt-3"
      data-testid="additional-table-columns-section"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('additionalTables.columnLayout.label')}
      </p>
      {/* Wskazówka jest instrukcją edycji — na układzie systemowym mówi, że można tylko ukrywać,
          a bez możliwości zapisu (podgląd) zastępuje ją krótka informacja, że to podgląd. */}
      <p className="text-xs text-text-secondary" data-testid="additional-table-columns-hint">
        {onChangeColumns
          ? t('additionalTables.columnLayout.hint')
          : hideOnly
            ? t('additionalTables.columnLayout.hideOnlyHint')
            : t('additionalTables.columnLayout.readOnlyHint')}
      </p>
      {body}
    </div>
  );
}

// ─── AdditionalQueryConfigPanel — F22 (widoki dodatkowe), mirror AdditionalTableConfigPanel ──

/**
 * Panel wyboru KTÓREGO widoku SQL (z katalogu Ustawienia → „Widoki dodatkowe")
 * dotyczy zakładka typu 'additional_query'. Analogiczny do `AdditionalTableConfigPanel`
 * (tabele dodatkowe): lista pobierana BEZ filtra serwerowego, filtrowana klient-side
 * przez `filterAdditionalTablesForModule` (funkcja generyczna po `parentEntity`,
 * reużyta 1:1 — widoki dodatkowe mają dokładnie ten sam kształt `parentEntity`).
 *
 * Pod wyborem widoku stoi edytor układu kolumn widoku (`AdditionalQueryColumnsSection`) — te same
 * trzy tryby co przy tabeli dodatkowej: układ własny (`onChangeColumns`, tryb `full`), układ
 * systemowy (`onChangeHiddenColumns`, tryb `hideOnly`) i podgląd bez zapisu.
 */
function AdditionalQueryConfigPanel({
  tab,
  parentModuleSlug,
  onChangeViewSlug,
  onChangeColumns,
  onChangeHiddenColumns,
}: {
  tab: LayoutTab;
  parentModuleSlug: string;
  /**
   * Zmiana widoku zakładki. Brak callbacku = wybór widoku zablokowany (podgląd, układ systemowy,
   * zakładka tylko do odczytu); zmiana widoku czyści układ kolumn, więc nie może być dostępna
   * z klawiatury w podglądzie.
   */
  onChangeViewSlug?: (slug: string) => void;
  /**
   * Zapis układu kolumn zakładki — układ własny, tryb `full` (`undefined` = układ domyślny).
   * Bez tego i bez `onChangeHiddenColumns` edytor kolumn jest tylko do odczytu (podgląd).
   */
  onChangeColumns?: (columns: EmbeddedTableColumn[] | undefined) => void;
  /**
   * Zapis maski ukrytych kolumn — układ systemowy, tryb `hideOnly` (`undefined` = brak maski).
   * Podawany zamiast `onChangeColumns`, nigdy obok niego.
   */
  onChangeHiddenColumns?: (hiddenColumnIds: string[] | undefined) => void;
}) {
  const { t } = useTranslation();

  const viewsQuery = useAdditionalViewsList();
  const filtered = filterAdditionalTablesForModule(viewsQuery.data ?? [], parentModuleSlug);
  const viewSlug = tab.viewSlug ?? '';

  return (
    <div className="flex flex-col gap-3 rounded border border-border-primary bg-bg-secondary p-4">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('additionalViews.tableSelector.label')}
      </p>
      <p className="text-xs text-text-secondary">{t('additionalViews.tableSelector.hint')}</p>
      {viewsQuery.isPending ? (
        <p className="text-sm text-text-muted">{t('common.loading')}</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-text-tertiary">
          {t('additionalViews.tableSelector.noViewsForModule')}
        </p>
      ) : (
        <Dropdown
          value={tab.viewSlug ?? ''}
          onChange={(v) => onChangeViewSlug?.(v)}
          disabled={onChangeViewSlug === undefined}
          data-testid="additional-query-view-selector"
          options={[
            { value: '', label: t('additionalViews.tableSelector.placeholder') },
            ...filtered.map((v) => ({ value: v.slug, label: v.displayName })),
          ]}
        />
      )}
      {viewSlug !== '' && (
        <AdditionalQueryColumnsSection
          viewSlug={viewSlug}
          columns={tab.tableColumns}
          hiddenColumnIds={tab.hiddenColumnIds}
          onChangeColumns={onChangeColumns}
          onChangeHiddenColumns={onChangeHiddenColumns}
        />
      )}
    </div>
  );
}

/**
 * Układ kolumn widoku SQL CAŁEJ zakładki (`type='additional_query'`) — edytor kolumn pod wyborem
 * widoku; odpowiednik `AdditionalTableColumnsSection`. Edytor dostaje `tableColumns` zakładki i
 * maskę `hiddenColumnIds` (maska działa zawsze). Układ własny (`onChangeColumns`) — tryb `full`:
 * cały `tableColumns`, maska wtapiana w układ. Układ systemowy (`onChangeHiddenColumns`) — tryb
 * `hideOnly`: sama maska, z której zapis wyprowadza nakładkę tenanta. Kolumny widoku znane są
 * dopiero po uruchomieniu zapytania (model `useAdditionalViewColumnsModel`), więc gdy baza je
 * odrzuci, sekcja pokazuje powód z serwera. Zapytania żyją tu (montowane dopiero po wyborze
 * widoku), a nie w panelu wyboru. Bez obu callbacków edytor jest podglądem bez kontrolek.
 */
function AdditionalQueryColumnsSection({
  viewSlug,
  columns,
  hiddenColumnIds,
  onChangeColumns,
  onChangeHiddenColumns,
}: {
  viewSlug: string;
  columns: EmbeddedTableColumn[] | undefined;
  hiddenColumnIds: string[] | undefined;
  onChangeColumns?: (columns: EmbeddedTableColumn[] | undefined) => void;
  onChangeHiddenColumns?: (hiddenColumnIds: string[] | undefined) => void;
}) {
  const { t } = useTranslation();
  const model = useAdditionalViewColumnsModel(viewSlug);
  const settings = useMemo(
    () => ({ tableColumns: columns, hiddenColumnIds }),
    [columns, hiddenColumnIds],
  );
  // Układ własny ma pierwszeństwo; sam `onChangeHiddenColumns` = układ systemowy (tylko ukrywanie).
  const hideOnly = onChangeColumns === undefined && onChangeHiddenColumns !== undefined;
  const onChange = onChangeColumns
    ? (patch: Record<string, unknown>) =>
        onChangeColumns(parseEmbeddedTableColumns(patch.tableColumns))
    : onChangeHiddenColumns
      ? (patch: Record<string, unknown>) =>
          onChangeHiddenColumns(parseEmbeddedTableHiddenColumnIds(patch.hiddenColumnIds))
      : undefined;

  let body: ReactNode;
  if (model.status === 'pending') {
    body = <p className="text-sm text-text-muted">{t('common.loading')}</p>;
  } else if (model.status === 'error') {
    // Powód od bazy (np. „division by zero") widzi admin — kreator budują tylko administratorzy.
    body = (
      <p className="text-sm text-danger-600" data-testid="additional-query-columns-error">
        {model.errorReason !== undefined
          ? t('additionalViews.columnsLoadError', { reason: model.errorReason })
          : t('additionalViews.columnsLoadErrorNoReason')}
      </p>
    );
  } else if (model.status === 'notFound') {
    body = (
      <p className="text-sm text-text-tertiary">
        {t('additionalViews.grid.viewNotFound', { slug: viewSlug })}
      </p>
    );
  } else {
    body = (
      <TableLayoutEditor
        descriptors={model.descriptors}
        settings={settings}
        onChange={onChange}
        mode={hideOnly ? 'hideOnly' : 'full'}
        title={model.title ?? viewSlug}
        testIdPrefix="additional-query-tab-columns"
      />
    );
  }

  return (
    <div
      className="flex flex-col gap-2 border-t border-border-primary pt-3"
      data-testid="additional-query-columns-section"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('additionalViews.columnLayout.label')}
      </p>
      {/* Wskazówka jest instrukcją edycji — na układzie systemowym mówi, że można tylko ukrywać,
          a bez możliwości zapisu (podgląd) zastępuje ją krótka informacja, że to podgląd. */}
      <p className="text-xs text-text-secondary" data-testid="additional-query-columns-hint">
        {onChangeColumns
          ? t('additionalViews.columnLayout.hint')
          : hideOnly
            ? t('additionalViews.columnLayout.hideOnlyHint')
            : t('additionalViews.columnLayout.readOnlyHint')}
      </p>
      {body}
    </div>
  );
}

// ─── NonFieldsTabPlaceholder ─────────────────────────────────────────────────

function NonFieldsTabPlaceholder({
  tab,
  parentModuleSlug,
}: {
  tab: LayoutTab;
  /**
   * F12.N T5.8: slug modułu macierzystego (np. `contractors`). Potrzebny do
   * zbudowania URL'a do edytora drzewka kategorii dla zakładek typu `tree_picker`
   * — drzewko żyje pod `/system-modules/<parent>/settings?tab=categories`.
   */
  parentModuleSlug: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // F12.N T5.6b: dla helper-encji canvas renderuje sekcje inline (FieldsTabContent
  // w helper-mode) — placeholder w ogóle się nie renderuje. Przycisk navigujący
  // zostaje dla pełnoprawnych modułów jak `contractor_contacts` (osobna strona
  // szczegółów + osobny builder).
  //
  // F12.N T5.8: dla zakładek typu `tree_picker` (np. „Kategorie") drzewko
  // żyje w settings macierzystego modułu (`/system-modules/<parent>/settings?
  // tab=categories`) — admin może je tam edytować drag-droppem hierarchii
  // (react-arborist, gotowe z F11 T11.5). Inny URL niż subcollection.
  const canOpenSubBuilder = tab.type === 'subcollection' && !!tab.collection;
  const canOpenTreeEditor = tab.type === 'tree_picker';
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 p-12 text-center"
      data-testid={`system-module-builder-tab-content-${tab.type}`}
    >
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-accent-500/10 text-accent-600">
        <LayoutIcon className="h-6 w-6" />
      </div>
      <h3 className="text-lg font-semibold text-text-primary">{tab.title}</h3>
      <p className="max-w-md text-sm text-text-tertiary">
        {t(`tenant.systemModuleBuilder.tabTypePlaceholder.${tab.type}`)}
      </p>
      {tab.collection && (
        <p className="text-xs text-text-muted">
          {t('tenant.systemModuleBuilder.collectionLabel', { collection: tab.collection })}
        </p>
      )}
      {canOpenSubBuilder && (
        <button
          type="button"
          onClick={() => navigate(`/system-modules/${tab.collection}/builder`)}
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
          data-testid={`system-module-builder-open-sub-builder-${tab.collection}`}
        >
          <LayoutIcon className="h-4 w-4" />
          {t('tenant.systemModuleBuilder.openSubBuilder')}
        </button>
      )}
      {canOpenTreeEditor && (
        <button
          type="button"
          onClick={() => navigate(`/system-modules/${parentModuleSlug}/settings?tab=categories`)}
          className="mt-2 inline-flex items-center gap-2 rounded-lg bg-accent-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition hover:bg-accent-700 focus:outline-none focus:ring-2 focus:ring-accent-500 focus:ring-offset-2"
          data-testid={`system-module-builder-open-category-tree-editor`}
        >
          <LayoutIcon className="h-4 w-4" />
          {t('tenant.systemModuleBuilder.openCategoryTreeEditor', {
            defaultValue: 'Edytuj drzewko kategorii',
          })}
        </button>
      )}
    </div>
  );
}

// ─── F12.N E2.T1: panel extras dla zakładek non-fields ──────────────────────
//
// Renderuje listę klocków `tab.extras` dodanych przez admina przez kliknięcie
// elementu z palety. Każdy klocek renderowany przez `LayoutElementRegistry`
// w mode='builder' (preview), z opcją usunięcia. Empty state z hintem.

function NonFieldsExtrasPanel({
  tab,
  onRemoveExtra,
  onUpdateExtraSettings,
  systemHideOnly = false,
  selectedExtraId = null,
  onSelectExtra,
}: {
  tab: LayoutTab;
  onRemoveExtra: (elementId: string) => void;
  /** Inline zapis settings bloku extra (ikonka oka w formatkach — adres/Kontakty/…). */
  onUpdateExtraSettings?: (elementId: string, patch: Record<string, unknown>) => void;
  /** Systemowy layout: formatka ukrywa kolumny (oko), ale sam blok jest nieusuwalny. */
  systemHideOnly?: boolean;
  /** Zaznaczony klocek (z panelem właściwości w prawym panelu). */
  selectedExtraId?: string | null;
  /** Brak callbacku = selekcja wyłączona (layout systemowy / podgląd). Przycisk „Właściwości”
   * na zaznaczonym klocku wywołuje `onSelectExtra(null)` (odznaczenie). */
  onSelectExtra?: (elementId: string | null) => void;
}) {
  const { t } = useTranslation();
  const extras = tab.extras ?? [];

  if (extras.length === 0) {
    return (
      <div className="mx-6 mb-6 rounded-lg border border-dashed border-border-secondary p-6 text-center">
        <p className="text-xs text-text-muted">
          {t('tenant.systemModuleBuilder.nonFieldsExtras.empty', {
            defaultValue:
              'Kliknij klocek z palety po lewej żeby dodać go do tej zakładki (nagłówek, alert, separator itp.).',
          })}
        </p>
      </div>
    );
  }

  return (
    <div
      className="mx-6 mb-6 space-y-2 rounded-lg border border-dashed border-border-secondary p-4"
      data-testid="non-fields-extras-panel"
    >
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('tenant.systemModuleBuilder.nonFieldsExtras.heading', {
          defaultValue: 'Dodatkowe klocki',
        })}{' '}
        ({extras.length})
      </p>
      {extras.map((el) => {
        const preview = tryRenderLayoutElement({
          element: {
            id: el.id,
            type: el.type,
            settings: (el.settings ?? {}) as Record<string, unknown>,
          },
          children: [],
          mode: 'builder',
          // Systemowy layout: formatka pokazuje tylko oko (bez reorderu), pierce inertu.
          systemHideOnly,
          onSettingsChange: onUpdateExtraSettings
            ? (patch) => onUpdateExtraSettings(el.id, patch)
            : undefined,
        });
        // Zaznaczalny tylko klocek z whitelisty, który ma `propertiesEditor`, i tylko gdy
        // rodzic dopuszcza selekcję (własny layout, nie podgląd).
        const selectable = Boolean(onSelectExtra) && extraHasPropertiesPanel(el.type);
        const isSelected = selectable && el.id === selectedExtraId;
        const toggleSelect = () => onSelectExtra?.(isSelected ? null : el.id);
        return (
          <div
            key={el.id}
            className={
              'group relative rounded border border-border-primary bg-bg-primary p-3' +
              (isSelected ? ' ring-2 ring-accent-500' : '')
            }
            data-testid={`non-fields-extra-${el.id}`}
          >
            {/* Kontrolki karty: „Właściwości” (tylko klocek zaznaczalny) i usuń. Widoczne po
                najechaniu, przy fokusie klawiatury w karcie oraz zawsze na zaznaczonej karcie. */}
            <div
              className={
                'absolute right-2 top-2 flex gap-1 transition group-hover:opacity-100 group-focus-within:opacity-100 ' +
                (isSelected ? 'opacity-100' : 'opacity-0')
              }
            >
              {selectable && (
                <IconButton
                  size="sm"
                  aria-pressed={isSelected}
                  onClick={toggleSelect}
                  aria-label={t('tenant.systemModuleBuilder.props.elementProps')}
                  title={t('tenant.systemModuleBuilder.props.elementProps')}
                  data-testid={`non-fields-extra-${el.id}-properties`}
                >
                  <SlidersHorizontal />
                </IconButton>
              )}
              {/* Usuwanie całego bloku — tylko na WŁASNYM layoucie. Na systemowym formatka
                  jest nieusuwalna (wolno tylko ukrywać jej kolumny/pola ikonką oka). */}
              {!systemHideOnly && (
                <IconButton
                  variant="danger"
                  size="sm"
                  onClick={() => onRemoveExtra(el.id)}
                  aria-label={t('common.delete')}
                  title={t('common.delete')}
                  data-testid={`non-fields-extra-${el.id}-remove`}
                >
                  <Trash2 />
                </IconButton>
              )}
            </div>
            <div>
              {preview ?? (
                <p className="text-xs text-text-tertiary">
                  {t('tenant.systemModuleBuilder.unknownElement', { type: el.type })}
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Panel właściwości zaznaczonego klocka `tab.extras` (zakładki NON-FIELDS) — pod
 * `TabPropertiesPanel` w prawym panelu. Renderuje wyłącznie `propertiesEditor` typu
 * (bez zakładek Styl/Zaawansowane); brak edytora w definicji = nic nie renderujemy.
 */
function ExtraElementProperties({
  element,
  onChange,
  moduleSlug,
}: {
  element: LayoutElement;
  onChange: (patch: Record<string, unknown>) => void;
  moduleSlug?: string;
}) {
  const { t } = useTranslation();
  const def = LayoutElementRegistry.get(element.type);
  if (!def?.propertiesEditor) return null;
  return (
    <div className="mt-4 space-y-3" data-testid="system-module-builder-extra-properties">
      <p className="text-xs font-semibold uppercase tracking-wide text-text-muted">
        {t('tenant.systemModuleBuilder.props.elementProps')}
      </p>
      {def.propertiesEditor({ settings: element.settings ?? {}, onChange, moduleSlug })}
    </div>
  );
}

/**
 * EmbeddedReadOnlyTabPlaceholder — Etap 2 kartoteki (2026-06-24).
 *
 * Renderowany gdy builder jest w trybie `isAdminEmbedded` (super admin → edytor
 * modułu) i aktywna zakładka to typ read-only (tree_picker / satellite /
 * additional / panel / audit_log / approvals / subcollection z readOnlyTabCollections).
 *
 * Zamiast żywego komponentu (który strzela w API tenanta i crashuje) pokazuje
 * neutralną notę informacyjną. Zero zapytań API.
 *
 * Wyjątek: w kreatorze osadzonym w zakresie TENANTA (`builderHasTenantData`) cała zakładka
 * z tabelą dodatkową (`satellite` / `additional`) nie dostaje tej zaślepki, tylko panel
 * z wyborem tabeli i edytorem kolumn — tylko do odczytu, gdy host oznacza typ zakładki
 * jako tylko do odczytu. Zaślepka zostaje więc dla niej wyłącznie w panelu ITE.
 */
function EmbeddedReadOnlyTabPlaceholder({ tab }: { tab: LayoutTab }) {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-col items-center justify-center gap-3 p-12 text-center"
      data-testid={`system-module-builder-tab-content-${tab.type}-embedded-readonly`}
    >
      <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-bg-secondary text-text-tertiary">
        <LayoutIcon className="h-6 w-6" />
      </div>
      <h3 className="text-base font-semibold text-text-secondary">{tab.title}</h3>
      <p className="max-w-sm text-sm text-text-tertiary">
        {t('tenant.systemModuleBuilder.embeddedReadOnlyTab.note')}
      </p>
    </div>
  );
}

// ─── Fields tab content (canvas + paleta + properties) — z C.4, niezmienione ──

interface FieldsTabContentProps {
  sections: LayoutSection[];
  availableFields: SystemModuleField[];
  usedFields: SystemModuleField[];
  fieldById: Map<string, SystemModuleField>;
  selectedSectionId: string | null;
  onSelectSection: (id: string | null) => void;
  onAddSection: () => void;
  onRemoveSection: (id: string) => void;
  /** F12.N E2.T4: duplicate sekcji (klonuje strukturę z nowymi UUID).
      Opcjonalne — helper-inline nie wspiera duplicate (osobny scope). */
  onDuplicateSection?: (id: string) => void;
  onUpdateSection: (id: string, patch: Partial<LayoutSection>) => void;
  onAddFieldToSection: (fieldId: string) => void;
  onRemoveField: (sectionId: string, elementId: string) => void;
  /** F12.N E3.T1: CRUD paneli akordeonu (każdy panel ma własny worek dzieci). */
  onAddAccordionItem: (accordionId: string) => void;
  onUpdateAccordionItemTitle: (accordionId: string, panelId: string, newTitle: string) => void;
  onRemoveAccordionItem: (accordionId: string, panelId: string) => void;
  /** Etap 3 T2: CRUD kroków steppera/wizarda (wspólny edytor, decyzja UX 2026-05-17). */
  onAddStep: (stepperId: string) => void;
  onUpdateStepTitle: (stepperId: string, stepId: string, newTitle: string) => void;
  onUpdateStepIcon: (stepperId: string, stepId: string, newIcon: string) => void;
  onRemoveStep: (stepperId: string, stepId: string) => void;
  /**
   * F12.N E3.T1 (Sub-tura 2c): drop pola/elementu z palety na dowolny container.
   * `containerId` = sekcja lub panel akordeonu (parytetowo). Wstawia child przez
   * `insertIntoContainer` w drzewie — drop trafia DOKŁADNIE tam gdzie kursor.
   * Click-to-add (button w palecie) wciąż używa `onAddFieldToSection` / `onAddLayoutElement`
   * z `selectedSectionId` (legacy path).
   */
  onAddFromPaletteToContainer?: (
    kind: 'field' | 'layout-element',
    payload: string,
    containerId: string,
  ) => void;
  /** F12.N D3: czysta sygnatura — usePaletteDnD rozpakowuje DragEndEvent. */
  onSectionReorder: (activeId: string, overId: string) => void;
  /**
   * Pakiet B Sub-2/3: sortowanie/przenoszenie elementu między kontenerami.
   * Same-container gdy source===target; cross-container gdy różne.
   */
  onElementReorder: (
    sourceContainerId: string,
    activeId: string,
    targetContainerId: string,
    overId: string,
  ) => void;
  /** Pakiet B Sub-2: sortowanie paneli akordeonu w obrębie jednego akordeonu. */
  onAccordionPanelReorder: (accordionId: string, activeId: string, overId: string) => void;
  /** Etap 3 T2: sortowanie kroków steppera/wizarda w obrębie jednego steppera. */
  onStepReorder: (stepperId: string, activeId: string, overId: string) => void;
  selectedTab: LayoutTab;
  roleTemplates: RoleTemplate[];
  onUpdateTab: (patch: Partial<LayoutTab>) => void;
  /** Krok A: left-panel tab switcher (Pola vs Elementy layoutu). */
  leftPanelTab: 'fields' | 'elements';
  setLeftPanelTab: (tab: 'fields' | 'elements') => void;
  /** Klik „+ Dodaj pole" → otwiera modal dodawania pola w komponencie root.
   *  `undefined` = dodawanie pól niedostępne w tym trybie → przycisk ukryty. */
  onAddFieldClick?: () => void;
  /** Krok B: klik elementu w palecie Elementy → wstawia do aktywnej sekcji. */
  onAddLayoutElement: (type: string) => void;
  /** E2.T2a: pasek zakładek wewnątrz canvasu (pierwszy element nad sekcjami). */
  tabsBarSlot?: React.ReactNode;
  /**
   * F12.M Etap E (PR 2): pasek wyboru kategorii dla edytora `field_overrides`
   * (ADR-0030). Renderowany nad `tabsBarSlot`. Null = brak paska (np. testy lub
   * widoki nie wspierające overrides).
   */
  /**
   * F12.N T5.6b: opcjonalny banner informujący że admin edytuje pola encji
   * pomocniczej (np. Adresy) w trybie inline. Renderowany nad sekcjami w canvasie.
   */
  helperInlineBanner?: React.ReactNode;
  /** Tryb szablonu — ukrywa widoczność wg ról w TabPropertiesPanel (byt tenanta). */
  hideRoleVisibility?: boolean;
  /**
   * Slug modułu systemowego którego layout edytuje builder (np. `purchase_invoices`) —
   * przekazywany do `LayoutElementsPalette` do filtrowania elementów ze scopingiem
   * (`allowedModules`, patrz `document_items_view`). W trybie edycji helper-encji
   * (`activeHelperCollection`) to slug helper-encji, NIE macierzystego modułu —
   * paleta pokazuje elementy scope'owane do TEJ encji, którą admin faktycznie edytuje.
   */
  moduleSlug?: string;
  /**
   * Selekcja bloku treści (non-field) w canvasie — niezależna od `selectedSectionId`
   * (patrz komentarz przy `updateElementSettings` w komponencie root). Opcjonalne —
   * scope helper-inline nie wspiera (drugi call site `FieldsTabContent` ich nie
   * przekazuje), `document_items_view` i tak nie pojawia się tam w palecie
   * (scoping `allowedModules` do 6 modułów klasy Dokumenty).
   */
  selectedElementId?: string | null;
  onSelectElement?: (id: string | null) => void;
  onUpdateElementSettings?: (elementId: string, patch: Record<string, unknown>) => void;
  /**
   * Layout SYSTEMOWY: canvas jest inertny (`pointerEvents:none`), a dozwolone jest
   * WYŁĄCZNIE ukrywanie. Włącza ikonkę oka na klockach pól i formatkach (pierce inertu),
   * chowa uchwyty/usuwanie. `false`/brak = własny layout (pełna edycja).
   */
  systemHideOnly?: boolean;
  /** Ukryj/pokaż zwykłe pole na systemowym layoucie (ikonka oka na klocku pola). */
  onToggleFieldHidden?: (elementId: string) => void;
  /**
   * T2: elementy view-level (page-header/highlight) — rodzeństwo `tabs`, renderowane
   * nad paskiem zakładek (przez `tabsBarSlot`). Przekazywane tylko w macierzystym
   * scope; helper-inline ich nie ma (default []). Używane do wyszukania zaznaczonego
   * elementu (panel właściwości) — sam render slotu robi parent w `tabsBarSlot`.
   */
  viewLevelElements?: LayoutElement[];
  /** T2: typy do wykluczenia z palety Elementy (np. page-header gdy już istnieje singleton). */
  excludeElementTypes?: string[];
}

// ── T2: pasek elementów VIEW-LEVEL (page-header/highlight) nad zakładkami ──────
interface ViewLevelBarProps {
  elements: LayoutElement[];
  selectedElementId?: string | null;
  onSelectElement?: (id: string) => void;
  onRemoveElement?: (id: string) => void;
  /**
   * Renderuje dziecko kontenera view-level (np. pole/element upuszczony do środka
   * `highlight`) — parytet z `renderContainerChild` w `SortableSectionView`. Wymagany
   * tylko gdy wśród `elements` jest typ `isContainer===true` (dziś: highlight).
   */
  renderChild?: (child: TreeLayoutElement, containerId: string) => ReactNode;
}

function ViewLevelBar({
  elements,
  selectedElementId,
  onSelectElement,
  onRemoveElement,
  renderChild,
}: ViewLevelBarProps) {
  const { t } = useTranslation();
  return (
    <div className="mb-3 space-y-2" data-testid="system-module-builder-view-level-bar">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-text-muted">
        {t('tenant.systemModuleBuilder.viewLevel.sectionTitle')}
      </p>
      {elements.map((el) => {
        // Highlight (i przyszłe view-level kontenery) — prawdziwy drop target dla
        // pól/elementów z palety, parytet z generyczną gałęzią kontenerów w sekcji
        // (SortableSectionView ~5392). Leaf view-level (page-header) idzie starą ścieżką.
        const isContainer = LayoutElementRegistry.get(el.type)?.isContainer === true;
        const preview =
          isContainer && renderChild ? (
            <ContainerInCanvas
              element={el as unknown as TreeLayoutElement}
              renderChild={renderChild}
            />
          ) : (
            (tryRenderLayoutElement({
              element: {
                id: el.id,
                type: el.type,
                settings: (el.settings ?? {}) as Record<string, unknown>,
              },
              children: [],
              mode: 'builder',
            }) ?? (
              <p className="text-xs text-text-tertiary">
                {t('tenant.systemModuleBuilder.unknownElement', { type: el.type })}
              </p>
            ))
          );
        return (
          <div
            key={el.id}
            className={
              'group/vl relative rounded ' +
              (onSelectElement ? 'cursor-pointer ' : '') +
              (el.id === selectedElementId ? 'ring-2 ring-accent-500' : '')
            }
            onClick={
              onSelectElement
                ? (e) => {
                    e.stopPropagation();
                    onSelectElement(el.id);
                  }
                : undefined
            }
            data-testid={`system-module-builder-view-level-${el.id}`}
          >
            {preview}
            {onRemoveElement && (
              <IconButton
                variant="danger"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveElement(el.id);
                }}
                aria-label={t('tenant.systemModuleBuilder.viewLevel.remove')}
                className="absolute right-1 top-1 opacity-0 transition group-hover/vl:opacity-100"
                data-testid={`system-module-builder-view-level-${el.id}-remove`}
              >
                <X className="h-3 w-3" />
              </IconButton>
            )}
          </div>
        );
      })}
    </div>
  );
}

function FieldsTabContent({
  sections,
  availableFields,
  usedFields,
  fieldById,
  selectedSectionId,
  onSelectSection,
  onAddSection,
  onRemoveSection,
  onDuplicateSection,
  onUpdateSection,
  onAddFieldToSection,
  onRemoveField,
  onAddAccordionItem,
  onUpdateAccordionItemTitle,
  onRemoveAccordionItem,
  onAddStep,
  onUpdateStepTitle,
  onUpdateStepIcon,
  onRemoveStep,
  onAddFromPaletteToContainer,
  onSectionReorder,
  onElementReorder,
  onAccordionPanelReorder,
  onStepReorder,
  selectedTab,
  roleTemplates,
  onUpdateTab,
  leftPanelTab,
  setLeftPanelTab,
  onAddFieldClick,
  onAddLayoutElement,
  tabsBarSlot,
  helperInlineBanner,
  hideRoleVisibility = false,
  moduleSlug,
  selectedElementId,
  onSelectElement,
  onUpdateElementSettings,
  systemHideOnly = false,
  onToggleFieldHidden,
  viewLevelElements = [],
  excludeElementTypes,
}: FieldsTabContentProps) {
  const { t } = useTranslation();
  // Wyszukiwarka pól (parytet z paletą kartotek w MenuPalette) — filtruje sekcje
  // „Dostępne" i „Na canvas" po nazwie/slugu z normalizeSearch.
  const [fieldQuery, setFieldQuery] = useState('');
  // Ręczne poszerzanie panelu właściwości (jak menu) — przeciągnięcie splittera po lewej.
  const [propsWidth, setPropsWidth] = useState(288);
  const startPropsResize = (e: { preventDefault: () => void; clientX: number }) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = propsWidth;
    const onMove = (ev: MouseEvent) => {
      setPropsWidth(Math.min(640, Math.max(240, startW + (startX - ev.clientX))));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const fieldQ = normalizeSearch(fieldQuery.trim());
  const fieldMatches = (f: SystemModuleField) =>
    !fieldQ || normalizeSearch(`${f.name} ${f.slug}`).includes(fieldQ);
  const filteredAvailableFields = availableFields.filter(fieldMatches);
  const filteredUsedFields = usedFields.filter(fieldMatches);
  const selectedSection = sections.find((s) => s.id === selectedSectionId) ?? null;
  // Blok może siedzieć w DOWOLNEJ sekcji (klik na blok robi stopPropagation, patrz
  // `LayoutElementBlock` — `selectedElementId` NIE musi zgadzać się z `selectedSectionId`),
  // więc szukamy po wszystkich sekcjach zakładki, nie tylko `selectedSection`.
  const selectedElement = selectedElementId
    ? // T2: najpierw sprawdź elementy view-level (rodzeństwo `tabs`), potem sekcje.
      (viewLevelElements.find((el) => el.id === selectedElementId) ??
      (sections
        .map((s) => findInTree(s.elements, selectedElementId))
        .find((found) => found && 'type' in found) as LayoutElement | undefined) ??
      null)
    : null;

  // F12.N D3 — jeden top-level DndContext wokół palety + canvasu. Paletę
  // (`PaletteFieldButton`, `LayoutElementsPalette`) używa `useDraggable` z
  // `data: PaletteDragData`; sekcje na canvasie `useSortable` z `data: SectionDragData`
  // (target='section'). Dispatcher rozpoznaje 3 ścieżki: palette → onAddFromPalette,
  // section → onSectionReorder, element → noop (elementy w SystemBuilder nie są sortable).
  const { sensors: paletteSensors, onDragEnd: paletteOnDragEnd } = usePaletteDnD({
    onAddFromPalette: (kind, payload, sectionId) => {
      // F12.N E3.T1 Sub-tura 2c: drop trafia DOKŁADNIE do kontenera pod kursorem
      // (sekcja lub panel akordeonu). Gdy parent dostarczył nowy callback — używamy go.
      // Fallback: stary path z `selectedSectionId` (legacy, np. helper-inline scope).
      if (onAddFromPaletteToContainer) {
        onAddFromPaletteToContainer(kind, payload, sectionId);
        return;
      }
      if (kind === 'field') onAddFieldToSection(payload);
      else onAddLayoutElement(payload);
    },
    onSectionReorder,
    onElementReorder,
    onAccordionPanelReorder,
    onStepReorder,
  });

  // F12.N E3.T1 + pakiet B Sub-2/3 — DragOverlay pokazuje „ducha" jadącego za kursorem
  // dla WSZYSTKICH źródeł drag (palette, pole/element w canvasie, sekcja, panel akordeonu).
  // Bez ghost'a sortable klocek tylko reorganizuje sąsiedztwo (transform + opacity), ale
  // user nie widzi co przeciąga — wygląda jakby gest się gdzieś gubił. Pakiet B fix 2026-05-17.
  type ActiveDragInfo =
    | { kind: 'palette'; data: PaletteDragData }
    | { kind: 'element'; id: string }
    | { kind: 'section'; id: string }
    | { kind: 'accordion-panel'; id: string }
    | { kind: 'stepper-step'; id: string };
  const [activeDrag, setActiveDrag] = useState<ActiveDragInfo | null>(null);
  const onDragStart = (event: DragStartEvent) => {
    const data = event.active.data.current as { source?: string } | undefined;
    if (!data?.source) return;
    if (data.source === 'palette') {
      setActiveDrag({ kind: 'palette', data: data as unknown as PaletteDragData });
    } else if (data.source === 'element') {
      setActiveDrag({ kind: 'element', id: String(event.active.id) });
    } else if (data.source === 'section') {
      setActiveDrag({ kind: 'section', id: String(event.active.id) });
    } else if (data.source === 'accordion-panel') {
      setActiveDrag({ kind: 'accordion-panel', id: String(event.active.id) });
    } else if (data.source === 'stepper-step') {
      setActiveDrag({ kind: 'stepper-step', id: String(event.active.id) });
    }
  };
  const onDragEndWrapped = (event: DragEndEvent) => {
    paletteOnDragEnd(event);
    setActiveDrag(null);
  };

  return (
    <DndContext
      sensors={paletteSensors}
      collisionDetection={builderCanvasCollisionDetection}
      onDragStart={onDragStart}
      onDragEnd={onDragEndWrapped}
      onDragCancel={() => setActiveDrag(null)}
    >
      {/* Left: paleta pól / elementów layoutu (Krok A — parytet z ModuleBuilderPage) */}
      <aside
        className="w-64 shrink-0 overflow-y-auto border-r border-border-primary bg-bg-primary p-4"
        data-testid="system-module-builder-palette"
      >
        {/* Tab switcher: Pola | Elementy */}
        <div
          className="mb-3 flex gap-1 border-b border-border-primary"
          role="tablist"
          aria-label={t('tenant.systemModuleBuilder.leftPanel.tabsAria')}
        >
          <button
            type="button"
            role="tab"
            aria-selected={leftPanelTab === 'fields'}
            onClick={() => setLeftPanelTab('fields')}
            className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
              leftPanelTab === 'fields'
                ? 'border-b-2 border-accent-500 text-accent-600'
                : 'text-text-secondary hover:text-text-primary'
            }`}
            data-testid="system-module-builder-left-tab-fields"
          >
            {t('tenant.systemModuleBuilder.leftPanel.tabs.fields')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={leftPanelTab === 'elements'}
            onClick={() => setLeftPanelTab('elements')}
            className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${
              leftPanelTab === 'elements'
                ? 'border-b-2 border-accent-500 text-accent-600'
                : 'text-text-secondary hover:text-text-primary'
            }`}
            data-testid="system-module-builder-left-tab-elements"
          >
            {t('tenant.systemModuleBuilder.leftPanel.tabs.elements')}
          </button>
        </div>

        {leftPanelTab === 'fields' ? (
          <>
            {/* Header z przyciskiem Dodaj */}
            <div className="mb-2 flex items-center justify-between">
              <h2 className="text-sm font-semibold text-text-primary">
                {t('tenant.systemModuleBuilder.fieldsTitle')}
              </h2>
              {onAddFieldClick && (
                <Button
                  variant="ghost"
                  size="sm"
                  leftIcon={<Plus className="h-3 w-3" />}
                  onClick={onAddFieldClick}
                  data-testid="system-module-builder-add-field-btn"
                >
                  {t('tenant.systemModuleBuilder.leftPanel.addFieldBtn')}
                </Button>
              )}
            </div>

            {/* Wyszukiwarka pól (widoczna gdy w kartotece są jakiekolwiek pola) */}
            {(availableFields.length > 0 || usedFields.length > 0) && (
              <PaletteSearchInput
                value={fieldQuery}
                onChange={setFieldQuery}
                placeholder={t('common.layoutPalette.searchFields')}
                testId="system-module-builder-fields-search"
              />
            )}

            {/* Sekcja: Dostępne (do dodania na canvas) */}
            {filteredAvailableFields.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] font-medium uppercase tracking-wider text-text-muted">
                  {t('tenant.systemModuleBuilder.leftPanel.availableTitle')}
                </p>
                {filteredAvailableFields.map((field) => (
                  <PaletteFieldButton
                    key={field.id}
                    field={field}
                    onClick={() => onAddFieldToSection(field.id)}
                  />
                ))}
              </div>
            )}

            {/* Separator + sekcja: Na canvas (disabled) */}
            {filteredUsedFields.length > 0 && filteredAvailableFields.length > 0 && (
              <hr className="my-3 border-border-primary" />
            )}
            {filteredUsedFields.length > 0 && (
              <div className="space-y-1">
                <p className="text-[10px] font-medium uppercase tracking-wider text-text-muted">
                  {t('tenant.systemModuleBuilder.leftPanel.onCanvasTitle')}
                </p>
                {filteredUsedFields.map((field) => (
                  <PaletteFieldButton key={field.id} field={field} disabled />
                ))}
              </div>
            )}

            {/* Brak trafień wyszukiwania (są pola, ale filtr je odrzucił) */}
            {(availableFields.length > 0 || usedFields.length > 0) &&
              filteredAvailableFields.length === 0 &&
              filteredUsedFields.length === 0 && (
                <p className="text-xs text-text-tertiary">{t('common.layoutPalette.noFields')}</p>
              )}

            {/* Pusta paleta */}
            {availableFields.length === 0 && usedFields.length === 0 && (
              <p className="text-xs text-text-tertiary">
                {t('tenant.systemModuleBuilder.leftPanel.noFields')}
              </p>
            )}
          </>
        ) : (
          /* Tab Elementy (Krok B) — paleta layout elements współdzielona
             z buildery modułów własnych (heading, divider, alert, image, ...). */
          <div data-testid="system-module-builder-elements-palette">
            <LayoutElementsPalette
              onAdd={onAddLayoutElement}
              moduleSlug={moduleSlug}
              excludeTypes={excludeElementTypes}
            />
          </div>
        )}
      </aside>

      {/* Center: canvas */}
      <div className="flex-1 overflow-y-auto p-6" data-testid="system-module-builder-canvas">
        {/* F12.M Etap E (PR 2) — pasek wyboru kategorii dla edytora `field_overrides` (ADR-0030) */}
        {/* E2.T2a — pasek zakładek wewnątrz canvasu nad sekcjami (jak SellCall) */}
        {tabsBarSlot && <div className="mb-4">{tabsBarSlot}</div>}
        {/* F12.N T5.6b — banner trybu inline edycji helper-encji (jeśli aktywny) */}
        {helperInlineBanner}
        {sections.length === 0 ? (
          <EmptyCanvas onAddSection={onAddSection} />
        ) : (
          <SortableContext items={sections.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-3">
              {sections.map((section) => (
                <SortableSectionView
                  key={section.id}
                  section={section}
                  fieldById={fieldById}
                  selected={section.id === selectedSectionId}
                  onSelect={() => onSelectSection(section.id)}
                  onRemove={() => onRemoveSection(section.id)}
                  onDuplicate={
                    onDuplicateSection ? () => onDuplicateSection(section.id) : undefined
                  }
                  onRemoveField={(elementId) => onRemoveField(section.id, elementId)}
                  onAddAccordionItem={onAddAccordionItem}
                  onUpdateAccordionItemTitle={onUpdateAccordionItemTitle}
                  onRemoveAccordionItem={onRemoveAccordionItem}
                  onAddStep={onAddStep}
                  onUpdateStepTitle={onUpdateStepTitle}
                  onUpdateStepIcon={onUpdateStepIcon}
                  onRemoveStep={onRemoveStep}
                  selectedElementId={selectedElementId}
                  onSelectElement={onSelectElement}
                  onUpdateElementSettings={onUpdateElementSettings}
                  systemHideOnly={systemHideOnly}
                  onToggleFieldHidden={onToggleFieldHidden}
                />
              ))}
            </div>
          </SortableContext>
        )}

        {sections.length > 0 && (
          <Button
            variant="secondary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={onAddSection}
            className="mt-4"
            data-testid="system-module-builder-add-section"
          >
            {t('tenant.systemModuleBuilder.addSection')}
          </Button>
        )}
      </div>

      {/* Splitter do ręcznego poszerzania panelu właściwości (jak menu). */}
      <div
        role="separator"
        aria-orientation="vertical"
        onMouseDown={startPropsResize}
        title={t('tenant.systemModuleBuilder.props.resizeHint')}
        className="w-1 shrink-0 cursor-col-resize bg-border-primary transition-colors hover:bg-accent-400"
        data-testid="system-module-builder-properties-resizer"
      />
      {/* Right: properties */}
      <aside
        className="shrink-0 overflow-y-auto border-l border-border-primary bg-bg-primary p-4"
        style={{ width: propsWidth }}
        data-testid="system-module-builder-properties"
      >
        {/* Trzy ZWIJALNE grupy: właściwości ZAKŁADKI / SEKCJI / POLA(ELEMENTU). */}
        <CollapsibleGroup
          title={t('tenant.systemModuleBuilder.tabProperties.title')}
          testId="system-module-builder-group-tab"
        >
          <TabPropertiesPanel
            tab={selectedTab}
            roleTemplates={roleTemplates}
            onChange={onUpdateTab}
            hideRoleVisibility={hideRoleVisibility}
          />
        </CollapsibleGroup>
        {selectedSection ? (
          <div className="mt-4">
            <CollapsibleGroup
              title={t('tenant.systemModuleBuilder.properties.title')}
              subtitle={selectedSection.title ?? undefined}
              testId="system-module-builder-group-section"
            >
              <SectionProperties
                section={selectedSection}
                onChange={(patch) => onUpdateSection(selectedSection.id, patch)}
              />
            </CollapsibleGroup>
          </div>
        ) : (
          <p className="mt-4 text-xs text-text-tertiary">
            {t('tenant.systemModuleBuilder.selectSectionHint')}
          </p>
        )}
        {selectedElement &&
          onUpdateElementSettings &&
          (() => {
            const selField =
              selectedElement.type === 'field' && selectedElement.fieldId
                ? fieldById.get(selectedElement.fieldId)
                : null;
            const fInfo = selField
              ? {
                  name: selField.name,
                  fieldType: selField.fieldType,
                  required: Boolean(selField.isRequired),
                  description: selField.description,
                }
              : undefined;
            const isFld = selectedElement.type === 'field';
            return (
              <div className="mt-4">
                <CollapsibleGroup
                  accent
                  title={t(
                    isFld
                      ? 'tenant.systemModuleBuilder.props.fieldProps'
                      : 'tenant.systemModuleBuilder.props.elementProps',
                  )}
                  subtitle={
                    isFld ? fInfo?.name : LayoutElementRegistry.get(selectedElement.type)?.label
                  }
                  testId="system-module-builder-group-element"
                >
                  <ElementPropertiesPanel
                    element={selectedElement}
                    onChange={(patch) => onUpdateElementSettings(selectedElement.id, patch)}
                    moduleSlug={moduleSlug}
                    conditionFields={[...fieldById.values()]
                      .filter((f) => f.slug !== selField?.slug)
                      .map((f) => ({ value: f.slug, label: f.name }))}
                    fieldInfo={fInfo}
                  />
                </CollapsibleGroup>
              </div>
            );
          })()}
      </aside>

      <DragOverlay dropAnimation={null}>
        {activeDrag?.kind === 'palette' && (
          <PaletteDragGhost data={activeDrag.data} fieldById={fieldById} />
        )}
        {activeDrag?.kind === 'element' && (
          <CanvasElementGhost elementId={activeDrag.id} sections={sections} fieldById={fieldById} />
        )}
        {activeDrag?.kind === 'section' && (
          <CanvasSectionGhost sectionId={activeDrag.id} sections={sections} />
        )}
        {activeDrag?.kind === 'accordion-panel' && (
          <CanvasAccordionPanelGhost panelId={activeDrag.id} sections={sections} />
        )}
        {activeDrag?.kind === 'stepper-step' && (
          <CanvasStepGhost stepId={activeDrag.id} sections={sections} />
        )}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * CanvasElementGhost — duszek pola/elementu non-field przeciąganego WEWNĄTRZ canvasu.
 * Bez tego sortable klocek nie pokazuje co user przeciąga (reorganizuje tylko sąsiedztwo).
 * Pakiet B Sub-2/3 fix.
 */
function CanvasElementGhost({
  elementId,
  sections,
  fieldById,
}: {
  elementId: string;
  sections: LayoutSection[];
  fieldById: Map<string, SystemModuleField>;
}) {
  // Znajdź element w sekcjach (top-level lub zagnieżdżony w panelu akordeonu).
  let found: TreeLayoutElement | null = null;
  for (const s of sections) {
    const direct = s.elements.find((e) => e.id === elementId);
    if (direct) {
      found = direct;
      break;
    }
    const nested = findInTree(s.elements, elementId);
    if (nested && 'type' in nested) {
      found = nested as TreeLayoutElement;
      break;
    }
  }
  if (!found) return null;
  // Field
  if (found.type === 'field') {
    const field = found.fieldId ? fieldById.get(found.fieldId) : null;
    if (field) {
      return (
        <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-3 py-2 text-xs shadow-xl">
          <p className="truncate font-medium text-text-primary">{field.name}</p>
          <p className="truncate text-[10px] text-text-tertiary">
            {field.slug} · {field.fieldType}
          </p>
        </div>
      );
    }
  }
  // Non-field element
  return (
    <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-3 py-2 text-xs font-medium text-text-primary shadow-xl">
      {found.type}
    </div>
  );
}

function CanvasSectionGhost({
  sectionId,
  sections,
}: {
  sectionId: string;
  sections: LayoutSection[];
}) {
  const section = sections.find((s) => s.id === sectionId);
  if (!section) return null;
  return (
    <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-4 py-3 text-sm font-semibold text-text-primary shadow-xl">
      {section.title ?? 'Sekcja bez tytułu'}
    </div>
  );
}

function CanvasAccordionPanelGhost({
  panelId,
  sections,
}: {
  panelId: string;
  sections: LayoutSection[];
}) {
  // Panel akordeonu jest AccordionItem (nie LayoutElement) — szukamy po `accordionItems[].id`.
  for (const s of sections) {
    const found = findInTree(s.elements, panelId);
    if (found && 'title' in found && !('type' in found)) {
      const panel = found as { title: string };
      return (
        <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-3 py-2 text-xs font-medium text-text-primary shadow-xl">
          {panel.title}
        </div>
      );
    }
  }
  return null;
}

/**
 * CanvasStepGhost — duszek kroku steppera/wizarda przeciąganego w canvasie.
 * Etap 3 T2 — analogiczny do CanvasAccordionPanelGhost.
 */
function CanvasStepGhost({ stepId, sections }: { stepId: string; sections: LayoutSection[] }) {
  for (const s of sections) {
    const found = findInTree(s.elements, stepId);
    // Step jest StepItem (nie LayoutElement) — `title` jest, `type` brak.
    if (found && 'title' in found && !('type' in found)) {
      const step = found as { title: string };
      return (
        <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-3 py-2 text-xs font-medium text-text-primary shadow-xl">
          {step.title}
        </div>
      );
    }
  }
  return null;
}

/**
 * Wizualny „duch" pola/elementu z palety jadący za kursorem podczas drag.
 * Renderowany przez DragOverlay (poza normalnym drzewem DOM, fixed positioning).
 * Bez tego dnd-kit nie pokazuje żadnego wizualnego feedbacku gdy user przeciąga
 * z palety — chwyt + ruch myszką wygląda jakby nic się nie działo.
 */
function PaletteDragGhost({
  data,
  fieldById,
}: {
  data: PaletteDragData;
  fieldById: Map<string, SystemModuleField>;
}) {
  if (data.kind === 'field') {
    const field = fieldById.get(data.payload);
    if (!field) return null;
    return (
      <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-3 py-2 text-xs shadow-xl">
        <p className="truncate font-medium text-text-primary">{field.name}</p>
        <p className="truncate text-[10px] text-text-tertiary">
          {field.slug} · {field.fieldType}
        </p>
      </div>
    );
  }
  // layout-element — payload to typ („accordion", „heading", „alert" itp.)
  return (
    <div className="pointer-events-none rounded-md border-2 border-accent-500 bg-bg-primary px-3 py-2 text-xs font-medium text-text-primary shadow-xl">
      {data.payload}
    </div>
  );
}

// ─── Krok A: PaletteFieldButton (re-used dla "Dostępne" + "Na canvas") ──────

interface PaletteFieldButtonProps {
  field: SystemModuleField;
  onClick?: () => void;
  /** Disabled = pole już użyte na canvas (sekcja "Na canvas"). */
  disabled?: boolean;
}

function PaletteFieldButton({ field, onClick, disabled = false }: PaletteFieldButtonProps) {
  const { t } = useTranslation();
  // F12.N D3 — paleta wspiera drag&drop (oprócz click-to-add). Disabled pola
  // („Na canvasie") nie są draggable — duplikatów nie chcemy.
  const dragData: PaletteDragData = {
    source: 'palette',
    kind: 'field',
    payload: field.id,
  };
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `system-palette-field-${field.id}`,
    data: dragData,
    disabled,
  });

  const baseClass =
    'flex w-full items-center justify-between rounded-md border border-border-primary px-3 py-2 text-left text-xs';
  const interactiveClass = disabled
    ? 'cursor-default opacity-60'
    : `hover:border-accent-500 hover:bg-bg-secondary ${isDragging ? 'cursor-grabbing opacity-50' : 'cursor-grab'}`;

  const badgeLabel = field.isCore
    ? t('tenant.systemModuleFields.coreBadge')
    : t('tenant.systemModuleBuilder.extBadge');
  const typeLabel = t(
    systemFieldTypeI18nKey(field.fieldType as SystemFieldType) || 'tenant.systemFieldTypes.varchar',
  );

  // Tooltip pola (parytet z paletą elementów, tone="info") — pełna nazwa (gdy ucięta),
  // opcjonalny opis pola z katalogu, oraz linia techniczna: badge · typ · slug.
  const tooltipContent = (
    <span>
      <b>{field.name}</b>
      {field.description ? (
        <>
          <br />
          {field.description}
        </>
      ) : null}
      <br />
      <span className="opacity-70">
        {badgeLabel} · {typeLabel} · {field.slug}
      </span>
    </span>
  );

  // F12.N E3.T1 fix (2026-05-16) — natywny <button> blokuje gest pointerdown z @dnd-kit
  // (przeglądarka zjada pointerdown na buttonie zanim biblioteka zdąży go przechwycić,
  // klik działa ale drag nie startuje). Zamiana na <div role="button"> z keyboard
  // handlerem dla a11y. Parytet z FieldListItem z buildera modułów własnych.
  return (
    <Tooltip tone="info" placement="right" content={tooltipContent}>
      <div
        ref={setNodeRef}
        onClick={disabled ? undefined : onClick}
        onKeyDown={
          disabled
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onClick?.();
                }
              }
        }
        // Active path: @dnd-kit dostarcza role/tabIndex/aria-* + pointerDown listeners.
        // Disabled path: manualne atrybuty żeby zachować dostępność (focus skip + screen-reader).
        {...(disabled
          ? ({ role: 'button', tabIndex: -1, 'aria-disabled': true } as const)
          : { ...attributes, ...listeners })}
        className={`${baseClass} ${interactiveClass}`}
        data-testid={`system-module-builder-palette-${field.slug}`}
      >
        <div className="min-w-0 flex-1">
          <p className="truncate font-medium text-text-primary">{field.name}</p>
          <p className="truncate text-[10px] text-text-tertiary">
            {badgeLabel}
            {' · '}
            {typeLabel}
          </p>
        </div>
        {!disabled && <Plus className="h-3 w-3 shrink-0 text-text-muted" />}
      </div>
    </Tooltip>
  );
}

// ─── Empty canvas (z C.4) ────────────────────────────────────────────────────

function EmptyCanvas({ onAddSection }: { onAddSection: () => void }) {
  const { t } = useTranslation();
  return (
    <div
      className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-border-primary py-16 text-center"
      data-testid="system-module-builder-empty"
    >
      <p className="text-sm font-medium text-text-primary">
        {t('tenant.systemModuleBuilder.empty.title')}
      </p>
      <p className="mt-1 max-w-md text-xs text-text-tertiary">
        {t('tenant.systemModuleBuilder.empty.subtitle')}
      </p>
      <Button
        variant="primary"
        leftIcon={<Plus className="h-4 w-4" />}
        onClick={onAddSection}
        className="mt-4"
        data-testid="system-module-builder-empty-add"
      >
        {t('tenant.systemModuleBuilder.addSection')}
      </Button>
    </div>
  );
}

// ─── Sortable section view (z C.4, niezmienione) ────────────────────────────

interface SortableSectionViewProps {
  section: LayoutSection;
  fieldById: Map<string, SystemModuleField>;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
  /** F12.N E2.T4: duplicate handler. Opcjonalny — helper-inline scope nie wspiera. */
  onDuplicate?: () => void;
  onRemoveField: (elementId: string) => void;
  /** F12.N E3.T1: CRUD paneli akordeonu. */
  onAddAccordionItem: (accordionId: string) => void;
  onUpdateAccordionItemTitle: (accordionId: string, panelId: string, newTitle: string) => void;
  onRemoveAccordionItem: (accordionId: string, panelId: string) => void;
  /** Etap 3 T2: CRUD kroków steppera/wizarda (wspólny edytor). */
  onAddStep: (stepperId: string) => void;
  onUpdateStepTitle: (stepperId: string, stepId: string, newTitle: string) => void;
  onUpdateStepIcon: (stepperId: string, stepId: string, newIcon: string) => void;
  onRemoveStep: (stepperId: string, stepId: string) => void;
  /** Selekcja bloku treści (non-field) — patrz `FieldsTabContentProps`. */
  selectedElementId?: string | null;
  onSelectElement?: (id: string | null) => void;
  /** Zapis settings bloku WPROST z podglądu (inline eye-toggle w formatkach). */
  onUpdateElementSettings?: (elementId: string, patch: Record<string, unknown>) => void;
  /** Systemowy layout: tylko ukrywanie (oko na polach/formatkach), bez uchwytów/usuwania. */
  systemHideOnly?: boolean;
  /** Ukryj/pokaż zwykłe pole (ikonka oka) — ścieżka systemowego layoutu. */
  onToggleFieldHidden?: (elementId: string) => void;
}

function SortableSectionView({
  section,
  fieldById,
  selected,
  onSelect,
  onDuplicate,
  onRemove,
  onRemoveField,
  onAddAccordionItem,
  onUpdateAccordionItemTitle,
  onRemoveAccordionItem,
  onAddStep,
  onUpdateStepTitle,
  onUpdateStepIcon,
  onRemoveStep,
  selectedElementId,
  onSelectElement,
  onUpdateElementSettings,
  systemHideOnly = false,
  onToggleFieldHidden,
}: SortableSectionViewProps) {
  const { t } = useTranslation();
  // F12.N D3 — sekcja jest jednocześnie drop target dla klocków z palety
  // (przez `data.target='section'` rozpoznawane w `usePaletteDnD.onDragEnd`).
  const sectionData: SectionDragData = {
    source: 'section',
    target: 'section',
    sectionId: section.id,
  };
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    data: sectionData,
  });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const columnsClass =
    section.columns === 1 ? 'grid-cols-1' : section.columns === 3 ? 'grid-cols-3' : 'grid-cols-2';

  // Wspólny renderer dziecka kontenera (kolumny / stopka / page-*) — pole → FieldBlock,
  // non-field → LayoutElementBlock, oba z `sortableContainerId=containerId`. Wydzielone,
  // żeby nie duplikować closure per kontener (parytet z accordion/stepper renderChild).
  const renderContainerChild = (child: TreeLayoutElement, containerId: string): React.ReactNode => {
    if (child.type === 'field') {
      const f = child.fieldId ? fieldById.get(child.fieldId) : null;
      if (!f) {
        return (
          <div className="rounded border border-warning-300 bg-warning-50 px-3 py-2 text-xs text-warning-700">
            {t('tenant.systemModuleBuilder.unknownField')}
          </div>
        );
      }
      return (
        <FieldBlock
          elementId={child.id}
          field={f}
          onRemove={onRemoveField}
          sortableContainerId={containerId}
          systemHideOnly={systemHideOnly}
          hidden={child.hidden === true}
          onToggleHidden={onToggleFieldHidden}
        />
      );
    }
    const childPreview = tryRenderLayoutElement({
      element: {
        id: child.id,
        type: child.type,
        settings: (child.settings ?? {}) as Record<string, unknown>,
      },
      children: [],
      mode: 'builder',
      systemHideOnly,
      onSettingsChange: onUpdateElementSettings
        ? (patch) => onUpdateElementSettings(child.id, patch)
        : undefined,
    }) ?? (
      <p className="text-xs text-text-tertiary">
        {t('tenant.systemModuleBuilder.unknownElement', { type: child.type })}
      </p>
    );
    return (
      <LayoutElementBlock
        elementId={child.id}
        elementType={child.type}
        preview={childPreview}
        onRemove={onRemoveField}
        sortableContainerId={containerId}
      />
    );
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={onSelect}
      className={
        'group cursor-pointer rounded-lg border bg-bg-primary p-4 transition ' +
        (selected ? 'border-accent-500 shadow' : 'border-border-primary hover:border-accent-300') +
        (isDragging ? ' opacity-50' : '')
      }
      data-testid={`system-module-builder-section-${section.id}`}
    >
      <div className="mb-3 flex items-center gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Przeciągnij sekcję"
          className="cursor-grab rounded p-1 text-text-muted hover:bg-bg-tertiary"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="flex-1 text-sm font-medium text-text-primary">
          {section.title ?? t('tenant.systemModuleBuilder.untitledSection')}
        </span>
        {onDuplicate && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDuplicate();
            }}
            aria-label={t('tenant.systemModuleBuilder.duplicateSection')}
            title={t('tenant.systemModuleBuilder.duplicateSection')}
            className="rounded p-1 text-text-muted opacity-0 transition group-hover:opacity-100 hover:bg-bg-tertiary hover:text-accent-600"
            data-testid={`system-module-builder-section-${section.id}-duplicate`}
          >
            <Copy className="h-4 w-4" />
          </button>
        )}
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={t('common.delete')}
          className="rounded p-1 text-text-muted opacity-0 transition group-hover:opacity-100 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
          data-testid={`system-module-builder-section-${section.id}-remove`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>

      {section.elements.length === 0 ? (
        <p className="rounded border border-dashed border-border-primary px-3 py-4 text-center text-xs text-text-tertiary">
          {t('tenant.systemModuleBuilder.sectionEmpty')}
        </p>
      ) : (
        <SortableContext items={section.elements.map((e) => e.id)} strategy={rectSortingStrategy}>
          <div className={`grid gap-2 ${columnsClass}`}>
            {section.elements.map((el) => {
              // F12.N E3.T1 — akordeon ma własny komponent w canvasie (per-panel drop targets,
              // CRUD itemów, expand/collapse). NIE leci do LayoutElementRegistry preview.
              if (el.type === 'accordion') {
                return (
                  <div key={el.id} className="group/el relative col-span-full">
                    <AccordionInCanvas
                      element={el as unknown as TreeLayoutElement}
                      renderChild={(child, containerId) => {
                        if (child.type === 'field') {
                          const f = child.fieldId ? fieldById.get(child.fieldId) : null;
                          if (!f) {
                            return (
                              <div className="rounded border border-warning-300 bg-warning-50 px-3 py-2 text-xs text-warning-700">
                                {t('tenant.systemModuleBuilder.unknownField')}
                              </div>
                            );
                          }
                          return (
                            <FieldBlock
                              elementId={child.id}
                              field={f}
                              onRemove={onRemoveField}
                              sortableContainerId={containerId}
                              systemHideOnly={systemHideOnly}
                              hidden={child.hidden === true}
                              onToggleHidden={onToggleFieldHidden}
                            />
                          );
                        }
                        const childPreview = tryRenderLayoutElement({
                          element: {
                            id: child.id,
                            type: child.type,
                            settings: (child.settings ?? {}) as Record<string, unknown>,
                          },
                          children: [],
                          mode: 'builder',
                          systemHideOnly,
                          onSettingsChange: onUpdateElementSettings
                            ? (patch) => onUpdateElementSettings(child.id, patch)
                            : undefined,
                        }) ?? (
                          <p className="text-xs text-text-tertiary">
                            {t('tenant.systemModuleBuilder.unknownElement', { type: child.type })}
                          </p>
                        );
                        return (
                          <LayoutElementBlock
                            elementId={child.id}
                            elementType={child.type}
                            preview={childPreview}
                            onRemove={onRemoveField}
                            sortableContainerId={containerId}
                          />
                        );
                      }}
                      onAddItem={() => onAddAccordionItem(el.id)}
                      onUpdateItemTitle={(panelId, newTitle) =>
                        onUpdateAccordionItemTitle(el.id, panelId, newTitle)
                      }
                      onRemoveItem={(panelId) => onRemoveAccordionItem(el.id, panelId)}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveField(el.id);
                      }}
                      aria-label={t('common.delete')}
                      className="absolute right-1 top-1 rounded p-1 text-text-muted opacity-0 transition group-hover/el:opacity-100 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                      data-testid={`system-module-builder-layout-element-${el.id}-remove`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              }

              // Etap 3 T2 — stepper i wizard mają wspólny edytor StepperInCanvas
              // (decyzja UX 2026-05-17). Różnica tylko w runtime renderer.
              if (el.type === 'stepper' || el.type === 'wizard') {
                return (
                  <div key={el.id} className="group/el relative col-span-full">
                    <StepperInCanvas
                      element={el as unknown as TreeLayoutElement}
                      renderChild={(child, containerId) => {
                        if (child.type === 'field') {
                          const f = child.fieldId ? fieldById.get(child.fieldId) : null;
                          if (!f) {
                            return (
                              <div className="rounded border border-warning-300 bg-warning-50 px-3 py-2 text-xs text-warning-700">
                                {t('tenant.systemModuleBuilder.unknownField')}
                              </div>
                            );
                          }
                          return (
                            <FieldBlock
                              elementId={child.id}
                              field={f}
                              onRemove={onRemoveField}
                              sortableContainerId={containerId}
                              systemHideOnly={systemHideOnly}
                              hidden={child.hidden === true}
                              onToggleHidden={onToggleFieldHidden}
                            />
                          );
                        }
                        const childPreview = tryRenderLayoutElement({
                          element: {
                            id: child.id,
                            type: child.type,
                            settings: (child.settings ?? {}) as Record<string, unknown>,
                          },
                          children: [],
                          mode: 'builder',
                          systemHideOnly,
                          onSettingsChange: onUpdateElementSettings
                            ? (patch) => onUpdateElementSettings(child.id, patch)
                            : undefined,
                        }) ?? (
                          <p className="text-xs text-text-tertiary">
                            {t('tenant.systemModuleBuilder.unknownElement', { type: child.type })}
                          </p>
                        );
                        return (
                          <LayoutElementBlock
                            elementId={child.id}
                            elementType={child.type}
                            preview={childPreview}
                            onRemove={onRemoveField}
                            sortableContainerId={containerId}
                          />
                        );
                      }}
                      onAddStep={() => onAddStep(el.id)}
                      onUpdateStepTitle={(stepId, newTitle) =>
                        onUpdateStepTitle(el.id, stepId, newTitle)
                      }
                      onUpdateStepIcon={(stepId, newIcon) =>
                        onUpdateStepIcon(el.id, stepId, newIcon)
                      }
                      onRemoveStep={(stepId) => onRemoveStep(el.id, stepId)}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveField(el.id);
                      }}
                      aria-label={t('common.delete')}
                      className="absolute right-1 top-1 rounded p-1 text-text-muted opacity-0 transition group-hover/el:opacity-100 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                      data-testid={`system-module-builder-layout-element-${el.id}-remove`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              }

              // Kontener `columns` — prawdziwy drop target dla pól/elementów z palety.
              // Bez tego (generyczna gałąź non-field niżej) pole upuszczone „na kolumnę"
              // trafiało do sekcji-rodzica zamiast do wnętrza kontenera. Parytet z
              // AccordionInCanvas / StepperInCanvas (własny `useDroppable` + renderChild).
              if (el.type === 'columns') {
                return (
                  <div key={el.id} className="group/el relative col-span-full">
                    <ColumnsInCanvas
                      element={el as unknown as TreeLayoutElement}
                      renderChild={renderContainerChild}
                    />
                    <IconButton
                      variant="danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveField(el.id);
                      }}
                      aria-label={t('common.delete')}
                      className="absolute right-1 top-1 opacity-0 transition group-hover/el:opacity-100"
                      data-testid={`system-module-builder-layout-element-${el.id}-remove`}
                    >
                      <X className="h-3 w-3" />
                    </IconButton>
                  </div>
                );
              }

              // Generyczne kontenery `children[]` (card, row, grid, fieldset, panel, sidebar,
              // collapsible, page-footer/main/aside) — prawdziwy drop target dla pól/elementów
              // z palety. Bez tego (generyczna gałąź non-field niżej) pole upuszczone „na
              // kontener" trafiało do sekcji-rodzica. Gate = `isContainer===true` z wykluczeniem
              // typów o odrębnej obsłudze:
              //  - accordion/stepper/wizard/tabs — inny model dzieci (accordionItems/steps/tabs),
              //    obsłużone wcześniejszymi gałęziami / poza tą pętlą,
              //  - columns — dedykowany `ColumnsInCanvas` (gałąź wyżej).
              // Leafy (`page-header`, `page-navbar`, heading, alert, …) mają isContainer=false
              // i nie wchodzą tu — lecą do generycznej gałęzi non-field niżej.
              if (
                LayoutElementRegistry.get(el.type)?.isContainer === true &&
                !CONTAINER_DEDICATED_TYPES.has(el.type)
              ) {
                return (
                  <div
                    key={el.id}
                    className="group/el relative col-span-full"
                    onClick={
                      onSelectElement
                        ? (e) => {
                            e.stopPropagation();
                            onSelectElement(el.id);
                          }
                        : undefined
                    }
                  >
                    <div
                      className={
                        'rounded ' + (el.id === selectedElementId ? 'ring-2 ring-accent-500' : '')
                      }
                    >
                      <ContainerInCanvas
                        element={el as unknown as TreeLayoutElement}
                        renderChild={renderContainerChild}
                      />
                    </div>
                    <IconButton
                      variant="danger"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveField(el.id);
                      }}
                      aria-label={t('common.delete')}
                      className="absolute right-1 top-1 opacity-0 transition group-hover/el:opacity-100"
                      data-testid={`system-module-builder-layout-element-${el.id}-remove`}
                    >
                      <X className="h-3 w-3" />
                    </IconButton>
                  </div>
                );
              }

              // Krok B — non-field elementy (heading/divider/alert/...): render preview
              // przez LayoutElementRegistry w mode='builder'. Pokazuje user'owi jak
              // element wygląda + klikalne na delete.
              if (el.type !== 'field') {
                const preview = tryRenderLayoutElement({
                  element: {
                    id: el.id,
                    type: el.type,
                    settings: (el.settings ?? {}) as Record<string, unknown>,
                  },
                  children: [],
                  mode: 'builder',
                  // Systemowy layout: formatka pokazuje tylko oko (bez reorderu), pierce inertu.
                  systemHideOnly,
                  // Inline edycja settings z podglądu (ikonka oka na formatkach — np. adres
                  // macierzysty). Bloki, które tego nie używają, po prostu ignorują callback.
                  onSettingsChange: onUpdateElementSettings
                    ? (patch) => onUpdateElementSettings(el.id, patch)
                    : undefined,
                }) ?? (
                  <p className="text-xs text-text-tertiary">
                    {t('tenant.systemModuleBuilder.unknownElement', { type: el.type })}
                  </p>
                );
                // Bloki-formatki (fullWidth) zajmują cały wiersz gridu sekcji — inaczej
                // ściska je do pół kolumny i pola wyglądają na malutkie.
                const wrapClass = LayoutElementRegistry.get(el.type)?.fullWidth
                  ? 'col-span-full'
                  : undefined;
                const block = (
                  <LayoutElementBlock
                    key={el.id}
                    elementId={el.id}
                    elementType={el.type}
                    preview={preview}
                    onRemove={onRemoveField}
                    sortableContainerId={section.id}
                    selected={el.id === selectedElementId}
                    onSelect={onSelectElement ? () => onSelectElement(el.id) : undefined}
                  />
                );
                return wrapClass ? (
                  <div key={el.id} className={wrapClass}>
                    {block}
                  </div>
                ) : (
                  block
                );
              }

              const field = el.fieldId ? fieldById.get(el.fieldId) : null;
              if (!field) {
                return (
                  <div
                    key={el.id}
                    className="flex items-center justify-between gap-2 rounded border border-warning-300 bg-warning-50 px-3 py-2 text-xs text-warning-700"
                    data-testid={`system-module-builder-orphan-${el.id}`}
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {t('tenant.systemModuleBuilder.unknownField')}
                    </span>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onRemoveField(el.id);
                      }}
                      aria-label={t('common.delete')}
                      className="rounded p-1 text-warning-700 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                      data-testid={`system-module-builder-orphan-${el.id}-remove`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                );
              }
              return (
                <FieldBlock
                  key={el.id}
                  elementId={el.id}
                  field={field}
                  onRemove={onRemoveField}
                  sortableContainerId={section.id}
                  selected={el.id === selectedElementId}
                  onSelect={onSelectElement ? () => onSelectElement(el.id) : undefined}
                  systemHideOnly={systemHideOnly}
                  hidden={el.hidden === true}
                  onToggleHidden={onToggleFieldHidden}
                />
              );
            })}
          </div>
        </SortableContext>
      )}
    </div>
  );
}

// ─── Tab properties panel (per-role visibility) ──────────────────────────────

interface TabPropertiesPanelProps {
  tab: LayoutTab;
  roleTemplates: RoleTemplate[];
  onChange: (patch: Partial<LayoutTab>) => void;
  /**
   * Tryb szablonu (super admin) — widoczność zakładki wg ról to byt TENANTA (role żyją
   * u klienta), poza kontraktem szablonu. Zamiast pustej listy „brak szablonów ról"
   * pokazujemy krótką notę wyjaśniającą. Domyślnie false (builder tenanta).
   */
  hideRoleVisibility?: boolean;
}

function TabPropertiesPanel({
  tab,
  roleTemplates,
  onChange,
  hideRoleVisibility = false,
}: TabPropertiesPanelProps) {
  const { t } = useTranslation();
  const visibleRoleIds = tab.visibleRoleIds ?? [];

  function toggleRole(roleId: string) {
    const current = tab.visibleRoleIds ?? [];
    const next = current.includes(roleId)
      ? current.filter((id) => id !== roleId)
      : [...current, roleId];
    onChange({ visibleRoleIds: next });
  }

  const sortedRoles = roleTemplates.slice().sort((a, b) => {
    if (a.isSystem !== b.isSystem) return a.isSystem ? -1 : 1;
    return a.name.localeCompare(b.name, 'pl');
  });

  return (
    <div className="space-y-3">
      {hideRoleVisibility ? (
        <p className="text-xs text-text-muted" data-testid="tab-props-role-template-note">
          {t('tenant.systemModuleBuilder.tabProperties.roleVisibilityTemplateNote', {
            defaultValue:
              'Widoczność zakładki według ról ustawia klient w swoim panelu — role nie należą do szablonu.',
          })}
        </p>
      ) : (
        <FormField label={t('tenant.systemModuleBuilder.tabProperties.visibleRoles')}>
          {sortedRoles.length === 0 ? (
            <p className="text-xs text-text-tertiary">
              {t('tenant.systemModuleBuilder.tabProperties.noRoles')}
            </p>
          ) : (
            <div className="space-y-1">
              <div className="rounded-md border border-border-primary bg-bg-primary p-2">
                <div className="max-h-48 space-y-1 overflow-y-auto">
                  {sortedRoles.map((role) => (
                    <label
                      key={role.id}
                      className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-bg-secondary"
                      data-testid={`tab-props-role-${role.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={visibleRoleIds.includes(role.id)}
                        onChange={() => toggleRole(role.id)}
                        className="h-4 w-4 rounded border-border-primary text-accent-600 focus:ring-accent-500"
                      />
                      <span className="flex-1 text-text-primary">{role.name}</span>
                      {role.isSystem && (
                        <span className="rounded bg-bg-tertiary px-1.5 py-0.5 text-[10px] font-medium uppercase text-text-tertiary">
                          system
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
              <p className="mt-1 text-xs text-text-muted">
                {visibleRoleIds.length === 0
                  ? t('tenant.systemModuleBuilder.tabProperties.allRolesSee')
                  : t('tenant.systemModuleBuilder.tabProperties.selectedCount', {
                      count: visibleRoleIds.length,
                    })}
              </p>
            </div>
          )}
        </FormField>
      )}
    </div>
  );
}

// ─── Section properties panel (z C.4) ────────────────────────────────────────

interface SectionPropertiesProps {
  section: LayoutSection;
  onChange: (patch: Partial<LayoutSection>) => void;
}

function SectionProperties({ section, onChange }: SectionPropertiesProps) {
  const { t } = useTranslation();
  return (
    <div className="space-y-3">
      <FormField label={t('tenant.systemModuleBuilder.properties.sectionTitle')}>
        <Input
          value={section.title ?? ''}
          onChange={(e) => onChange({ title: e.target.value || null })}
          placeholder={t('tenant.systemModuleBuilder.properties.sectionTitlePlaceholder')}
          maxLength={100}
          data-testid="system-module-builder-section-title"
        />
      </FormField>
      <FormField label={t('tenant.systemModuleBuilder.properties.columns')}>
        <Dropdown
          value={String(section.columns)}
          onChange={(v) => onChange({ columns: Number(v) as 1 | 2 | 3 })}
          options={[
            { value: '1', label: '1' },
            { value: '2', label: '2' },
            { value: '3', label: '3' },
          ]}
          data-testid="system-module-builder-section-columns"
        />
      </FormField>
    </div>
  );
}

// ─── Element (block) properties panel — dowolny typ z zarejestrowanym
// `propertiesEditor` w `LayoutElementRegistry` (np. "Pozycje dokumentu",
// "Kafelki KPI", "Lista"). Host generyczny — nie zna konkretnych typów,
// tylko woła `def.propertiesEditor({settings, onChange})` (mirror
// `LayoutElementContentTab` z `ModuleBuilderPage.tsx`, ale bez zależności
// od tamtejszego modelu zakładek/szerokości bloku, którego tu nie ma). ──────

interface ElementPropertiesProps {
  element: LayoutElement;
  onChange: (patch: Record<string, unknown>) => void;
  moduleSlug?: string;
  /** Pola layoutu do wyboru w warunku widoczności (zakładka Zaawansowane). */
  conditionFields?: { value: string; label: string }[];
  /** Info o polu źródłowym (read-only karta w Treści — parytet z SellCall). */
  fieldInfo?: { name: string; fieldType: string; required: boolean; description?: string | null };
}

// ─── Zwijalna grupa właściwości (nagłówek z chevronem + treść) ───────────────
function CollapsibleGroup({
  title,
  subtitle,
  accent = false,
  testId,
  defaultOpen = true,
  children,
}: {
  title: string;
  subtitle?: string | null;
  accent?: boolean;
  testId?: string;
  defaultOpen?: boolean;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div
      className={
        'rounded-lg border bg-bg-primary ' +
        (accent ? 'border-accent-300' : 'border-border-primary')
      }
      data-testid={testId}
    >
      <Button
        type="button"
        variant="ghost"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left font-normal"
        aria-expanded={open}
      >
        <span className="min-w-0">
          <span className="block text-sm font-semibold text-text-primary">{title}</span>
          {subtitle ? (
            <span className="block truncate text-xs text-text-tertiary">{subtitle}</span>
          ) : null}
        </span>
        <ChevronDown
          className={
            'h-4 w-4 shrink-0 text-text-muted transition-transform ' + (open ? 'rotate-180' : '')
          }
        />
      </Button>
      {open ? <div className="px-3 pb-3">{children}</div> : null}
    </div>
  );
}

// ─── Panel właściwości elementu z zakładkami (Treść / Styl / Zaawansowane) ────
// Treść: nazwa/etykieta (pole → nagłówek nad polem; blok → nazwa elementu) + ewentualny
//        propertiesEditor bloku. Styl: pełny panel wyglądu formatki (typografia/tło/…),
//        portowany z SellCalla. Zaawansowane: widoczność warunkowa. Wszystko zapisuje się
//        w `element.settings` (settings.label / settings.style / settings.conditional*) i
//        jedzie istniejącą rurą publikacji layoutu.
function ElementPropertiesPanel({
  element,
  onChange,
  moduleSlug,
  conditionFields = [],
  fieldInfo,
}: ElementPropertiesProps) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'content' | 'style' | 'advanced'>('content');
  // Cel stylu dla pola: 'label' = napis (tytuł), 'body' = środek (kontrolka pola).
  const [styleTarget, setStyleTarget] = useState<'label' | 'body'>('label');
  const def = LayoutElementRegistry.get(element.type);
  const settings = element.settings ?? {};
  const currentLabel = typeof settings.label === 'string' ? settings.label : '';
  const currentStyle = (settings as { style?: LayoutElementStyle }).style;
  const currentLabelStyle = (settings as { labelStyle?: LayoutElementStyle }).labelStyle;
  const isField = element.type === 'field';
  // Szerokość w siatce sekcji (col-span). Panel zapisuje w `settings.width`; renderer czyta
  // `settings.width` (a dla starych layoutów też `el.width`).
  const rawWidth = Number((settings as { width?: unknown }).width);
  const currentWidth = rawWidth === 2 || rawWidth === 3 ? rawWidth : 1;

  // Widoczność warunkowa (settings.conditional*). 'always' = brak warunku.
  const condOp =
    typeof settings.conditionalOperator === 'string' ? settings.conditionalOperator : '';
  const condField = typeof settings.conditionalField === 'string' ? settings.conditionalField : '';
  const condValue = typeof settings.conditionalValue === 'string' ? settings.conditionalValue : '';
  const visibilityValue = condOp || 'always';
  const needsField =
    condOp === 'equals' || condOp === 'not_equals' || condOp === 'empty' || condOp === 'not_empty';
  const needsValue = condOp === 'equals' || condOp === 'not_equals';

  return (
    <div className="space-y-3" data-testid="system-module-builder-element-properties">
      {isField && (
        <div
          className="rounded-lg border border-border-primary bg-bg-secondary p-3"
          data-testid="system-module-builder-field-preview"
        >
          <div className="mb-1 text-[10px] uppercase tracking-wide text-text-tertiary">
            {t('tenant.systemModuleBuilder.props.livePreview')}
          </div>
          <div
            style={styleToCSS(currentLabelStyle)}
            className="mb-1 text-xs font-medium text-text-primary"
          >
            {fieldInfo?.name ?? ''}
          </div>
          <div
            style={styleToCSS(currentStyle)}
            className="flex h-9 items-center rounded border border-border-strong bg-bg-primary px-2 text-xs text-text-muted"
          >
            {t('tenant.systemModuleBuilder.props.livePreviewValue')}
          </div>
        </div>
      )}
      <div className="flex gap-1 rounded-lg bg-bg-secondary p-1">
        {(['content', 'style', 'advanced'] as const).map((id) => (
          <Button
            key={id}
            type="button"
            variant={tab === id ? 'primary' : 'ghost'}
            size="sm"
            className="flex-1 text-xs"
            onClick={() => setTab(id)}
            data-testid={`system-module-builder-element-tab-${id}`}
          >
            {t(`tenant.systemModuleBuilder.props.${id}Tab`)}
          </Button>
        ))}
      </div>

      {tab === 'content' &&
        (isField ? (
          <div className="space-y-3">
            <FormField label={t('tenant.systemModuleBuilder.props.gridWidth')}>
              <Dropdown
                value={String(currentWidth)}
                onChange={(v) => onChange({ width: Number(v) })}
                options={[
                  { value: '1', label: t('tenant.systemModuleBuilder.props.width1col') },
                  { value: '2', label: t('tenant.systemModuleBuilder.props.width2col') },
                  { value: '3', label: t('tenant.systemModuleBuilder.props.width3col') },
                ]}
                data-testid="system-module-builder-field-width"
              />
            </FormField>
            {fieldInfo && (
              <div className="space-y-1 rounded-lg bg-bg-secondary p-3 text-xs">
                <p>
                  <span className="text-text-tertiary">
                    {t('tenant.systemModuleBuilder.props.infoField')}:
                  </span>{' '}
                  <span className="font-medium text-text-primary">{fieldInfo.name}</span>
                </p>
                <p>
                  <span className="text-text-tertiary">
                    {t('tenant.systemModuleBuilder.props.infoType')}:
                  </span>{' '}
                  {fieldInfo.fieldType}
                </p>
                <p>
                  <span className="text-text-tertiary">
                    {t('tenant.systemModuleBuilder.props.infoRequired')}:
                  </span>{' '}
                  {fieldInfo.required
                    ? t('tenant.systemModuleBuilder.props.yes')
                    : t('tenant.systemModuleBuilder.props.no')}
                </p>
                {fieldInfo.description && (
                  <p>
                    <span className="text-text-tertiary">
                      {t('tenant.systemModuleBuilder.props.infoDescription')}:
                    </span>{' '}
                    {fieldInfo.description}
                  </p>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <FormField label={t('tenant.systemModuleBuilder.props.elementName')}>
              <Input
                value={currentLabel}
                onChange={(e) => onChange({ label: e.target.value || undefined })}
                placeholder={t('tenant.systemModuleBuilder.props.elementNamePlaceholder')}
                maxLength={100}
                data-testid="system-module-builder-field-label"
              />
            </FormField>
            {def?.propertiesEditor
              ? def.propertiesEditor({ settings, onChange, moduleSlug })
              : null}
          </div>
        ))}

      {tab === 'style' &&
        (isField ? (
          <div className="space-y-3">
            <div className="flex gap-1 rounded-lg bg-bg-secondary p-1">
              {(['label', 'body'] as const).map((tg) => (
                <Button
                  key={tg}
                  type="button"
                  variant={styleTarget === tg ? 'primary' : 'ghost'}
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => setStyleTarget(tg)}
                  data-testid={`system-module-builder-style-target-${tg}`}
                >
                  {t(
                    tg === 'label'
                      ? 'tenant.systemModuleBuilder.props.styleTargetLabel'
                      : 'tenant.systemModuleBuilder.props.styleTargetBody',
                  )}
                </Button>
              ))}
            </div>
            <StylePanelAdvanced
              style={styleTarget === 'label' ? currentLabelStyle : currentStyle}
              onChange={(s) => onChange(styleTarget === 'label' ? { labelStyle: s } : { style: s })}
              elementType={element.type}
            />
          </div>
        ) : (
          <StylePanelAdvanced
            style={currentStyle}
            onChange={(s) => onChange({ style: s })}
            elementType={element.type}
          />
        ))}

      {tab === 'advanced' && (
        <div className="space-y-3">
          <FormField label={t('tenant.systemModuleBuilder.props.visibility')}>
            <Dropdown
              value={visibilityValue}
              onChange={(v) =>
                onChange(
                  v === 'always'
                    ? {
                        conditionalOperator: undefined,
                        conditionalField: undefined,
                        conditionalValue: undefined,
                      }
                    : { conditionalOperator: v },
                )
              }
              options={[
                { value: 'always', label: t('tenant.systemModuleBuilder.props.visibilityAlways') },
                { value: 'equals', label: t('tenant.systemModuleBuilder.props.opEquals') },
                { value: 'not_equals', label: t('tenant.systemModuleBuilder.props.opNotEquals') },
                { value: 'not_empty', label: t('tenant.systemModuleBuilder.props.opNotEmpty') },
                { value: 'empty', label: t('tenant.systemModuleBuilder.props.opEmpty') },
              ]}
              data-testid="system-module-builder-visibility-op"
            />
          </FormField>
          {needsField && (
            <FormField label={t('tenant.systemModuleBuilder.props.conditionField')}>
              <Dropdown
                value={condField}
                onChange={(v) => onChange({ conditionalField: v })}
                options={conditionFields}
                placeholder={t('tenant.systemModuleBuilder.props.conditionFieldPlaceholder')}
                data-testid="system-module-builder-visibility-field"
              />
            </FormField>
          )}
          {needsValue && (
            <FormField label={t('tenant.systemModuleBuilder.props.conditionValue')}>
              <Input
                value={condValue}
                onChange={(e) => onChange({ conditionalValue: e.target.value || undefined })}
                data-testid="system-module-builder-visibility-value"
              />
            </FormField>
          )}
        </div>
      )}
    </div>
  );
}
